// @ts-nocheck
import { useState, useEffect, useRef, useCallback, useMemo } from "react";

/* ═══════════════════════════════════════════════════════════
   GATE ECE 2027 TRACKER v3 — Updated
   - Background-safe Pomodoro timer (survives screen lock)
   - Rebuilt OCR (jsDelivr CDN, preprocessing, live progress)
   - Modal z-index fix (no more overlap with floating widgets)
   - Lec-uncheck now correctly clears revision schedule
   - NEW: AI Study Assistant (Claude-powered)
   - NEW: Study Calendar Heatmap
   - NEW: SM-2 style flashcard spaced repetition
═══════════════════════════════════════════════════════════ */

const uid = () => Math.random().toString(36).slice(2, 9);
const todayS = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
};
const addDays = (s, n) => {
  const d = new Date(s + "T12:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};
const daysDiff = (s) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(s + "T00:00:00");
  return Math.ceil((due - now) / 86400000);
};
const vibrate = (ms = 40) => {
  try {
    navigator.vibrate && navigator.vibrate(ms);
  } catch (e) {}
};
const sendNotification = (title, body, tag) => {
  try {
    if (!("Notification" in window) || Notification.permission !== "granted")
      return;
    new Notification(title, {
      body,
      tag,
      icon: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' rx='20' fill='%23b39dff'/><text x='50' y='68' font-size='55' text-anchor='middle' fill='%23050810' font-weight='bold' font-family='sans-serif'>G</text></svg>",
    });
  } catch (e) {}
};
const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
};

/* ── SPACED REPETITION ──────────────────────────────────── */
const getRevStatus = (t) => {
  if (!t.lec || !t.studiedDate) return null;
  if (!t.r1) {
    const due = addDays(t.studiedDate, 7);
    return { stage: 1, label: "Rev 1 (7d)", due, diff: daysDiff(due) };
  }
  if (t.r1 && !t.r2 && t.r1Date) {
    const due = addDays(t.r1Date, 14);
    return { stage: 2, label: "Rev 2 (14d)", due, diff: daysDiff(due) };
  }
  if (t.r2 && !t.r3 && t.r2Date) {
    const due = addDays(t.r2Date, 30);
    return { stage: 3, label: "Rev 3 (30d)", due, diff: daysDiff(due) };
  }
  return null;
};
const isDueToday = (t) => {
  const r = getRevStatus(t);
  return r ? r.diff <= 0 : false;
};

/* ── KEYWORD → SUBJECT AUTO-TAG ─────────────────────────── */
const KW_MAP = {
  "Engineering Mathematics": [
    "eigenvalue",
    "matrix",
    "determinant",
    "linear algebra",
    "calculus",
    "differential equation",
    "laplace",
    "z-transform",
    "fourier",
    "probability",
    "statistics",
    "numerical",
    "integration",
    "gradient",
    "divergence",
    "curl",
    "partial",
    "series",
    "vector",
    "complex",
    "contour",
    "residue",
    "newton",
    "runge",
    "interpolation",
  ],
  "Networks, Signals & Systems": [
    "network",
    "circuit",
    "mesh",
    "kirchhoff",
    "thevenin",
    "norton",
    "superposition",
    "two-port",
    "transient",
    "lti",
    "convolution",
    "impulse",
    "step response",
    "bode",
    "nyquist",
    "signal",
    "system",
    "sampling",
    "aliasing",
    "sinusoid",
    "phasor",
    "impedance",
    "admittance",
    "resonance",
    "filter",
  ],
  "Electronic Devices": [
    "semiconductor",
    "pn junction",
    "diode",
    "bjt",
    "mosfet",
    "jfet",
    "transistor",
    "drift",
    "diffusion",
    "fermi",
    "energy band",
    "depletion",
    "inversion",
    "zener",
    "led",
    "solar cell",
    "photodiode",
    "thyristor",
    "ic fabrication",
    "doping",
    "carrier",
    "mobility",
    "recombination",
    "avalanche",
  ],
  "Analog Circuits": [
    "amplifier",
    "op-amp",
    "feedback",
    "oscillator",
    "clipper",
    "clamper",
    "rectifier",
    "biasing",
    "small signal",
    "gain",
    "bandwidth",
    "slew rate",
    "common emitter",
    "common source",
    "push pull",
    "class a",
    "class b",
    "class ab",
    "wien bridge",
    "colpitts",
    "hartley",
    "power amplifier",
  ],
  "Digital Circuits": [
    "boolean",
    "logic gate",
    "flip flop",
    "latch",
    "counter",
    "shift register",
    "adc",
    "dac",
    "mux",
    "demux",
    "encoder",
    "decoder",
    "karnaugh",
    "k-map",
    "sop",
    "pos",
    "sequential",
    "combinational",
    "pld",
    "fpga",
    "memory",
    "sram",
    "dram",
    "rom",
    "timing",
    "setup",
    "hold",
  ],
  "Control Systems": [
    "transfer function",
    "block diagram",
    "signal flow",
    "root locus",
    "bode plot",
    "nyquist",
    "routh",
    "stability",
    "steady state",
    "transient",
    "pid",
    "controller",
    "gain margin",
    "phase margin",
    "state space",
    "controllability",
    "observability",
    "pole",
    "zero",
    "closed loop",
    "open loop",
  ],
  Communications: [
    "modulation",
    "am",
    "fm",
    "pm",
    "pcm",
    "delta modulation",
    "ask",
    "fsk",
    "psk",
    "qam",
    "noise",
    "snr",
    "bandwidth",
    "channel",
    "entropy",
    "huffman",
    "hamming",
    "error correction",
    "coding",
    "multiplexing",
    "fdm",
    "tdm",
    "cdma",
    "spread spectrum",
    "demodulation",
    "constellation",
  ],
  Electromagnetics: [
    "maxwell",
    "wave",
    "transmission line",
    "waveguide",
    "antenna",
    "radiation",
    "electric field",
    "magnetic field",
    "flux",
    "gauss",
    "ampere",
    "faraday",
    "poynting",
    "vswr",
    "reflection",
    "refraction",
    "polarization",
    "smith chart",
    "impedance matching",
    "microstrip",
    "stripline",
    "coaxial",
  ],
  "General Aptitude": [
    "verbal",
    "reasoning",
    "comprehension",
    "aptitude",
    "analogy",
    "sentence",
    "grammar",
    "vocabulary",
    "critical",
    "data interpretation",
    "ratio",
    "proportion",
    "percentage",
    "probability",
    "permutation",
    "combination",
    "algebra",
    "geometry",
    "trigonometry",
  ],
};
const autoDetectSubject = (text) => {
  const lower = text.toLowerCase();
  let best = { subject: "General / Other", count: 0 };
  for (const [subject, keywords] of Object.entries(KW_MAP)) {
    const count = keywords.filter((kw) => lower.includes(kw)).length;
    if (count > best.count) best = { subject, count };
  }
  return best.subject;
};
const extractAutoTags = (text) => {
  const lower = text.toLowerCase();
  const tags = [];
  for (const keywords of Object.values(KW_MAP))
    for (const kw of keywords)
      if (lower.includes(kw) && !tags.includes(kw)) tags.push(kw);
  return tags.slice(0, 5);
};

/* ── IMAGE OCR (via Claude Vision API) — shared utility ──────
   Tesseract.js can't run inside the Claude.ai artifact sandbox
   (CSP blocks the CDN script + WASM/worker files), which is why
   OCR never produced any text. This sends the photo straight to
   the Claude API (already wired up — no key needed) and asks it
   to transcribe everything. Much better on handwriting too. The
   image is still downscaled + contrast-boosted first to keep the
   request small and fast.                                       */
const preprocessImageForOCR = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read image file"));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        try {
          // Smaller cap = smaller base64 payload = faster API round-trip
          const MAX_DIM = 1000;
          let { width, height } = img;
          const scale = Math.min(1, MAX_DIM / Math.max(width, height));
          width = Math.max(1, Math.round(width * scale));
          height = Math.max(1, Math.round(height * scale));
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          // CSS canvas filter: same greyscale + contrast boost as the old
          // per-pixel loop but runs in a single GPU-accelerated draw call.
          // No JS iterations → no sandbox iteration-limit error.
          ctx.filter = "grayscale(1) contrast(1.3)";
          ctx.drawImage(img, 0, 0, width, height);
          ctx.filter = "none";
          resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.82) });
        } catch (e) {
          reject(e);
        }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
// onProgress(pct 0-100, statusText) — returns { text, preview }
const runImageOCR = async (file, onProgress) => {
  onProgress(8, "Preparing image…");
  const { dataUrl } = await preprocessImageForOCR(file);
  const base64Data = dataUrl.split(",")[1];

  onProgress(25, "Sending to AI for reading…");
  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: 1000,
      messages: [
        {
          role: "user",
          content: [
            {
              type: "image",
              source: {
                type: "base64",
                media_type: "image/jpeg",
                data: base64Data,
              },
            },
            {
              type: "text",
              text: "Transcribe ALL text visible in this image exactly as written, including formulas, numbers, and symbols. Output ONLY the transcribed text with no preamble, commentary, or explanation. If it's handwritten, do your best to read it.",
            },
          ],
        },
      ],
    }),
  });

  onProgress(80, "Reading text…");
  if (!response.ok) throw new Error(`OCR request failed (${response.status})`);
  const data = await response.json();
  const text = (data?.content || [])
    .map((b) => (b.type === "text" ? b.text : ""))
    .join("")
    .trim();

  onProgress(100, "Done");
  return { text, preview: dataUrl };
};
// Downscales + JPEG-compresses an uploaded image before storing it as a
// data URL, so a single flashcard photo doesn't blow past localStorage's
// ~5MB quota (which would silently fail to save the whole card).
const compressImageForStorage = (file, maxDim = 800, quality = 0.7) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read image file"));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        try {
          let { width, height } = img;
          const scale = Math.min(1, maxDim / Math.max(width, height));
          width = Math.max(1, Math.round(width * scale));
          height = Math.max(1, Math.round(height * scale));
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch (e) {
          reject(e);
        }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }); /* ── TOPIC FACTORY ──────────────────────────────────────── */
const mkTopics = (names) =>
  names.map((name) => ({
    id: uid(),
    name,
    lec: false,
    asgn: false,
    pyq: false,
    r1: false,
    r2: false,
    r3: false,
    test: false,
    notes: "",
    confidence: 0,
    studiedDate: null,
    r1Date: null,
    r2Date: null,
    r3Date: null,
    difficulty: 0,
    timeSpent: 0,
    lectureUrl: "",
  }));

/* ── PYQ YEAR-WISE FREQUENCY DATA ───────────────────────── */
const PYQ_FREQ = {
  "Linear Algebra": [1, 2, 1, 1, 2, 1, 2, 1, 1, 2],
  Calculus: [2, 1, 2, 2, 1, 2, 1, 2, 2, 1],
  "Differential Equations": [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  "Complex Analysis": [1, 0, 1, 1, 0, 1, 0, 1, 1, 0],
  "Probability & Statistics": [2, 2, 1, 2, 2, 2, 2, 2, 2, 2],
  "Numerical Methods": [0, 1, 0, 1, 0, 0, 1, 0, 0, 1],
  "Circuit Laws & Mesh Analysis": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Network Theorems": [2, 3, 2, 3, 3, 2, 3, 2, 3, 3],
  "Two-Port Networks": [1, 2, 1, 2, 1, 2, 1, 2, 1, 2],
  "Transient Analysis": [1, 1, 2, 1, 2, 1, 1, 2, 1, 1],
  "LTI Systems & Convolution": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Fourier Transform (CT/DT)": [2, 2, 2, 2, 3, 2, 2, 2, 2, 2],
  "Laplace Transform": [2, 1, 2, 1, 2, 2, 1, 2, 1, 2],
  "Z-Transform": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Sampling Theorem": [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  "Semiconductors & Energy Bands": [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  "PN Junction Diode": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "BJT Characteristics": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "MOSFET & JFET": [2, 3, 2, 3, 3, 2, 3, 2, 3, 3],
  "Special Devices (Zener, LED, Solar)": [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  "IC Fabrication Basics": [0, 0, 1, 0, 0, 1, 0, 0, 1, 0],
  "Diode Circuits (Clippers, Clampers)": [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  "BJT Amplifiers": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "FET Amplifiers": [1, 2, 1, 2, 1, 2, 1, 2, 1, 2],
  "Op-Amp Fundamentals": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Op-Amp Applications": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Feedback Amplifiers": [1, 2, 1, 2, 2, 1, 2, 1, 2, 1],
  Oscillators: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  "Power Amplifiers": [0, 1, 0, 1, 0, 0, 1, 0, 1, 0],
  "Number Systems & Boolean Algebra": [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  "K-Map Minimization": [1, 2, 1, 2, 1, 2, 1, 2, 1, 2],
  "Combinational Circuits": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Flip-Flops & Latches": [1, 2, 1, 2, 1, 2, 1, 2, 1, 2],
  "Counters & Shift Registers": [1, 1, 1, 2, 1, 1, 2, 1, 1, 1],
  "ADC & DAC": [1, 1, 2, 1, 1, 2, 1, 1, 1, 2],
  "Memory & PLDs": [0, 1, 0, 0, 1, 0, 0, 1, 0, 0],
  "Transfer Functions": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Block Diagrams & SFG": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Time Response Analysis": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Bode & Polar Plots": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Root Locus": [1, 2, 1, 2, 1, 2, 1, 2, 1, 2],
  "Stability (Routh, Nyquist)": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "State Space Analysis": [1, 1, 2, 1, 1, 2, 1, 1, 1, 2],
  "AM / FM / PM Modulation": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Sampling & Quantization": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "PCM & Delta Modulation": [1, 1, 2, 1, 2, 1, 1, 2, 1, 1],
  "Digital Modulation (ASK/FSK/PSK)": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Noise Analysis": [1, 2, 1, 2, 1, 2, 1, 2, 1, 2],
  "Information Theory & Entropy": [1, 1, 2, 1, 2, 1, 1, 2, 1, 1],
  "Error Correction Coding": [1, 1, 1, 2, 1, 1, 1, 1, 2, 1],
  "Maxwell's Equations": [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  "Uniform Plane Waves": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Transmission Lines": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  Waveguides: [1, 2, 1, 2, 1, 2, 1, 2, 1, 2],
  "Antennas & Radiation": [1, 1, 2, 1, 2, 1, 1, 2, 1, 2],
  "Verbal Ability": [3, 3, 3, 3, 3, 3, 3, 3, 3, 3],
  "Critical Reasoning": [3, 3, 3, 3, 3, 3, 3, 3, 3, 3],
  "Reading Comprehension": [2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
  "Numerical Aptitude": [4, 4, 4, 4, 4, 4, 4, 4, 4, 4],
  "Data Interpretation": [3, 3, 3, 3, 3, 3, 3, 3, 3, 3],
};
/* ── COLLEGE / PSU CUTOFF DATA ──────────────────────────── */
const COLLEGE_DATA = [
  // IITs
  {
    name: "IIT Delhi",
    dept: "ECE / VLSI",
    type: "IIT",
    tier: 1,
    minScore: 72,
    typicalRank: 400,
  },
  {
    name: "IIT Bombay",
    dept: "Electrical Engg.",
    type: "IIT",
    tier: 1,
    minScore: 70,
    typicalRank: 500,
  },
  {
    name: "IIT Madras",
    dept: "ECE",
    type: "IIT",
    tier: 1,
    minScore: 69,
    typicalRank: 600,
  },
  {
    name: "IIT Kanpur",
    dept: "EE (Comm. & SP)",
    type: "IIT",
    tier: 1,
    minScore: 68,
    typicalRank: 700,
  },
  {
    name: "IIT Kharagpur",
    dept: "ECE",
    type: "IIT",
    tier: 1,
    minScore: 67,
    typicalRank: 900,
  },
  {
    name: "IIT Roorkee",
    dept: "ECE",
    type: "IIT",
    tier: 1,
    minScore: 65,
    typicalRank: 1100,
  },
  {
    name: "IIT Hyderabad",
    dept: "ECE",
    type: "IIT",
    tier: 2,
    minScore: 62,
    typicalRank: 1400,
  },
  {
    name: "IIT Guwahati",
    dept: "ECE",
    type: "IIT",
    tier: 2,
    minScore: 60,
    typicalRank: 1700,
  },
  {
    name: "IIT (BHU) Varanasi",
    dept: "ECE",
    type: "IIT",
    tier: 2,
    minScore: 57,
    typicalRank: 2200,
  },
  {
    name: "IIT Jodhpur",
    dept: "ECE",
    type: "IIT",
    tier: 2,
    minScore: 54,
    typicalRank: 2800,
  },
  {
    name: "IIT Patna",
    dept: "ECE",
    type: "IIT",
    tier: 2,
    minScore: 52,
    typicalRank: 3200,
  },
  {
    name: "IIT Mandi",
    dept: "ECE",
    type: "IIT",
    tier: 2,
    minScore: 50,
    typicalRank: 3800,
  },
  // NITs & IIITs
  {
    name: "IIIT Hyderabad",
    dept: "ECE",
    type: "IIIT",
    tier: 1,
    minScore: 67,
    typicalRank: 900,
  },
  {
    name: "IIIT Bangalore",
    dept: "ECE",
    type: "IIIT",
    tier: 1,
    minScore: 62,
    typicalRank: 1400,
  },
  {
    name: "NIT Trichy",
    dept: "ECE",
    type: "NIT",
    tier: 1,
    minScore: 58,
    typicalRank: 2000,
  },
  {
    name: "NIT Warangal",
    dept: "ECE",
    type: "NIT",
    tier: 1,
    minScore: 57,
    typicalRank: 2200,
  },
  {
    name: "NIT Surathkal",
    dept: "ECE",
    type: "NIT",
    tier: 1,
    minScore: 55,
    typicalRank: 2600,
  },
  {
    name: "NIT Calicut",
    dept: "ECE",
    type: "NIT",
    tier: 1,
    minScore: 54,
    typicalRank: 2900,
  },
  {
    name: "NIT Rourkela",
    dept: "ECE",
    type: "NIT",
    tier: 1,
    minScore: 53,
    typicalRank: 3100,
  },
  {
    name: "NIT Kurukshetra",
    dept: "ECE",
    type: "NIT",
    tier: 2,
    minScore: 50,
    typicalRank: 3800,
  },
  {
    name: "MNIT Jaipur",
    dept: "ECE",
    type: "NIT",
    tier: 2,
    minScore: 50,
    typicalRank: 3800,
  },
  {
    name: "VNIT Nagpur",
    dept: "ECE",
    type: "NIT",
    tier: 2,
    minScore: 49,
    typicalRank: 4200,
  },
  {
    name: "NIT Silchar",
    dept: "ECE",
    type: "NIT",
    tier: 2,
    minScore: 46,
    typicalRank: 5500,
  },
  // PSUs
  {
    name: "BARC",
    dept: "Scientific Officer",
    type: "PSU",
    tier: 1,
    minScore: 67,
    typicalRank: null,
  },
  {
    name: "DRDO",
    dept: "Scientist B",
    type: "PSU",
    tier: 1,
    minScore: 65,
    typicalRank: null,
  },
  {
    name: "ISRO",
    dept: "Scientist/Engineer SC",
    type: "PSU",
    tier: 1,
    minScore: 62,
    typicalRank: null,
  },
  {
    name: "NTPC",
    dept: "Executive Trainee",
    type: "PSU",
    tier: 1,
    minScore: 58,
    typicalRank: null,
  },
  {
    name: "PGCIL",
    dept: "Executive Trainee",
    type: "PSU",
    tier: 1,
    minScore: 57,
    typicalRank: null,
  },
  {
    name: "BEL",
    dept: "Probationary Engineer",
    type: "PSU",
    tier: 1,
    minScore: 55,
    typicalRank: null,
  },
  {
    name: "IOCL",
    dept: "Engineer",
    type: "PSU",
    tier: 2,
    minScore: 55,
    typicalRank: null,
  },
  {
    name: "GAIL",
    dept: "Executive ET",
    type: "PSU",
    tier: 2,
    minScore: 52,
    typicalRank: null,
  },
  {
    name: "BHEL",
    dept: "Engineer Trainee",
    type: "PSU",
    tier: 2,
    minScore: 50,
    typicalRank: null,
  },
  {
    name: "ONGC",
    dept: "AEE Electronics",
    type: "PSU",
    tier: 2,
    minScore: 50,
    typicalRank: null,
  },
  {
    name: "AAI",
    dept: "Junior Executive",
    type: "PSU",
    tier: 2,
    minScore: 50,
    typicalRank: null,
  },
  {
    name: "BSNL",
    dept: "JTO / DE",
    type: "PSU",
    tier: 2,
    minScore: 48,
    typicalRank: null,
  },
  {
    name: "CIL",
    dept: "Management Trainee",
    type: "PSU",
    tier: 3,
    minScore: 45,
    typicalRank: null,
  },
  {
    name: "NALCO",
    dept: "Engineer Trainee",
    type: "PSU",
    tier: 3,
    minScore: 43,
    typicalRank: null,
  },
];
const TYPE_COLORS = {
  IIT: "#b39dff",
  NIT: "#5ecfff",
  IIIT: "#4eeaaa",
  PSU: "#ffe666",
};
/* ── BUILT-IN TOPIC DIFFICULTY ──────────────────────────── */
const TOPIC_DIFFICULTY = {
  "Linear Algebra": 4,
  Calculus: 3,
  "Differential Equations": 3,
  "Complex Analysis": 4,
  "Probability & Statistics": 3,
  "Numerical Methods": 2,
  "Circuit Laws & Mesh Analysis": 3,
  "Network Theorems": 4,
  "Two-Port Networks": 4,
  "Transient Analysis": 3,
  "LTI Systems & Convolution": 4,
  "Fourier Transform (CT/DT)": 4,
  "Laplace Transform": 3,
  "Z-Transform": 4,
  "Sampling Theorem": 2,
  "Semiconductors & Energy Bands": 3,
  "PN Junction Diode": 3,
  "BJT Characteristics": 4,
  "MOSFET & JFET": 4,
  "Special Devices (Zener, LED, Solar)": 2,
  "IC Fabrication Basics": 2,
  "Diode Circuits (Clippers, Clampers)": 2,
  "BJT Amplifiers": 4,
  "FET Amplifiers": 3,
  "Op-Amp Fundamentals": 3,
  "Op-Amp Applications": 4,
  "Feedback Amplifiers": 4,
  Oscillators: 3,
  "Power Amplifiers": 3,
  "Number Systems & Boolean Algebra": 2,
  "K-Map Minimization": 3,
  "Combinational Circuits": 3,
  "Flip-Flops & Latches": 3,
  "Counters & Shift Registers": 3,
  "ADC & DAC": 3,
  "Memory & PLDs": 2,
  "Transfer Functions": 3,
  "Block Diagrams & SFG": 3,
  "Time Response Analysis": 4,
  "Bode & Polar Plots": 4,
  "Root Locus": 5,
  "Stability (Routh, Nyquist)": 4,
  "State Space Analysis": 5,
  "AM / FM / PM Modulation": 3,
  "Sampling & Quantization": 3,
  "PCM & Delta Modulation": 2,
  "Digital Modulation (ASK/FSK/PSK)": 3,
  "Noise Analysis": 4,
  "Information Theory & Entropy": 4,
  "Error Correction Coding": 3,
  "Maxwell's Equations": 3,
  "Uniform Plane Waves": 3,
  "Transmission Lines": 4,
  Waveguides: 4,
  "Antennas & Radiation": 4,
  "Verbal Ability": 2,
  "Critical Reasoning": 3,
  "Reading Comprehension": 2,
  "Numerical Aptitude": 3,
  "Data Interpretation": 3,
};
/* ── DESIGN TOKENS ─────────────────────────────────────── */
const G = {
  bg: "#050810",
  bgDeep: "#020408",
  bgMid: "#0b1025",
  glass: "rgba(18,28,52,0.88)",
  glassHover: "rgba(24,38,72,0.96)",
  glassBorder: "rgba(120,160,255,0.28)",
  glassBorderBright: "rgba(120,160,255,0.5)",
  frost: "rgba(130,170,255,0.1)",
  frostMid: "rgba(130,170,255,0.16)",
  violet: "#b39dff",
  cyan: "#5ecfff",
  emerald: "#4eeaaa",
  amber: "#ffe666",
  rose: "#ff5577",
  pink: "#ff85c0",
  purple: "#d580ff",
  orange: "#ff9f6b",
  green: "#5af080",
  textPrimary: "#ffffff",
  textSecondary: "#d4e4ff",
  textMuted: "#9ab4d8",
  textDim: "#6080a8",
  glow: (c, i = 0.5) =>
    `0 0 18px ${c}${Math.round(i * 255)
      .toString(16)
      .padStart(2, "0")}`,
};
/* ── TOPIC CO-OCCURRENCE CLUSTERS ───────────────────────── */
const TOPIC_CLUSTERS = [
  {
    id: "signals",
    name: "Signals & Systems Core",
    color: G.emerald,
    strength: 5,
    desc: "Always tested together — one question spans 2-3 of these",
    topics: [
      "LTI Systems & Convolution",
      "Fourier Transform (CT/DT)",
      "Laplace Transform",
      "Z-Transform",
      "Sampling Theorem",
    ],
  },
  {
    id: "control",
    name: "Control Systems Cluster",
    color: G.amber,
    strength: 5,
    desc: "Bode, Root Locus, and Stability form one interlinked question set",
    topics: [
      "Transfer Functions",
      "Block Diagrams & SFG",
      "Bode & Polar Plots",
      "Root Locus",
      "Stability (Routh, Nyquist)",
      "Time Response Analysis",
    ],
  },
  {
    id: "bjt",
    name: "BJT & Analog Circuit Chain",
    color: G.pink,
    strength: 4,
    desc: "Device physics → small-signal model → amplifier → feedback",
    topics: [
      "BJT Characteristics",
      "BJT Amplifiers",
      "Feedback Amplifiers",
      "Op-Amp Fundamentals",
      "Op-Amp Applications",
    ],
  },
  {
    id: "mosfet",
    name: "MOSFET & Digital Bridge",
    color: G.cyan,
    strength: 4,
    desc: "MOSFET feeds into both analog FET amplifiers and CMOS digital circuits",
    topics: [
      "MOSFET & JFET",
      "FET Amplifiers",
      "Combinational Circuits",
      "ADC & DAC",
    ],
  },
  {
    id: "comm",
    name: "Communications Chain",
    color: G.purple,
    strength: 5,
    desc: "Modulation → quantization → noise → coding appear as a 4–5 mark block",
    topics: [
      "AM / FM / PM Modulation",
      "Sampling & Quantization",
      "PCM & Delta Modulation",
      "Noise Analysis",
      "Information Theory & Entropy",
      "Digital Modulation (ASK/FSK/PSK)",
    ],
  },
  {
    id: "em",
    name: "Electromagnetics Chain",
    color: G.rose,
    strength: 4,
    desc: "Wave path: Maxwell → plane waves → transmission lines → antennas",
    topics: [
      "Maxwell's Equations",
      "Uniform Plane Waves",
      "Transmission Lines",
      "Waveguides",
      "Antennas & Radiation",
    ],
  },
  {
    id: "networks",
    name: "Network Analysis Cluster",
    color: G.orange,
    strength: 4,
    desc: "Theorems and two-port are applied together in the same circuit analysis question",
    topics: [
      "Circuit Laws & Mesh Analysis",
      "Network Theorems",
      "Two-Port Networks",
      "Transient Analysis",
    ],
  },
  {
    id: "math",
    name: "Mathematics Foundation",
    color: G.violet,
    strength: 3,
    desc: "Linear algebra + probability in every paper; calculus underpins EM & control",
    topics: [
      "Linear Algebra",
      "Calculus",
      "Differential Equations",
      "Probability & Statistics",
      "Complex Analysis",
    ],
  },
  {
    id: "digital",
    name: "Digital Sequential Cluster",
    color: G.green,
    strength: 3,
    desc: "Flip-flops, counters, shift registers always appear in the same block",
    topics: [
      "Flip-Flops & Latches",
      "Counters & Shift Registers",
      "K-Map Minimization",
      "Number Systems & Boolean Algebra",
    ],
  },
];
/* ── STUDY ORDER ─────────────────────────────────────────── */
const STUDY_ORDER = [
  { id: "math", deps: [], reason: "Foundation for all signal and system math" },
  {
    id: "nss",
    deps: ["math"],
    reason: "Signals need calculus, transforms, and complex analysis",
  },
  {
    id: "ed",
    deps: ["math"],
    reason: "Device physics uses differential equations and probability",
  },
  {
    id: "ac",
    deps: ["ed"],
    reason: "Circuit analysis requires device knowledge",
  },
  {
    id: "dc",
    deps: ["math"],
    reason: "Boolean algebra and number systems are self-contained",
  },
  {
    id: "ctrl",
    deps: ["math", "nss"],
    reason: "Laplace/Z-transform from NSS + calculus from math",
  },
  {
    id: "comm",
    deps: ["math", "nss"],
    reason: "Fourier, probability, and system theory from math + NSS",
  },
  {
    id: "em",
    deps: ["math", "nss"],
    reason: "Vector calculus from math, wave theory from NSS",
  },
  { id: "ga", deps: [], reason: "Practice daily — improves across all stages" },
];

/* ── INITIAL DATA ───────────────────────────────────────── */
const INIT_SUBJECTS = [
  {
    id: "math",
    name: "Engineering Mathematics",
    clr: "#b39dff",
    wt: "15%",
    topics: mkTopics([
      "Linear Algebra",
      "Calculus",
      "Differential Equations",
      "Complex Analysis",
      "Probability & Statistics",
      "Numerical Methods",
    ]),
  },
  {
    id: "nss",
    name: "Networks, Signals & Systems",
    clr: "#4eeaaa",
    wt: "12%",
    topics: mkTopics([
      "Circuit Laws & Mesh Analysis",
      "Network Theorems",
      "Two-Port Networks",
      "Transient Analysis",
      "LTI Systems & Convolution",
      "Fourier Transform (CT/DT)",
      "Laplace Transform",
      "Z-Transform",
      "Sampling Theorem",
    ]),
  },
  {
    id: "ed",
    name: "Electronic Devices",
    clr: "#ff9f6b",
    wt: "10%",
    topics: mkTopics([
      "Semiconductors & Energy Bands",
      "PN Junction Diode",
      "BJT Characteristics",
      "MOSFET & JFET",
      "Special Devices (Zener, LED, Solar)",
      "IC Fabrication Basics",
    ]),
  },
  {
    id: "ac",
    name: "Analog Circuits",
    clr: "#ff85c0",
    wt: "12%",
    topics: mkTopics([
      "Diode Circuits (Clippers, Clampers)",
      "BJT Amplifiers",
      "FET Amplifiers",
      "Op-Amp Fundamentals",
      "Op-Amp Applications",
      "Feedback Amplifiers",
      "Oscillators",
      "Power Amplifiers",
    ]),
  },
  {
    id: "dc",
    name: "Digital Circuits",
    clr: "#5ecfff",
    wt: "10%",
    topics: mkTopics([
      "Number Systems & Boolean Algebra",
      "K-Map Minimization",
      "Combinational Circuits",
      "Flip-Flops & Latches",
      "Counters & Shift Registers",
      "ADC & DAC",
      "Memory & PLDs",
    ]),
  },
  {
    id: "ctrl",
    name: "Control Systems",
    clr: "#ffe666",
    wt: "10%",
    topics: mkTopics([
      "Transfer Functions",
      "Block Diagrams & SFG",
      "Time Response Analysis",
      "Bode & Polar Plots",
      "Root Locus",
      "Stability (Routh, Nyquist)",
      "State Space Analysis",
    ]),
  },
  {
    id: "comm",
    name: "Communications",
    clr: "#d580ff",
    wt: "12%",
    topics: mkTopics([
      "AM / FM / PM Modulation",
      "Sampling & Quantization",
      "PCM & Delta Modulation",
      "Digital Modulation (ASK/FSK/PSK)",
      "Noise Analysis",
      "Information Theory & Entropy",
      "Error Correction Coding",
    ]),
  },
  {
    id: "em",
    name: "Electromagnetics",
    clr: "#ff7090",
    wt: "9%",
    topics: mkTopics([
      "Maxwell's Equations",
      "Uniform Plane Waves",
      "Transmission Lines",
      "Waveguides",
      "Antennas & Radiation",
    ]),
  },
  {
    id: "ga",
    name: "General Aptitude",
    clr: "#5af080",
    wt: "15%",
    topics: mkTopics([
      "Verbal Ability",
      "Critical Reasoning",
      "Reading Comprehension",
      "Numerical Aptitude",
      "Data Interpretation",
    ]),
  },
];

const INIT_ROUTINE = [
  {
    id: "r1",
    time: "5:00 AM",
    task: "Wake Up & Morning Routine",
    cat: "personal",
    done: false,
  },
  {
    id: "r2",
    time: "5:30 AM",
    task: "Exercise / Yoga / Walk",
    cat: "health",
    done: false,
  },
  {
    id: "r3",
    time: "6:30 AM",
    task: "Study Session 1 — Core Subject",
    cat: "study",
    done: false,
  },
  {
    id: "r4",
    time: "9:00 AM",
    task: "Breakfast",
    cat: "personal",
    done: false,
  },
  {
    id: "r5",
    time: "9:30 AM",
    task: "Study Session 2 — PYQ Practice",
    cat: "study",
    done: false,
  },
  {
    id: "r6",
    time: "12:00 PM",
    task: "Lunch Break",
    cat: "personal",
    done: false,
  },
  {
    id: "r7",
    time: "1:00 PM",
    task: "Rest / Nap (30 min)",
    cat: "health",
    done: false,
  },
  {
    id: "r8",
    time: "1:30 PM",
    task: "Study Session 3 — Weak Areas",
    cat: "study",
    done: false,
  },
  {
    id: "r9",
    time: "4:00 PM",
    task: "Tea Break",
    cat: "personal",
    done: false,
  },
  {
    id: "r10",
    time: "4:30 PM",
    task: "Study Session 4 — Revision",
    cat: "study",
    done: false,
  },
  {
    id: "r11",
    time: "6:30 PM",
    task: "Evening Walk / Exercise",
    cat: "health",
    done: false,
  },
  { id: "r12", time: "7:30 PM", task: "Dinner", cat: "personal", done: false },
  {
    id: "r13",
    time: "8:00 PM",
    task: "Study Session 5 — Mock Test / GA",
    cat: "study",
    done: false,
  },
  {
    id: "r14",
    time: "10:00 PM",
    task: "Review Day & Plan Tomorrow",
    cat: "study",
    done: false,
  },
  { id: "r15", time: "10:30 PM", task: "Sleep", cat: "health", done: false },
];

/* ── FORMULA FLASH CARDS SEED DATA (SM-2 fields included) ── */
const INIT_FLASHCARDS = [
  {
    id: uid(),
    subject: "Engineering Mathematics",
    front: "Eigenvalue condition",
    back: "Av = λv  →  det(A−λI) = 0",
    backImg: "",
    known: false,
    due: todayS(),
    ease: 2.5,
    interval: 1,
    reps: 0,
  },
  {
    id: uid(),
    subject: "Networks, Signals & Systems",
    front: "Thevenin voltage",
    back: "V_th = V_oc (open-circuit voltage at terminals)",
    backImg: "",
    known: false,
    due: todayS(),
    ease: 2.5,
    interval: 1,
    reps: 0,
  },
  {
    id: uid(),
    subject: "Networks, Signals & Systems",
    front: "Convolution (continuous)",
    back: "y(t) = x(t)*h(t) = ∫ x(τ)h(t−τ) dτ",
    backImg: "",
    known: false,
    due: todayS(),
    ease: 2.5,
    interval: 1,
    reps: 0,
  },
  {
    id: uid(),
    subject: "Electronic Devices",
    front: "Diode current equation",
    back: "I = I₀(e^(V/V_T) − 1),  V_T = kT/q ≈ 26 mV at 300K",
    backImg: "",
    known: false,
    due: todayS(),
    ease: 2.5,
    interval: 1,
    reps: 0,
  },
  {
    id: uid(),
    subject: "Control Systems",
    front: "2nd order system ωn, ζ",
    back: "s² + 2ζωₙs + ωₙ² = 0  →  ωd = ωₙ√(1−ζ²)",
    backImg: "",
    known: false,
    due: todayS(),
    ease: 2.5,
    interval: 1,
    reps: 0,
  },
  {
    id: uid(),
    subject: "Control Systems",
    front: "Gain Margin (Bode)",
    back: "GM = −20 log|G(jω)| at phase crossover (∠G = −180°)",
    backImg: "",
    known: false,
    due: todayS(),
    ease: 2.5,
    interval: 1,
    reps: 0,
  },
  {
    id: uid(),
    subject: "Communications",
    front: "Shannon capacity",
    back: "C = B log₂(1 + S/N)  bits/second",
    backImg: "",
    known: false,
    due: todayS(),
    ease: 2.5,
    interval: 1,
    reps: 0,
  },
  {
    id: uid(),
    subject: "Electromagnetics",
    front: "VSWR formula",
    back: "VSWR = (1+|Γ|)/(1−|Γ|),  Γ = (Z_L − Z_0)/(Z_L + Z_0)",
    backImg: "",
    known: false,
    due: todayS(),
    ease: 2.5,
    interval: 1,
    reps: 0,
  },
  {
    id: uid(),
    subject: "Analog Circuits",
    front: "Op-amp inverting gain",
    back: "A_v = −R_f / R_in",
    backImg: "",
    known: false,
    due: todayS(),
    ease: 2.5,
    interval: 1,
    reps: 0,
  },
];

/* ── CONSTANTS ──────────────────────────────────────────── */
const TRACK_FIELDS = ["lec", "asgn", "pyq", "r1", "r2", "r3", "test"];
const COLS = [
  { key: "lec", label: "Lec", color: "#4eeaaa" },
  { key: "asgn", label: "Assign", color: "#5ecfff" },
  { key: "pyq", label: "PYQ", color: "#ffe666" },
  { key: "r1", label: "Rev 1", color: "#ff85c0" },
  { key: "r2", label: "Rev 2", color: "#d580ff" },
  { key: "r3", label: "Rev 3", color: "#ff9f6b" },
  { key: "test", label: "Test", color: "#ff7090" },
];
/* ── FORMULA KEYBOARD SYMBOLS ───────────────────────────── */
const FORMULA_SYMBOLS = [
  { s: "∫", d: "Integral" },
  { s: "∬", d: "Double integral" },
  { s: "∂", d: "Partial deriv." },
  { s: "Σ", d: "Summation" },
  { s: "Π", d: "Product" },
  { s: "√", d: "Square root" },
  { s: "∞", d: "Infinity" },
  { s: "∇", d: "Nabla/Del" },
  { s: "∈", d: "Element of" },
  { s: "λ", d: "Lambda" },
  { s: "ω", d: "omega" },
  { s: "Ω", d: "Omega (cap)" },
  { s: "∠", d: "Angle" },
  { s: "φ", d: "Phi" },
  { s: "Φ", d: "Phi (cap)" },
  { s: "δ", d: "delta" },
  { s: "Δ", d: "Delta (cap)" },
  { s: "π", d: "Pi" },
  { s: "α", d: "Alpha" },
  { s: "β", d: "Beta" },
  { s: "γ", d: "Gamma" },
  { s: "θ", d: "Theta" },
  { s: "Θ", d: "Theta (cap)" },
  { s: "μ", d: "Mu" },
  { s: "σ", d: "sigma" },
  { s: "ε", d: "Epsilon" },
  { s: "η", d: "Eta" },
  { s: "τ", d: "Tau" },
  { s: "ψ", d: "Psi" },
  { s: "ρ", d: "Rho" },
  { s: "≈", d: "Approx" },
  { s: "≠", d: "Not equal" },
  { s: "≤", d: "Less/equal" },
  { s: "≥", d: "Greater/eq" },
  { s: "→", d: "Right arrow" },
  { s: "⇒", d: "Implies" },
  { s: "±", d: "Plus/minus" },
  { s: "×", d: "Times" },
  { s: "÷", d: "Divide" },
  { s: "°", d: "Degree" },
  { s: "²", d: "Squared" },
  { s: "³", d: "Cubed" },
  { s: "⁻¹", d: "Inverse" },
  { s: "ₒ", d: "Subscript 0" },
  { s: "ₙ", d: "Subscript n" },
  { s: "ⱼ", d: "Subscript j" },
  { s: "⁺", d: "Superscript +" },
  { s: "∝", d: "Proportional" },
];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CAT_COLOR = {
  study: "#b39dff",
  health: "#5af080",
  personal: "#ffe666",
  ga: "#5af080",
  error: "#ff5577",
  test: "#ffe666",
  revision: "#ff85c0",
};
const CAT_ICON = {
  study: "📚",
  health: "🌿",
  personal: "☕",
  ga: "✏️",
  error: "🐛",
  test: "🧪",
  revision: "🔁",
};
const ERROR_TYPES = [
  "Conceptual",
  "Calculation",
  "Formula Error",
  "Reading Mistake",
  "Silly Mistake",
  "Tricky Question",
];
const ALL_SUBJS = [
  "Engineering Mathematics",
  "Networks, Signals & Systems",
  "Electronic Devices",
  "Analog Circuits",
  "Digital Circuits",
  "Control Systems",
  "Communications",
  "Electromagnetics",
  "General Aptitude",
  "General / Other",
];
const SUBJ_SHORT = [
  "Eng Math",
  "Networks",
  "E.Devices",
  "Analog",
  "Digital",
  "Control",
  "Comm",
  "EM",
  "GA",
  "General",
];
const WEIGHTAGE = {
  "Engineering Mathematics": 15,
  "Networks, Signals & Systems": 12,
  "Analog Circuits": 12,
  Communications: 12,
  "Electronic Devices": 10,
  "Digital Circuits": 10,
  "Control Systems": 10,
  Electromagnetics: 9,
  "General Aptitude": 15,
};
const DEFAULT_PROFILE = {
  name: "GATE Aspirant",
  photo: "",
  examDate: "2027-02-01",
};
const PYQ_YEARS = [2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024];

/* ── STREAK CALCULATOR ─────────────────────────────────── */
const calcStreak = (log) => {
  if (!log || log.length === 0) return 0;
  const dailyMins = {};
  log.forEach((e) => {
    if (e.date) dailyMins[e.date] = (dailyMins[e.date] || 0) + e.minutes;
  });
  const valid = Object.keys(dailyMins)
    .filter((d) => dailyMins[d] >= 120)
    .sort()
    .reverse();
  if (!valid.length) return 0;
  const today = todayS(),
    yesterday = addDays(today, -1);
  let check = valid.includes(today)
    ? today
    : valid.includes(yesterday)
    ? yesterday
    : null;
  if (!check) return 0;
  let streak = 0;
  while (valid.includes(check)) {
    streak++;
    check = addDays(check, -1);
  }
  return streak;
};

/* ── FORMULA KEYBOARD WIDGET ────────────────────────────── */
const FormulaKeyboard = ({ visible, onToggle }) => {
  const lastFocusedRef = useRef(null);

  /* Track whichever input/textarea the user last typed in.
     This is the fallback when focus was already lost before
     the symbol button was clicked (e.g. on touch devices). */
  useEffect(() => {
    const track = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        lastFocusedRef.current = e.target;
      }
    };
    document.addEventListener("focusin", track, true);
    return () => document.removeEventListener("focusin", track, true);
  }, []);

  const insertSymbol = useCallback((symbol) => {
    // Prefer currently active element; fall back to last tracked field
    const active = document.activeElement;
    const el =
      active?.tagName === "INPUT" || active?.tagName === "TEXTAREA"
        ? active
        : lastFocusedRef.current;

    if (!el) {
      // Nothing focused at all — copy to clipboard as last resort
      try {
        navigator.clipboard.writeText(symbol);
      } catch (_) {}
      return;
    }

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    el.focus();

    if (typeof el.setRangeText === "function") {
      // setRangeText updates the value AND moves the caret to just after
      // the inserted text in one native, synchronous step. Once we fire
      // "input", React's value will match node.value on re-render, so it
      // won't reassign .value — meaning the caret position survives
      // instead of snapping back to position 0 on every click.
      el.setRangeText(symbol, start, end, "end");
      el.dispatchEvent(new Event("input", { bubbles: true }));
    } else {
      // Fallback for environments without setRangeText
      const proto =
        el.tagName === "INPUT"
          ? window.HTMLInputElement.prototype
          : window.HTMLTextAreaElement.prototype;
      const nativeSetter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
      if (nativeSetter) {
        nativeSetter.call(
          el,
          el.value.slice(0, start) + symbol + el.value.slice(end)
        );
        el.dispatchEvent(new Event("input", { bubbles: true }));
        setTimeout(() => {
          el.selectionStart = el.selectionEnd = start + symbol.length;
        }, 0);
      }
    }
  }, []);

  if (!visible) {
    return (
      <button
        onClick={onToggle}
        title="Open Formula Keyboard (Σ)"
        style={{
          position: "fixed",
          bottom: 20,
          left: 20,
          zIndex: 1100,
          ...BS(G.amber),
          padding: "10px 16px",
          fontSize: 19,
          borderRadius: 14,
          boxShadow: `0 8px 28px rgba(0,0,0,0.55), 0 0 20px ${G.amber}28`,
          backdropFilter: "blur(16px)",
        }}
      >
        Σ
      </button>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        left: 20,
        zIndex: 1100,
        background: "rgba(8,14,30,0.97)",
        border: `1px solid ${G.amber}44`,
        borderRadius: 18,
        padding: 14,
        boxShadow: `0 16px 52px rgba(0,0,0,0.72), 0 0 36px ${G.amber}18`,
        backdropFilter: "blur(28px) saturate(200%)",
        width: 298,
      }}
      /* ← THE KEY FIX: preventDefault on mousedown stops the browser from
           moving focus away from the active input when any part of this
           panel is clicked. onClick events still fire normally.          */
      onMouseDown={(e) => e.preventDefault()}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg,transparent,${G.amber}aa,transparent)`,
          borderRadius: "18px 18px 0 0",
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 800,
            color: G.amber,
            letterSpacing: "0.04em",
          }}
        >
          Σ FORMULA KEYBOARD
        </span>
        <button
          onClick={onToggle}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: `1px solid ${G.glassBorder}`,
            borderRadius: 7,
            color: G.textMuted,
            cursor: "pointer",
            fontSize: 14,
            padding: "2px 9px",
            fontFamily: "inherit",
          }}
        >
          −
        </button>
      </div>
      <div style={{ fontSize: 10, color: G.textDim, marginBottom: 9 }}>
        Click a symbol to insert it at your cursor — works in any field
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(8,1fr)",
          gap: 3,
        }}
      >
        {FORMULA_SYMBOLS.map(({ s, d }) => (
          <button
            key={s}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertSymbol(s)}
            title={d}
            style={{
              background: `${G.amber}14`,
              border: `1px solid ${G.amber}2a`,
              borderRadius: 7,
              color: G.amber,
              cursor: "pointer",
              fontSize: 13,
              padding: "7px 2px",
              fontFamily: "serif",
              lineHeight: 1,
              transition: "all 0.12s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = `${G.amber}30`;
              e.currentTarget.style.borderColor = `${G.amber}77`;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = `${G.amber}14`;
              e.currentTarget.style.borderColor = `${G.amber}2a`;
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
};
/* ── LIQUID GLASS CARD ─────────────────────────────────── */
const GlassCard = ({
  children,
  style = {},
  accent = null,
  glow = false,
  onClick = null,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={onClick ? "glass-card-clickable" : ""}
      style={{
        background: G.glass,
        border: `1px solid ${
          hovered && onClick
            ? accent
              ? accent + "88"
              : G.glassBorderBright
            : accent
            ? accent + "55"
            : G.glassBorder
        }`,
        borderRadius: 18,
        backdropFilter: "blur(28px) saturate(200%)",
        WebkitBackdropFilter: "blur(28px) saturate(200%)",
        boxShadow:
          glow && accent
            ? `0 8px 36px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.12), 0 0 40px ${accent}2a`
            : hovered && onClick
            ? "0 12px 44px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.15)"
            : "0 8px 36px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)",
        position: "relative",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transform: hovered && onClick ? "translateY(-2px)" : "translateY(0)",
        transition:
          "transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
        ...style,
      }}
    >
      {accent && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg,transparent,${accent}aa,transparent)`,
            pointerEvents: "none",
          }}
        />
      )}
      {children}
    </div>
  );
};
/* ── INPUT / BUTTON STYLES ─────────────────────────────── */
const IS = {
  background: "rgba(10,18,38,0.92)",
  border: `1px solid ${G.glassBorder}`,
  borderRadius: 10,
  padding: "9px 13px",
  color: G.textPrimary,
  fontSize: 13,
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  fontFamily: "inherit",
  backdropFilter: "blur(12px)",
  transition: "border-color 0.2s, box-shadow 0.2s",
};
const BS = (clr, filled = false) => ({
  padding: "7px 17px",
  background: filled ? `linear-gradient(135deg,${clr},${clr}cc)` : `${clr}22`,
  border: `1px solid ${clr}77`,
  borderRadius: 10,
  color: filled ? "#000" : clr,
  cursor: "pointer",
  fontSize: 12.5,
  fontWeight: 700,
  letterSpacing: "0.02em",
  transition: "all 0.18s ease",
  fontFamily: "inherit",
  backdropFilter: "blur(10px)",
  boxShadow: filled ? G.glow(clr, 0.45) : "none",
});

/* ── UTILITIES ─────────────────────────────────────────── */
const subjPct = (s) => {
  if (!s.topics.length) return 0;
  const d = s.topics.reduce(
    (a, t) => a + TRACK_FIELDS.filter((f) => t[f]).length,
    0
  );
  return Math.round((d / (s.topics.length * TRACK_FIELDS.length)) * 100);
};
const overallPct = (ss) =>
  ss.length
    ? Math.round(ss.reduce((a, s) => a + subjPct(s), 0) / ss.length)
    : 0;
const pctColor = (p) =>
  p >= 80 ? "#4eeaaa" : p >= 60 ? "#ffe666" : p >= 30 ? "#ff9f6b" : "#ff5577";
const topicStatus = (t) => {
  const d = TRACK_FIELDS.filter((f) => t[f]).length;
  if (d === 0) return { label: "Not Started", color: G.textDim };
  if (d === TRACK_FIELDS.length)
    return { label: "Complete ✓", color: "#4eeaaa" };
  if (t.r1 || t.r2 || t.r3) return { label: "Revising", color: "#d580ff" };
  if (t.pyq || t.asgn) return { label: "Practicing", color: "#ffe666" };
  return { label: "Learning", color: "#5ecfff" };
};

/* ── ACHIEVEMENTS ─────────────────────────────────────── */
const ACHIEVEMENTS = [
  {
    id: "first_lec",
    icon: "🎓",
    title: "First Step",
    desc: "Complete your first lecture",
    check: (d) => d.subjects.some((s) => s.topics.some((t) => t.lec)),
  },
  {
    id: "week_warrior",
    icon: "🔥",
    title: "Week Warrior",
    desc: "Study 7 days in a row (≥2hrs)",
    check: (d) => d.streak >= 7,
  },
  {
    id: "fortnight",
    icon: "⚡",
    title: "Fortnight Focus",
    desc: "14 day streak (≥2hrs/day)",
    check: (d) => d.streak >= 14,
  },
  {
    id: "error_crusher",
    icon: "🐛",
    title: "Error Crusher",
    desc: "Resolve 10 errors",
    check: (d) => d.errors.filter((e) => e.resolved).length >= 10,
  },
  {
    id: "mock_beast",
    icon: "🏆",
    title: "Mock Beast",
    desc: "Score 85%+ on a mock test",
    check: (d) =>
      d.tests.some((t) => Math.round((t.score / t.total) * 100) >= 85),
  },
  {
    id: "half_done",
    icon: "🌗",
    title: "Halfway There",
    desc: "Reach 50% overall progress",
    check: (d) => overallPct(d.subjects) >= 50,
  },
  {
    id: "pyq_master",
    icon: "📚",
    title: "PYQ Master",
    desc: "Complete PYQs for 20 topics",
    check: (d) =>
      d.subjects.reduce(
        (a, s) => a + s.topics.filter((t) => t.pyq).length,
        0
      ) >= 20,
  },
  {
    id: "notes_guru",
    icon: "📝",
    title: "Notes Guru",
    desc: "Add 25 quick notes",
    check: (d) => d.qnotes.length >= 25,
  },
  {
    id: "rev_king",
    icon: "🔁",
    title: "Revision King",
    desc: "Complete 30 Revisions",
    check: (d) =>
      d.subjects.reduce((a, s) => a + s.topics.filter((t) => t.r1).length, 0) >=
      30,
  },
  {
    id: "subject_ace",
    icon: "🎯",
    title: "Subject Ace",
    desc: "100% in any subject",
    check: (d) => d.subjects.some((s) => subjPct(s) === 100),
  },
  {
    id: "gate_ready",
    icon: "🚀",
    title: "GATE Ready",
    desc: "Reach 80% overall progress",
    check: (d) => overallPct(d.subjects) >= 80,
  },
  {
    id: "flashcard_hero",
    icon: "🃏",
    title: "Flashcard Hero",
    desc: "Get 20 flashcards to a 7+ day interval",
    check: (d) =>
      (d.flashcards || []).filter((f) => f.interval >= 7).length >= 20,
  },
  {
    id: "daily_planner",
    icon: "📋",
    title: "Daily Planner",
    desc: "Create 10 custom daily tasks",
    check: (d) => (d.dailyPlan || []).length >= 10,
  },
  {
    id: "habit_builder",
    icon: "🗓",
    title: "Habit Builder",
    desc: "Log study time on 14 different days",
    check: (d) => new Set((d.pomodoroLog || []).map((l) => l.date)).size >= 14,
  },
];
const checkAchievements = (data, earned) =>
  ACHIEVEMENTS.filter((a) => !earned.includes(a.id) && a.check(data)).map(
    (a) => a.id
  );

/* ── READINESS SCORE ─────────────────────────────────── */
const calcReadiness = (subjects, tests, errors, streak) => {
  const overall = overallPct(subjects);
  const testAvg = tests.length
    ? tests.reduce((a, t) => a + Math.round((t.score / t.total) * 100), 0) /
      tests.length
    : 0;
  const errRate = errors.length
    ? (errors.filter((e) => e.resolved).length / errors.length) * 100
    : 50;
  const streakBonus = Math.min(streak * 2, 20);
  return Math.round(
    Math.min(
      100,
      overall * 0.45 + testAvg * 0.35 + errRate * 0.1 + streakBonus * 0.1
    )
  );
};

/* ── GATE SCORE PREDICTOR ───────────────────────────────── */
const predictGATEScore = (readiness, testAvg, subjects) => {
  const baseScore = readiness * 0.75 + testAvg * 0.25;
  const gateScore = 20 + (baseScore / 100) * 60;
  const low = Math.max(15, Math.round(gateScore - 8));
  const high = Math.min(85, Math.round(gateScore + 8));
  const rank =
    high >= 70
      ? "<200"
      : high >= 60
      ? "200–800"
      : high >= 50
      ? "800–2500"
      : high >= 40
      ? "2500–6000"
      : ">6000";
  return { low, high, rank, score: Math.round(gateScore) };
};

/* ── DAILY TARGET ENGINE ────────────────────────────────── */
/* ── DATE UTILITY ───────────────────────────────────────── */
const getDaysLeft = (dateStr) => {
  if (!dateStr) return 0;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return 0;
  const target = new Date(parts[0], parts[1] - 1, parts[2]);
  target.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((target - now) / 86400000));
};

/* ── DAILY TARGET ENGINE ────────────────────────────────── */
const calcDailyTarget = (subjects, examDateStr) => {
  const daysLeft = getDaysLeft(examDateStr || "2027-02-01");
  const totalTopics = subjects.reduce((a, s) => a + s.topics.length, 0);
  const completedTopics = subjects.reduce(
    (a, s) => a + s.topics.filter((t) => t.lec).length,
    0
  );
  const remainingTopics = totalTopics - completedTopics;
  const pct = totalTopics
    ? Math.round((completedTopics / totalTopics) * 100)
    : 0;
  const studiedDates = subjects.flatMap((s) =>
    s.topics.filter((t) => t.studiedDate).map((t) => t.studiedDate)
  );
  const last30 = studiedDates.filter((d) => daysDiff(d) >= -30).length;
  const pacePerDay = last30 / 30;
  const daysAtPace =
    pacePerDay > 0 ? Math.round(remainingTopics / pacePerDay) : 9999;
  const neededPerDay =
    daysLeft > 0 ? (remainingTopics / daysLeft).toFixed(2) : "0.00";
  const onTrack =
    daysLeft <= 0
      ? remainingTopics === 0
      : parseFloat(pacePerDay) >= parseFloat(neededPerDay);
  return {
    daysLeft,
    totalTopics,
    completedTopics,
    remainingTopics,
    pct,
    pacePerDay: pacePerDay.toFixed(2),
    daysAtPace,
    neededPerDay,
    onTrack,
  };
};

/* ── SMART RECOMMENDATIONS ─────────────────────────────── */
const getRecommendations = (subjects) => {
  const recs = [];
  subjects.forEach((s) =>
    s.topics.forEach((t) => {
      const done = TRACK_FIELDS.filter((f) => t[f]).length;
      if (done < TRACK_FIELDS.length) {
        const next = !t.lec
          ? "Watch Lecture"
          : !t.asgn
          ? "Do Assignment"
          : !t.pyq
          ? "Solve PYQs"
          : !t.r1
          ? "Revision 1 (7d)"
          : !t.r2
          ? "Revision 2 (14d)"
          : !t.r3
          ? "Revision 3 (30d)"
          : "Take Test";
        const diff = TOPIC_DIFFICULTY[t.name] || 3;
        const roi =
          (WEIGHTAGE[s.name] || 10) *
          (1 - (t.confidence || 0) / 5) *
          ((TRACK_FIELDS.length - done) / TRACK_FIELDS.length) *
          (diff / 3);
        const freq = PYQ_FREQ[t.name]
          ? PYQ_FREQ[t.name].reduce((a, b) => a + b, 0)
          : 0;
        recs.push({
          subject: s.name,
          topic: t.name,
          confidence: t.confidence || 0,
          weightage: WEIGHTAGE[s.name] || 10,
          next,
          color: s.clr,
          roi,
          done,
          difficulty: diff,
          pyqFreq: freq,
        });
      }
    })
  );
  return recs.sort((a, b) => b.roi - a.roi).slice(0, 6);
};

/* ── SHARED COMPONENTS ─────────────────────────────────── */
const Bar = ({ pct, color = G.violet, h = 5 }) => (
  <div
    style={{
      background: "rgba(255,255,255,0.055)",
      borderRadius: 99,
      height: h,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        width: `${Math.min(pct, 100)}%`,
        background: color,
        height: "100%",
        transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
        borderRadius: 99,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg,transparent,rgba(255,255,255,0.22),transparent)",
          animation: "shimmer 2.4s ease-in-out infinite",
          borderRadius: 99,
        }}
      />
    </div>
  </div>
);

const CB = ({ checked, onChange, color = G.violet, size = 16 }) => (
  <div
    onClick={() => {
      vibrate(40);
      onChange();
    }}
    style={{
      width: size,
      height: size,
      borderRadius: 5,
      border: `2px solid ${checked ? color : G.glassBorder}`,
      background: checked ? color : "rgba(10,18,42,0.8)",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      transition:
        "background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
      boxShadow: checked ? `0 0 10px ${color}66` : "none",
    }}
  >
    {checked && (
      <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
        <path
          d="M1 3.5L3.5 6L8 1"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="14"
          strokeDashoffset="0"
          style={{ animation: "drawCheck 0.22s ease forwards" }}
        />
      </svg>
    )}
  </div>
);

const Tag = ({ text, color, squared = false, style }) => (
  <span
    style={{
      background: `${color}1e`,
      color,
      fontSize: 10.5,
      padding: "2px 8px",
      borderRadius: squared ? 5 : 99,
      fontWeight: 700,
      letterSpacing: "0.04em",
      whiteSpace: "nowrap",
      border: `1px solid ${color}3a`,
      backdropFilter: "blur(4px)",
      ...style,
    }}
  >
    {text}
  </span>
);

const Stars = ({ value, onChange, size = 12 }) => (
  <div style={{ display: "flex", gap: 1 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span
        key={i}
        onClick={() => onChange && onChange(i === value ? 0 : i)}
        style={{
          fontSize: size,
          color: i <= value ? "#ffe666" : G.textDim,
          cursor: onChange ? "pointer" : "default",
          lineHeight: 1,
          textShadow: i <= value ? "0 0 6px #ffe66688" : "none",
          transition: "all 0.15s",
        }}
      >
        ★
      </span>
    ))}
  </div>
);
const Spark = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  const w = 60,
    h = 20;
  const max = Math.max(...data, 1);
  const pts = data
    .map(
      (v, i) =>
        `${(i / (data.length - 1)) * w},${h - ((v / max) * h * 0.82 + 2)}`
    )
    .join(" ");
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ display: "block" }}
    >
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.75"
      />
    </svg>
  );
};
const DiffStars = ({ value }) => (
  <div style={{ display: "flex", gap: 1 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span
        key={i}
        style={{
          fontSize: 9,
          color: i <= value ? "#ff7090" : G.textDim,
          lineHeight: 1,
        }}
      >
        ◆
      </span>
    ))}
  </div>
);

/* Modal — zIndex raised ABOVE the floating Pomodoro timer (400)
   and Toasts (500) so dialogs can never be visually overlapped
   by those fixed-position widgets, in any orientation/viewport. */
const Modal = ({ title, onClose, children, wide = false, accent = null }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(2,4,12,0.88)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(16px)",
      padding: 16,
      boxSizing: "border-box",
    }}
    onClick={onClose}
  >
    <div
      style={{
        background: "rgba(10,16,34,0.96)",
        border: `1px solid ${accent ? accent + "66" : G.glassBorder}`,
        borderRadius: 22,
        padding: 26,
        width: "100%", // CHANGED: ensures responsiveness
        maxWidth: wide ? 720 : 460, // CHANGED: uses max-width instead of fixed width
        maxHeight: "85vh", // CHANGED: keeps it inside viewport
        overflowY: "auto",
        boxShadow: `0 36px 80px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.12)${
          accent ? `, 0 0 50px ${accent}22` : ""
        }`,
        backdropFilter: "blur(28px) saturate(200%)",
        position: "relative",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {accent && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg,transparent,${accent}aa,transparent)`,
            borderRadius: "22px 22px 0 0",
          }}
        />
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <span
          style={{
            fontWeight: 800,
            fontSize: 16,
            color: G.textPrimary,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </span>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.1)",
            border: `1px solid ${G.glassBorder}`,
            borderRadius: 9,
            color: G.textMuted,
            cursor: "pointer",
            fontSize: 16,
            lineHeight: 1,
            padding: "4px 9px",
            fontFamily: "inherit",
          }}
        >
          ×
        </button>
      </div>
      {children}
    </div>
  </div>
);

const Toast = ({ toasts }) => (
  <div
    style={{
      position: "fixed",
      top: 20,
      right: 20,
      zIndex: 500,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      pointerEvents: "none",
    }}
  >
    {toasts.map((t) => (
      <div
        key={t.id}
        style={{
          background: "rgba(10,16,34,0.97)",
          border: "none",
          borderLeft: `3px solid ${t.color || G.violet}`,
          borderRadius: "0 14px 14px 0",
          padding: "12px 18px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          backdropFilter: "blur(24px)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          animation: "fadeUp 0.28s ease",
          minWidth: 240,
          maxWidth: 320,
        }}
      >
        <span style={{ fontSize: 19 }}>{t.icon || "✨"}</span>
        <div>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: t.color || G.violet,
            }}
          >
            {t.title}
          </div>
          <div style={{ fontSize: 11, color: G.textSecondary, marginTop: 2 }}>
            {t.message}
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ── POMODORO TIMER ─────────────────────────────────────────
   Rewritten to be background/lock-safe: instead of decrementing
   a counter every setInterval tick (which iOS/iPadOS throttles
   or fully suspends once the screen locks or the tab is hidden),
   we compute remaining time from an absolute end-timestamp using
   Date.now(). On visibilitychange/focus we immediately re-sync,
   and the whole timer state is persisted to localStorage so even
   a full reload (which can happen after a long lock) resumes
   correctly — including crediting a session that finished while
   the device was locked.  
   const [showTimeUpModal, setShowTimeUpModal] = useState(false);                                     */
const POMO_MODES = {
  pomo: { label: "Pomodoro", mins: 25, color: G.pink },
  deep: { label: "Deep Work", mins: 50, color: G.violet },
  short: { label: "5m Break", mins: 5, color: G.emerald },
  long: { label: "15m Break", mins: 15, color: G.cyan },
};

const PomodoroTimer = ({
  subjects,
  pomodoroLog,
  onSessionComplete,
  addToast,
}) => {
  const [mode, setMode] = useState("pomo");
  const [time, setTime] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [selSubj, setSelSubj] = useState("Engineering Mathematics");
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);

  const modeRef = useRef(mode);
  modeRef.current = mode;
  const selSubjRef = useRef(selSubj);
  selSubjRef.current = selSubj;
  const endTimeRef = useRef(null);
  const audioCtxRef = useRef(null);

  // iOS Safari blocks audio unless the AudioContext is created/resumed
  // inside a real tap. We call this from the Start/Pause button so the
  // context is "unlocked" and ready by the time the alarm needs to fire.
  const ensureAudioContext = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      if (audioCtxRef.current.state === "suspended") {
        audioCtxRef.current.resume().catch(() => {});
      }
      return audioCtxRef.current;
    } catch (e) {
      return null;
    }
  };

  // 3-beep alarm via oscillator — no audio files/CDN needed.
  const playAlarmSound = () => {
    const ctx = ensureAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;
    const beep = (start, freq, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.35, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + duration + 0.02);
    };
    beep(now, 880, 0.22);
    beep(now + 0.32, 880, 0.22);
    beep(now + 0.64, 1175, 0.35);
  };
  const ensureNotificationPermission = () => {
    try {
      if (!("Notification" in window)) return;
      if (Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    } catch (e) {}
  };

  const notifyTimerDone = (m) => {
    try {
      if (!("Notification" in window)) return;
      if (Notification.permission !== "granted") return;
      new Notification("⏰ Time is Up!", {
        body: `Your ${m.label} session is complete.`,
        tag: "gate-pomo",
      });
    } catch (e) {}
  };
  // Today's session count + minutes, derived from the log so it
  // naturally resets at midnight instead of accumulating forever.
  const todayLog = (pomodoroLog || []).filter((l) => l.date === todayS());
  const todaySessions = todayLog.length;
  const todayMinutes = todayLog.reduce((a, l) => a + l.minutes, 0);

  /* Restore local storage state (mode/time/running/selSubj) */
  useEffect(() => {
    try {
      const raw = localStorage.getItem("gate_pomo_state");
      if (!raw) return;
      const st = JSON.parse(raw);
      if (st.mode) setMode(st.mode);
      if (st.selSubj) setSelSubj(st.selSubj);
      if (st.running && st.endTime) {
        const remaining = Math.round((st.endTime - Date.now()) / 1000);
        if (remaining > 0) {
          endTimeRef.current = st.endTime;
          setTime(remaining);
          setRunning(true);
        } else {
          setTime(0);
          setRunning(true);
        }
      } else if (typeof st.time === "number") {
        setTime(st.time);
      }
    } catch (e) {}
  }, []);

  /* Timer completion — NOTE: we do NOT reset time here. The reset
     happens only when the user clicks "Acknowledge", so the elapsed
     display stays frozen at 00:00 while the modal is showing.
     This also prevents the ghost-timer bug where the countdown
     useEffect would re-anchor endTimeRef to a freshly-reset time. */
  useEffect(() => {
    if (time === 0 && running) {
      setRunning(false);
      endTimeRef.current = null;
      const m = POMO_MODES[modeRef.current];

      if (modeRef.current !== "short" && modeRef.current !== "long") {
        onSessionComplete && onSessionComplete(selSubjRef.current, m.mins);
      } else {
        addToast &&
          addToast({
            title: "Break over!",
            message: "Time to get back to studying 💪",
            icon: "⏰",
            color: G.emerald,
          });
      }

      setShowTimeUpModal(true);
      vibrate([200, 100, 200, 100, 200]);
      playAlarmSound();
      notifyTimerDone(m);
      // time stays at 0 until user dismisses modal ↓
    }
  }, [time, running, onSessionComplete, addToast]);

  /* Persist on every change so a lock/reload can resume correctly */
  useEffect(() => {
    try {
      localStorage.setItem(
        "gate_pomo_state",
        JSON.stringify({
          mode,
          time,
          running,
          selSubj,
          endTime: endTimeRef.current,
        })
      );
    } catch (e) {}
  }, [mode, time, running, selSubj]);

  /* Timestamp-based countdown — immune to background throttling */
  useEffect(() => {
    if (!running) return;
    if (!endTimeRef.current) endTimeRef.current = Date.now() + time * 1000;
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.round((endTimeRef.current - Date.now()) / 1000)
      );
      setTime(remaining);
    };
    tick();
    const iv = setInterval(tick, 1000);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", tick);
      endTimeRef.current = null;
    };
  }, [running]);

  const switchMode = (m) => {
    setMode(m);
    setTime(POMO_MODES[m].mins * 60);
    setRunning(false);
    endTimeRef.current = null;
  };

  const resetTimer = () => {
    setTime(POMO_MODES[mode].mins * 60);
    setRunning(false);
    endTimeRef.current = null;
  };

  const mm = String(Math.floor(time / 60)).padStart(2, "0");
  const ss = String(time % 60).padStart(2, "0");
  const m = POMO_MODES[mode];
  const r = 30,
    circ = 2 * Math.PI * r,
    progress = 1 - time / (m.mins * 60);

  if (minimized)
    return (
      <div
        className="pomo-widget pomo-widget-min"
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 400,
          background: "rgba(8,14,30,0.96)",
          border: `1px solid ${m.color}55`,
          borderRadius: 40,
          padding: "8px 16px",
          boxShadow: `0 8px 32px rgba(0,0,0,0.65), 0 0 20px ${m.color}22`,
          backdropFilter: "blur(24px)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: "pointer",
          userSelect: "none",
          boxSizing: "border-box",
        }}
        onClick={() => setMinimized(false)}
      >
        <div style={{ position: "relative", width: 28, height: 28 }}>
          <svg
            width={28}
            height={28}
            viewBox="0 0 28 28"
            style={{ position: "absolute", top: 0, left: 0 }}
          >
            <circle
              cx={14}
              cy={14}
              r={11}
              fill="none"
              stroke={`${m.color}22`}
              strokeWidth={4}
            />
            <circle
              cx={14}
              cy={14}
              r={11}
              fill="none"
              stroke={m.color}
              strokeWidth={4}
              strokeDasharray={2 * Math.PI * 11}
              strokeDashoffset={2 * Math.PI * 11 * (1 - progress)}
              strokeLinecap="round"
              transform="rotate(-90 14 14)"
            />
          </svg>
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: running ? m.color : G.textDim,
                boxShadow: running ? G.glow(m.color, 0.9) : "none",
              }}
            />
          </div>
        </div>
        <span
          style={{
            fontSize: 15,
            color: m.color,
            fontWeight: 900,
            fontFamily: "monospace",
            letterSpacing: 1,
          }}
        >
          {mm}:{ss}
        </span>
        <span style={{ fontSize: 11, color: G.textMuted }}>
          🍅 {todaySessions}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMinimized(false);
          }}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: `1px solid ${G.glassBorder}`,
            borderRadius: 7,
            color: G.textMuted,
            cursor: "pointer",
            fontSize: 11,
            padding: "2px 7px",
            fontFamily: "inherit",
          }}
        >
          ↗
        </button>
      </div>
    );

  return (
    <div
      className="pomo-widget"
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 400,
        background: "rgba(8,14,30,0.97)",
        border: `1px solid ${m.color}44`,
        borderRadius: 22,
        padding: 18,
        boxShadow: `0 16px 56px rgba(0,0,0,0.7), 0 0 40px ${m.color}18, inset 0 1px 0 rgba(255,255,255,0.1)`,
        backdropFilter: "blur(28px) saturate(200%)",
        width: 260,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg,transparent,${m.color}aa,transparent)`,
          borderRadius: "22px 22px 0 0",
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: m.color,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Focus Timer
        </span>
        <button
          onClick={() => setMinimized(true)}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: `1px solid ${G.glassBorder}`,
            borderRadius: 7,
            color: G.textMuted,
            cursor: "pointer",
            fontSize: 13,
            padding: "2px 7px",
            fontFamily: "inherit",
            lineHeight: 1,
          }}
        >
          −
        </button>
      </div>

      <div
        style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" }}
      >
        {Object.entries(POMO_MODES).map(([k, v]) => (
          <button
            key={k}
            onClick={() => switchMode(k)}
            style={{
              flex: 1,
              minWidth: 48,
              padding: "4px 2px",
              background: mode === k ? `${v.color}28` : "transparent",
              border: `1px solid ${
                mode === k ? v.color + "88" : G.glassBorder
              }`,
              borderRadius: 8,
              color: mode === k ? v.color : G.textMuted,
              fontSize: 9.5,
              cursor: "pointer",
              fontFamily: "inherit",
              fontWeight: mode === k ? 800 : 400,
            }}
          >
            {v.label}
          </button>
        ))}
      </div>
      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}
      >
        <svg width={80} height={80} viewBox="0 0 80 80">
          <circle
            cx={40}
            cy={40}
            r={r}
            fill="none"
            stroke={`${m.color}1a`}
            strokeWidth={7}
          />
          <circle
            cx={40}
            cy={40}
            r={r}
            fill="none"
            stroke={m.color}
            strokeWidth={7}
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - progress)}
            strokeLinecap="round"
            transform="rotate(-90 40 40)"
            style={{
              transition: "stroke-dashoffset 1s linear",
              filter: `drop-shadow(0 0 6px ${m.color}88)`,
            }}
          />
          <text
            x={40}
            y={44}
            textAnchor="middle"
            fontSize={14}
            fontWeight="900"
            fill={m.color}
            fontFamily="monospace"
          >
            {mm}:{ss}
          </text>
        </svg>
      </div>
      <div style={{ marginBottom: 10 }}>
        <select
          value={selSubj}
          onChange={(e) => setSelSubj(e.target.value)}
          style={{
            ...IS,
            fontSize: 11,
            padding: "5px 8px",
            color: G.textSecondary,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {subjects.map((s) => (
            <option key={s.id} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div
        style={{
          display: "flex",
          gap: 6,
          justifyContent: "center",
          marginBottom: 10,
        }}
      >
        <button
          onClick={() => {
            ensureAudioContext();
            ensureNotificationPermission();
            setRunning((r) => !r);
          }}
          style={{ ...BS(m.color, running), padding: "7px 20px", fontSize: 12 }}
        >
          {running ? "⏸ Pause" : "▶ Start"}
        </button>
        <button
          onClick={resetTimer}
          style={{ ...BS(G.textMuted), padding: "7px 12px" }}
        >
          ↺
        </button>
      </div>
      <div style={{ textAlign: "center", fontSize: 10.5, color: G.textMuted }}>
        🍅 {todaySessions} session{todaySessions !== 1 ? "s" : ""} ·{" "}
        {Math.floor(todayMinutes / 60)}h {todayMinutes % 60}m studied today
      </div>
      <div
        style={{
          textAlign: "center",
          fontSize: 9,
          color: G.textDim,
          marginTop: 4,
        }}
      >
        ⏱ keeps time even if the screen locks
      </div>

      {showTimeUpModal && (
        <Modal
          title="⏰ Time is Up!"
          onClose={() => setShowTimeUpModal(false)}
          accent={m.color}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>
              {m.icon || "⏳"}
            </div>
            <div
              style={{
                fontSize: 18,
                color: G.textPrimary,
                fontWeight: "bold",
                marginBottom: 8,
              }}
            >
              Your {m.label} session is complete!
            </div>
            <div style={{ fontSize: 13, color: G.textMuted, marginBottom: 20 }}>
              Great job staying focused.
            </div>
            <button
              onClick={() => {
                setShowTimeUpModal(false);
                setTime(POMO_MODES[modeRef.current].mins * 60);
              }}
              style={{
                ...BS(m.color, true),
                width: "100%",
                padding: "12px",
                fontSize: 14,
              }}
            >
              Acknowledge
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};
/* ── GATE COUNTDOWN ─────────────────────────────────────── */
/* ── GATE COUNTDOWN ─────────────────────────────────────── */
const GATECountdown = ({ examDateStr }) => {
  const safeDate = examDateStr || "2027-02-01";
  const [tl, setTl] = useState({});
  useEffect(() => {
    const calc = () => {
      const parts = safeDate.split("-");
      if (parts.length !== 3) return;
      const gateDate = new Date(parts[0], parts[1] - 1, parts[2]);
      gateDate.setHours(9, 0, 0, 0); // 9:00 AM local time
      const diff = gateDate - new Date();
      if (diff <= 0) {
        setTl({ done: true });
        return;
      }
      setTl({
        days: Math.floor(diff / 86400000),
        hrs: Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    const iv = setInterval(calc, 1000);
    return () => clearInterval(iv);
  }, [safeDate]);
  if (tl.done)
    return (
      <div style={{ textAlign: "center", color: G.emerald, fontWeight: 700 }}>
        🎉 GATE Day!
      </div>
    );
  const urgency =
    tl.days < 30 ? "#ff5577" : tl.days < 90 ? "#ff9f6b" : "#ffe666";

  const displayDate = new Date(
    safeDate.split("-")[0],
    safeDate.split("-")[1] - 1,
    safeDate.split("-")[2]
  );

  return (
    <GlassCard accent={urgency} style={{ padding: "16px 20px" }}>
      <div
        style={{
          fontSize: 10.5,
          color: G.textMuted,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: 12,
        }}
      >
        ⏳ Target Exam —{" "}
        {displayDate.toLocaleDateString("en-IN", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 8,
          textAlign: "center",
        }}
      >
        {[
          ["DAYS", tl.days],
          ["HRS", tl.hrs],
          ["MINS", tl.mins],
          ["SECS", tl.secs],
        ].map(([l, v]) => (
          <div
            key={l}
            style={{
              background: "rgba(8,14,32,0.85)",
              borderRadius: 12,
              padding: "10px 4px",
              border: `1px solid ${urgency}2a`,
            }}
          >
            <div
              style={{
                fontSize: 26,
                fontWeight: 900,
                color: urgency,
                fontFamily: "monospace",
                letterSpacing: "-0.02em",
                textShadow: `0 0 20px ${urgency}66`,
              }}
            >
              {String(v || 0).padStart(2, "0")}
            </div>
            <div
              style={{
                fontSize: 9,
                color: G.textSecondary,
                fontWeight: 700,
                letterSpacing: "0.06em",
                marginTop: 2,
              }}
            >
              {l}
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  );
};
/* ── RADAR CHART ─────────────────────────────────────────── */
const RadarChart = ({ subjects }) => {
  const W = 290,
    H = 270,
    CX = 145,
    CY = 135,
    R = 105,
    n = subjects.length;
  const points = subjects.map((s, i) => {
    const angle = (i / n) * 2 * Math.PI - Math.PI / 2,
      pct = subjPct(s) / 100;
    return {
      x: CX + R * pct * Math.cos(angle),
      y: CY + R * pct * Math.sin(angle),
      lx: CX + (R + 24) * Math.cos(angle),
      ly: CY + (R + 24) * Math.sin(angle),
      name: s.name.split(" ").slice(-1)[0],
      pct: subjPct(s),
      clr: s.clr,
    };
  });
  const poly = points.map((p) => `${p.x},${p.y}`).join(" ");
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{
          width: "100%",
          maxWidth: 280,
          height: "auto",
          margin: "0 auto",
        }}
      >
        <defs>
          <radialGradient id="rg" cx="50%" cy="50%">
            <stop offset="0%" stopColor={G.violet} stopOpacity="0.35" />
            <stop offset="100%" stopColor={G.violet} stopOpacity="0.05" />
          </radialGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((rv) => {
          const rpts = subjects
            .map((_, i) => {
              const a = (i / n) * 2 * Math.PI - Math.PI / 2;
              return `${CX + R * rv * Math.cos(a)},${
                CY + R * rv * Math.sin(a)
              }`;
            })
            .join(" ");
          return (
            <polygon
              key={rv}
              points={rpts}
              fill="none"
              stroke={
                rv === 1 ? "rgba(120,160,255,0.35)" : "rgba(120,160,255,0.15)"
              }
              strokeWidth={rv === 1 ? "1.5" : "1"}
            />
          );
        })}
        {subjects.map((_, i) => {
          const a = (i / n) * 2 * Math.PI - Math.PI / 2;
          return (
            <line
              key={i}
              x1={CX}
              y1={CY}
              x2={CX + R * Math.cos(a)}
              y2={CY + R * Math.sin(a)}
              stroke="rgba(120,160,255,0.2)"
              strokeWidth="1"
            />
          );
        })}
        <polygon
          points={poly}
          fill="url(#rg)"
          stroke={G.violet}
          strokeWidth="2.5"
          strokeLinejoin="round"
          opacity="0.9"
        />
        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r={5}
              fill={p.clr}
              filter={`drop-shadow(0 0 4px ${p.clr})`}
            />
            <text
              x={p.lx}
              y={p.ly + 4}
              textAnchor="middle"
              fontSize="9.5"
              fill={p.clr}
              fontWeight="800"
            >
              {p.name}
            </text>
            <text
              x={p.lx}
              y={p.ly + 15}
              textAnchor="middle"
              fontSize="8.5"
              fill={G.textMuted}
            >
              {p.pct}%
            </text>
          </g>
        ))}
        <circle cx={CX} cy={CY} r="4" fill={G.violet} />
      </svg>
    </div>
  );
};

/* ── SVG TEST CHART WITH ROLLING 30d ─────────────────────── */
const SVGTestChart = ({ tests }) => {
  const [view, setView] = useState("all");
  if (tests.length < 2)
    return (
      <div
        style={{
          fontSize: 12.5,
          color: G.textDim,
          padding: "20px 0",
          textAlign: "center",
        }}
      >
        Add 2+ tests to see performance graph
      </div>
    );
  const filtered =
    view === "30d"
      ? (() => {
          const cutoff = addDays(todayS(), -30);
          return tests.filter((t) => !t.date || t.date >= cutoff);
        })()
      : tests;
  if (filtered.length < 2)
    return (
      <div
        style={{
          fontSize: 12.5,
          color: G.textDim,
          padding: "20px 0",
          textAlign: "center",
        }}
      >
        Not enough tests in last 30 days
      </div>
    );
  const W = 540,
    H = 190,
    PX = 44,
    PY = 18,
    IW = W - PX * 2,
    IH = H - PY * 2 - 26;
  const scores = filtered.map((t) => Math.round((t.score / t.total) * 100));
  const pts = scores.map((s, i) => ({
    x: PX + (i / (scores.length - 1 || 1)) * IW,
    y: PY + IH - (s / 100) * IH,
    s,
  }));
  const ma = scores.map((_, i) => {
    const sl = scores.slice(Math.max(0, i - 4), i + 1);
    return sl.reduce((a, b) => a + b, 0) / sl.length;
  });
  const maP = ma.map((s, i) => ({
    x: PX + (i / (scores.length - 1 || 1)) * IW,
    y: PY + IH - (s / 100) * IH,
  }));
  const linePath = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
    .join(" ");
  const maPath = maP
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
    .join(" ");
  const areaPath = `${linePath} L${pts[pts.length - 1].x},${PY + IH} L${
    pts[0].x
  },${PY + IH} Z`;
  const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  const trend = scores.length >= 2 ? scores[scores.length - 1] - scores[0] : 0;
  return (
    <div>
      <div style={{ display: "flex", gap: 7, marginBottom: 11 }}>
        {["all", "30d"].map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              ...BS(G.violet, view === v),
              padding: "5px 14px",
              fontSize: 11,
            }}
          >
            {v === "all" ? "All Time" : "Last 30 Days"}
          </button>
        ))}
      </div>
      <div
        style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}
      >
        {[
          { l: "Average", v: `${avg}%`, c: G.violet },
          { l: "Best", v: `${Math.max(...scores)}%`, c: G.emerald },
          {
            l: "Latest",
            v: `${scores[scores.length - 1]}%`,
            c: pctColor(scores[scores.length - 1]),
          },
          {
            l: "Trend",
            v: `${trend >= 0 ? "+" : ""}${trend}%`,
            c: trend >= 0 ? G.emerald : "#ff5577",
          },
        ].map((s) => (
          <GlassCard key={s.l} style={{ padding: "8px 14px" }} accent={s.c}>
            <div
              style={{
                fontSize: 9.5,
                color: G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {s.l}
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 900,
                color: s.c,
                textShadow: `0 0 15px ${s.c}66`,
              }}
            >
              {s.v}
            </div>
          </GlassCard>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
        <defs>
          <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={G.violet} stopOpacity="0.35" />
            <stop offset="100%" stopColor={G.violet} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0, 25, 50, 75, 100].map((y) => {
          const cy = PY + IH - (y / 100) * IH;
          return (
            <g key={y}>
              <line
                x1={PX}
                y1={cy}
                x2={PX + IW}
                y2={cy}
                stroke="rgba(120,160,255,0.2)"
                strokeWidth="1"
              />
              <text
                x={PX - 7}
                y={cy + 4}
                textAnchor="end"
                fontSize="9"
                fill={G.textDim}
              >
                {y}
              </text>
            </g>
          );
        })}
        <path d={areaPath} fill="url(#ag)" />
        {scores.length >= 3 && (
          <path
            d={maPath}
            fill="none"
            stroke={G.amber}
            strokeWidth="1.5"
            strokeDasharray="5 3"
            opacity="0.7"
          />
        )}
        <path
          d={linePath}
          fill="none"
          stroke={G.violet}
          strokeWidth="2.5"
          strokeLinejoin="round"
          style={{ filter: `drop-shadow(0 0 5px ${G.violet}77)` }}
        />
        {pts.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r="5.5"
              fill={pctColor(p.s)}
              style={{ filter: `drop-shadow(0 0 5px ${pctColor(p.s)})` }}
            />
            <text
              x={p.x}
              y={p.y - 10}
              textAnchor="middle"
              fontSize="9"
              fill={pctColor(p.s)}
              fontWeight="bold"
            >
              {p.s}%
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

/* ── GANTT TIMELINE ──────────────────────────────────────── */
/* ── GANTT TIMELINE ──────────────────────────────────────── */
const GanttTimeline = ({ subjects, examDateStr, pomodoroLog = [] }) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const [gy, gm, gd] = (examDateStr || "2027-02-01").split("-");
  const gate = new Date(+gy, +gm - 1, +gd);
  gate.setHours(0, 0, 0, 0);
  const totalDays = Math.ceil((gate - now) / 86400000);
  return (
    <div style={{ overflowX: "auto" }}>
      {subjects.map((s) => {
        const pct = subjPct(s);
        const remaining = 100 - pct;
        const subjectLog = pomodoroLog.filter((l) => l.subject === s.name);
        const timePerSubj = subjectLog.reduce((a, l) => a + l.minutes, 0);
        const uniqueStudyDays = new Set(subjectLog.map((l) => l.date)).size;
        const ratePerDay = uniqueStudyDays > 0 ? pct / uniqueStudyDays : 0;
        const daysNeeded =
          ratePerDay > 0
            ? Math.round(remaining / ratePerDay)
            : remaining > 0
            ? Math.round(totalDays * (remaining / 100))
            : 0;
        const finishDate = new Date(now.getTime() + daysNeeded * 86400000);
        const finishS = finishDate.toLocaleDateString("en-IN", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        const daysBeforeGate = Math.ceil((gate - finishDate) / 86400000);
        const ok = daysBeforeGate >= 60;
        const barWidth = Math.min(100, 100 - remaining) + "%";
        const projWidth = Math.min(100, (daysNeeded / totalDays) * 100) + "%";
        return (
          <div key={s.id} style={{ marginBottom: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: G.textSecondary,
                  fontWeight: 600,
                }}
              >
                {s.name}
              </span>
              <div style={{ display: "flex", gap: 7 }}>
                <Tag text={`${pct}% done`} color={s.clr} />
                <Tag
                  text={
                    daysBeforeGate > 0
                      ? `done ${daysBeforeGate}d before GATE`
                      : `⚠ ${Math.abs(daysBeforeGate)}d AFTER GATE`
                  }
                  color={ok ? G.emerald : "#ff5577"}
                />
              </div>
            </div>
            <div
              style={{
                height: 10,
                background: "rgba(15,25,55,0.8)",
                borderRadius: 99,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: barWidth,
                  height: "100%",
                  background: `linear-gradient(90deg,${s.clr}88,${s.clr})`,
                  borderRadius: 99,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: barWidth,
                  height: "100%",
                  width: `${Math.min(
                    100 - parseFloat(barWidth),
                    parseFloat(projWidth)
                  )}%`,
                  background: `${ok ? G.emerald : "#ff5577"}55`,
                  borderRadius: 99,
                }}
              />
            </div>
            {pct < 100 && (
              <div
                style={{
                  fontSize: 10,
                  color: ok ? G.textDim : "#ff5577",
                  marginTop: 2,
                }}
              >
                Projected finish: {finishS}
                {!ok ? " ← behind schedule!" : ""}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── RESCUE ALERT ────────────────────────────────────────── */
/* ── RESCUE ALERT ────────────────────────────────────────── */
const RescueAlerts = ({ subjects, goals, setGoals, addToast, examDateStr }) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const gate = new Date(examDateStr);
  gate.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil((gate - now) / 86400000);
  const alerts = subjects.filter((s) => subjPct(s) < 30 && daysLeft <= 300);
  if (!alerts.length) return null;
  return (
    <GlassCard
      accent="#ff5577"
      style={{
        padding: "14px 18px",
        marginBottom: 14,
        borderLeft: "3px solid #ff5577",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 800,
          color: "#ff5577",
          marginBottom: 10,
        }}
      >
        🚨 Rescue Alert — {daysLeft} days to GATE
      </div>
      {alerts.map((s) => {
        const pct = subjPct(s);
        const topicsNeeded = Math.ceil(s.topics.length * ((30 - pct) / 100));
        const weeksLeft = Math.ceil(daysLeft / 7);
        const topicsPerWeek =
          weeksLeft > 0 ? (topicsNeeded / weeksLeft).toFixed(1) : "?";
        return (
          <div
            key={s.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "8px 0",
              borderBottom: `1px solid ${G.glassBorder}44`,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: s.clr,
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1 }}>
              <span
                style={{
                  fontSize: 12.5,
                  color: G.textSecondary,
                  fontWeight: 600,
                }}
              >
                {s.name}
              </span>
              <span style={{ fontSize: 11, color: "#ff5577", marginLeft: 8 }}>
                {pct}% — needs {topicsNeeded} more topics ({topicsPerWeek}/week
                min)
              </span>
            </div>
            <button
              onClick={() => {
                setGoals((g) => [
                  ...g,
                  {
                    id: uid(),
                    text: `Rescue ${s.name}: ${topicsPerWeek} topics/week minimum`,
                    type: "short",
                    date: addDays(todayS(), 30),
                    done: false,
                  },
                ]);
                addToast({
                  title: "Goal Added",
                  message: `${s.name} rescue plan created`,
                  icon: "🎯",
                  color: s.clr,
                });
              }}
              style={{ ...BS(s.clr), padding: "5px 12px", fontSize: 11 }}
            >
              Add Goal
            </button>
          </div>
        );
      })}
    </GlassCard>
  );
};

/* ── PYQ YEAR-WISE MATRIX ───────────────────────────────── */
const PYQMatrix = ({ subjects, pyqMatrix, setPyqMatrix }) => {
  const toggle = (subjectId, topicName, yearIdx) => {
    const key = `${subjectId}_${topicName}_${yearIdx}`;
    setPyqMatrix((m) => ({ ...m, [key]: !m[key] }));
    vibrate(30);
  };
  return (
    <div style={{ overflowX: "auto" }}>
      {subjects.map((s) => (
        <div key={s.id} style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: s.clr,
              marginBottom: 10,
              borderBottom: `1px solid ${s.clr}33`,
              paddingBottom: 5,
            }}
          >
            {s.name}
          </div>
          <table
            style={{
              borderCollapse: "separate",
              borderSpacing: 3,
              fontSize: 10.5,
              minWidth: 600,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: "4px 10px",
                    color: G.textSecondary,
                    textAlign: "left",
                    fontSize: 10,
                    fontWeight: 700,
                    minWidth: 150,
                  }}
                >
                  Topic
                </th>
                {PYQ_YEARS.map((y) => (
                  <th
                    key={y}
                    style={{
                      padding: "4px 7px",
                      color: G.textMuted,
                      fontSize: 10,
                      fontWeight: 700,
                      textAlign: "center",
                      minWidth: 38,
                    }}
                  >
                    {y}
                  </th>
                ))}
                <th
                  style={{
                    padding: "4px 7px",
                    color: G.textMuted,
                    fontSize: 10,
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Freq
                </th>
              </tr>
            </thead>
            <tbody>
              {s.topics.map((t) => {
                const freq = PYQ_FREQ[t.name] || Array(10).fill(0);
                const totalFreq = freq.reduce((a, b) => a + b, 0);
                const solved = PYQ_YEARS.filter(
                  (_, i) => pyqMatrix[`${s.id}_${t.name}_${i}`]
                ).length;
                return (
                  <tr key={t.id}>
                    <td
                      style={{
                        padding: "5px 10px",
                        color: G.textSecondary,
                        fontSize: 11,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        {t.name}
                        <DiffStars value={TOPIC_DIFFICULTY[t.name] || 3} />
                      </div>
                      <div style={{ fontSize: 9, color: G.textDim }}>
                        {solved}/{PYQ_YEARS.length} years solved
                      </div>
                    </td>
                    {PYQ_YEARS.map((y, i) => {
                      const hasPYQ = freq[i] > 0;
                      const done = pyqMatrix[`${s.id}_${t.name}_${i}`];
                      return (
                        <td
                          key={y}
                          onClick={() => hasPYQ && toggle(s.id, t.name, i)}
                          style={{
                            textAlign: "center",
                            padding: "4px 3px",
                            cursor: hasPYQ ? "pointer" : "default",
                          }}
                        >
                          <div
                            style={{
                              width: 32,
                              height: 26,
                              borderRadius: 6,
                              background: !hasPYQ
                                ? "rgba(12,20,44,0.4)"
                                : done
                                ? `${s.clr}33`
                                : `${G.rose}22`,
                              border: `1px solid ${
                                !hasPYQ
                                  ? G.glassBorder
                                  : done
                                  ? s.clr
                                  : G.rose + "55"
                              }`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto",
                              transition: "all 0.15s",
                            }}
                          >
                            {!hasPYQ ? (
                              <span style={{ fontSize: 9, color: G.textDim }}>
                                —
                              </span>
                            ) : done ? (
                              <span style={{ fontSize: 11, color: s.clr }}>
                                ✓
                              </span>
                            ) : (
                              <span style={{ fontSize: 9, color: G.rose }}>
                                {freq[i]}Q
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ textAlign: "center", padding: "4px 7px" }}>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color:
                            totalFreq >= 15
                              ? G.rose
                              : totalFreq >= 10
                              ? G.orange
                              : totalFreq >= 5
                              ? G.amber
                              : G.textDim,
                        }}
                      >
                        {totalFreq}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

/* ── EFFORT-YIELD CHART ─────────────────────────────────── */
const EffortYieldChart = ({ subjects, tests, pomodoroLog }) => {
  const W = 520,
    H = 310,
    PX = 50,
    PY = 20,
    IW = W - PX - 30,
    IH = H - PY - 50;
  const timePerSubj = {};
  (pomodoroLog || []).forEach((l) => {
    timePerSubj[l.subject] = (timePerSubj[l.subject] || 0) + l.minutes;
  });
  const totalTime = Object.values(timePerSubj).reduce((a, b) => a + b, 0) || 1;
  const pts = subjects.map((s) => {
    const timePct = ((timePerSubj[s.name] || 0) / totalTime) * 100;
    const subjTests = tests.filter(
      (t) => t.subject === s.name || t.subject === "All"
    );
    const avgScore = subjTests.length
      ? subjTests.reduce(
          (a, t) => a + Math.round((t.score / t.total) * 100),
          0
        ) / subjTests.length
      : subjPct(s) * 0.8;
    const wt = WEIGHTAGE[s.name] || 10;
    return {
      name: s.name.split(" ").slice(-1)[0],
      timePct,
      avgScore,
      wt,
      clr: s.clr,
      isSink: timePct > wt * 1.5 && avgScore < 60,
    };
  });
  return (
    <div>
      <div
        style={{ display: "flex", gap: 14, marginBottom: 10, flexWrap: "wrap" }}
      >
        {[
          { c: "#ff5577", l: "Time Sink (over-invested, under-performing)" },
          { c: G.emerald, l: "High ROI topic" },
        ].map((x) => (
          <div
            key={x.l}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 10.5,
              color: G.textMuted,
            }}
          >
            <div
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                background: x.c,
              }}
            />
            {x.l}
          </div>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
        {[0, 25, 50, 75, 100].map((y) => {
          const cy = PY + IH - (y / 100) * IH;
          return (
            <g key={y}>
              <line
                x1={PX}
                y1={cy}
                x2={PX + IW}
                y2={cy}
                stroke="rgba(120,160,255,0.18)"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text
                x={PX - 7}
                y={cy + 4}
                textAnchor="end"
                fontSize="9.5"
                fill={G.textDim}
              >
                {y}
              </text>
            </g>
          );
        })}
        {[0, 25, 50, 75, 100].map((x) => {
          const cx = PX + (x / 100) * IW;
          return (
            <g key={x}>
              <line
                x1={cx}
                y1={PY}
                x2={cx}
                y2={PY + IH}
                stroke="rgba(120,160,255,0.18)"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text
                x={cx}
                y={PY + IH + 14}
                textAnchor="middle"
                fontSize="9.5"
                fill={G.textDim}
              >
                {x}%
              </text>
            </g>
          );
        })}
        <line
          x1={PX}
          y1={PY + IH}
          x2={PX + IW}
          y2={PY}
          stroke={`${G.emerald}33`}
          strokeWidth="1.5"
          strokeDasharray="8 4"
        />
        <text
          x={PX + IW * 0.72}
          y={PY + IH - IH * 0.72 + 14}
          fontSize="9"
          fill={`${G.emerald}88`}
        >
          Ideal
        </text>
        <text
          x={PX - 2}
          y={PY - 8}
          fontSize="10"
          fill={G.textMuted}
          textAnchor="middle"
        >
          Score%
        </text>
        <text x={PX + IW + 10} y={PY + IH + 5} fontSize="10" fill={G.textMuted}>
          Time%
        </text>
        {pts.map((p, i) => {
          const cx = PX + (p.timePct / 100) * IW,
            cy = PY + IH - (p.avgScore / 100) * IH,
            r = Math.max(7, Math.sqrt(p.wt) * 3.2);
          return (
            <g key={i}>
              <circle
                cx={cx}
                cy={cy}
                r={r}
                fill={
                  p.isSink ? "rgba(255,85,119,0.2)" : "rgba(78,234,170,0.15)"
                }
                stroke={p.isSink ? "#ff5577" : G.emerald}
                strokeWidth="2.5"
              />
              <text
                x={cx}
                y={cy - r - 4}
                textAnchor="middle"
                fontSize="10"
                fontWeight="700"
                fill={p.clr}
              >
                {p.name}
              </text>
              {p.isSink && (
                <text
                  x={cx}
                  y={cy + r + 12}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#ff5577"
                >
                  ⚠ Sink
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/* ── STUDY CALENDAR HEATMAP (NEW) ────────────────────────────
   GitHub-style contribution grid built from pomodoroLog. Shows
   the last ~18 weeks of study minutes, plus a few summary stats.
   Pure SVG — no external chart library needed.                */
const StudyCalendarHeatmap = ({ pomodoroLog, streak }) => {
  const WEEKS = 18;
  const dailyMins = {};
  (pomodoroLog || []).forEach((l) => {
    dailyMins[l.date] = (dailyMins[l.date] || 0) + l.minutes;
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDow = today.getDay(); // 0=Sun..6=Sat
  const totalCells = WEEKS * 7;
  const startOffset = totalCells - 1 - endDow; // pad so grid ends on a Saturday column
  const cells = [];
  for (let i = startOffset; i >= -endDow; i--) {
    const d = new Date(today.getTime() - i * 86400000);
    const ds = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
    cells.push({
      date: ds,
      mins: dailyMins[ds] || 0,
      dow: d.getDay(),
      future: d > today,
    });
  }
  // group into columns of 7 (Sun..Sat)
  const cols = [];
  for (let i = 0; i < cells.length; i += 7) cols.push(cells.slice(i, i + 7));

  const intensity = (m) =>
    m <= 0 ? 0 : m < 60 ? 1 : m < 120 ? 2 : m < 180 ? 3 : 4;
  const COLORS = [
    "rgba(255,255,255,0.05)",
    `${G.emerald}33`,
    `${G.emerald}66`,
    `${G.emerald}aa`,
    G.emerald,
  ];

  const activeDays = Object.values(dailyMins).filter((m) => m > 0).length;
  const totalMins = Object.values(dailyMins).reduce((a, b) => a + b, 0);
  const bestDay = Math.max(0, ...Object.values(dailyMins));
  const cellSize = 12,
    gap = 3;

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {[
          { l: "Current Streak", v: `${streak}🔥`, c: G.amber },
          { l: "Active Days", v: activeDays, c: G.emerald },
          {
            l: "Total Hours",
            v: `${Math.floor(totalMins / 60)}h ${totalMins % 60}m`,
            c: G.cyan,
          },
          {
            l: "Best Day",
            v: `${Math.floor(bestDay / 60)}h ${bestDay % 60}m`,
            c: G.violet,
          },
        ].map((s) => (
          <GlassCard key={s.l} accent={s.c} style={{ padding: "12px 14px" }}>
            <div
              style={{
                fontSize: 10,
                color: G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {s.l}
            </div>
            <div
              style={{
                fontSize: 19,
                fontWeight: 900,
                color: s.c,
                textShadow: `0 0 14px ${s.c}55`,
                marginTop: 3,
              }}
            >
              {s.v}
            </div>
          </GlassCard>
        ))}
      </div>
      <div style={{ overflowX: "auto", paddingBottom: 4 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap,
              paddingTop: 18,
              flexShrink: 0,
            }}
          >
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
              <div
                key={d}
                style={{
                  height: cellSize,
                  fontSize: 8.5,
                  color: G.textDim,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {i % 2 === 1 ? d.slice(0, 1) : ""}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap, flexShrink: 0 }}>
            {cols.map((col, ci) => {
              const firstOfMonth = col.find(
                (c) => new Date(c.date + "T12:00:00").getDate() <= 7
              );
              return (
                <div
                  key={ci}
                  style={{ display: "flex", flexDirection: "column", gap }}
                >
                  <div
                    style={{
                      height: 12,
                      fontSize: 8.5,
                      color: G.textDim,
                      textAlign: "center",
                    }}
                  >
                    {firstOfMonth
                      ? MONTH_NAMES[
                          new Date(firstOfMonth.date + "T12:00:00").getMonth()
                        ].slice(0, 3)
                      : ""}
                  </div>
                  {col.map((c, i) => (
                    <div
                      key={i}
                      title={`${c.date}: ${c.mins} min`}
                      style={{
                        width: cellSize,
                        height: cellSize,
                        borderRadius: 3,
                        background: c.future
                          ? "transparent"
                          : COLORS[intensity(c.mins)],
                        border: c.future
                          ? `1px dashed ${G.glassBorder}`
                          : c.date === todayS()
                          ? `1px solid ${G.violet}`
                          : "1px solid transparent",
                      }}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginTop: 12,
          fontSize: 10.5,
          color: G.textMuted,
        }}
      >
        <span>Less</span>
        {COLORS.map((c, i) => (
          <div
            key={i}
            style={{ width: 11, height: 11, borderRadius: 3, background: c }}
          />
        ))}
        <span>More</span>
        <span style={{ marginLeft: "auto", color: G.textDim }}>
          ≥2hrs (120min) keeps your streak alive 🔥
        </span>
      </div>
    </div>
  );
};

/* ── AI STUDY ASSISTANT (NEW) ─────────────────────────────────
   Claude-powered chat that's aware of the student's actual
   progress data — quick actions build prompts that embed real
   readiness/weak-topic info for genuinely personalized answers. */
/* ── AI STUDY ASSISTANT — FIXED ───────────────────────────────────
   Works in Claude.ai (no key needed, injected automatically) AND
   on any other device (user provides their Anthropic API key).

   The three headers that make direct browser→API work:
     x-api-key                            your API key
     anthropic-version                    2023-06-01
     anthropic-dangerous-direct-browser-ipc  true  ← tells Anthropic to allow CORS

   Get a key at: console.anthropic.com → API Keys
   ─────────────────────────────────────────────────────────────── */

/* ── STUDY ORDER MODAL ───────────────────────────────────── */
const StudyOrderModal = ({ subjects, onClose }) => {
  const order = STUDY_ORDER.map((o) => ({
    ...o,
    subj: subjects.find((s) => s.id === o.id),
  })).filter((o) => o.subj);
  return (
    <Modal
      title="📐 Recommended Study Order"
      onClose={onClose}
      accent={G.cyan}
      wide
    >
      <div style={{ fontSize: 12, color: G.textSecondary, marginBottom: 16 }}>
        Based on subject dependencies and GATE ECE curriculum structure:
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {order.map((o, i) => {
          const pct = subjPct(o.subj);
          return (
            <div
              key={o.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "11px 15px",
                background: "rgba(8,14,32,0.7)",
                borderRadius: 12,
                border: `1px solid ${o.subj.clr}2a`,
                borderLeft: `3px solid ${o.subj.clr}`,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: `${o.subj.clr}22`,
                  border: `2px solid ${o.subj.clr}55`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 900,
                  color: o.subj.clr,
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: G.textPrimary,
                  }}
                >
                  {o.subj.name}
                </div>
                <div style={{ fontSize: 11, color: G.textMuted, marginTop: 2 }}>
                  {o.reason}
                </div>
                {o.deps.length > 0 && (
                  <div style={{ fontSize: 10, color: G.textDim, marginTop: 2 }}>
                    Requires:{" "}
                    {o.deps
                      .map((d) => subjects.find((s) => s.id === d)?.name || d)
                      .join(", ")}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 900,
                    color: pctColor(pct),
                    textShadow: `0 0 12px ${pctColor(pct)}55`,
                  }}
                >
                  {pct}%
                </div>
                <Bar pct={pct} color={o.subj.clr} h={4} />
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

/* ── CUSTOM DAILY PLAN PAGE ──────────────────────────────── */
const DailyPlanPage = ({ dailyPlan, setDailyPlan }) => {
  const [nt, setNt] = useState({
    text: "",
    subject: "General / Other",
    type: "study",
  });
  const doAdd = () => {
    if (!nt.text.trim()) return;
    setDailyPlan((p) => [...p, { id: uid(), ...nt, done: false }]);
    setNt({ text: "", subject: "General / Other", type: "study" });
  };
  const toggle = (id) =>
    setDailyPlan((p) =>
      p.map((t) => (t.id === id ? { ...t, done: !t.done } : t))
    );
  const del = (id) => setDailyPlan((p) => p.filter((t) => t.id !== id));
  const reset = () =>
    setDailyPlan((p) => p.map((t) => ({ ...t, done: false })));
  const clear = () => setDailyPlan([]);
  const done = dailyPlan.filter((t) => t.done).length;

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: G.textPrimary,
              margin: 0,
              letterSpacing: "-0.025em",
            }}
          >
            Custom Daily Plan
          </h1>
          <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
            Set your specific goals and tasks for today
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={reset}
            style={{ ...BS(G.textMuted), padding: "7px 14px", fontSize: 11.5 }}
          >
            ↺ Reset All
          </button>
          <button
            onClick={clear}
            style={{ ...BS(G.rose), padding: "7px 14px", fontSize: 11.5 }}
          >
            Clear Plan
          </button>
        </div>
      </div>

      <GlassCard
        accent={G.violet}
        style={{
          padding: 18,
          marginBottom: 18,
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          value={nt.text}
          onChange={(e) => setNt({ ...nt, text: e.target.value })}
          placeholder="What do you want to accomplish today?"
          style={{ ...IS, flex: 1, minWidth: 200 }}
          onKeyDown={(e) => e.key === "Enter" && doAdd()}
        />
        <select
          value={nt.subject}
          onChange={(e) => setNt({ ...nt, subject: e.target.value })}
          style={{ ...IS, width: 160 }}
        >
          {ALL_SUBJS.map((s) => (
            <option key={s} value={s}>
              {s.split(" ").slice(-1)[0]}
            </option>
          ))}
        </select>
        <select
          value={nt.type}
          onChange={(e) => setNt({ ...nt, type: e.target.value })}
          style={{ ...IS, width: 140 }}
        >
          <option value="study">📚 Study/Revise</option>
          <option value="test">🧪 Test/PYQ</option>
          <option value="error">🐛 Fix Errors</option>
          <option value="personal">☕ Break/Other</option>
        </select>
        <button
          onClick={doAdd}
          style={{ ...BS(G.violet, true), padding: "9px 20px" }}
        >
          Add Task
        </button>
      </GlassCard>

      <GlassCard
        accent={G.cyan}
        style={{ padding: "14px 18px", marginBottom: 18 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, color: G.textPrimary }}>
            Today's Custom Checklist
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: G.textMuted }}>
              {done}/{dailyPlan.length} completed
            </span>
            <div style={{ width: 100 }}>
              <Bar
                pct={
                  dailyPlan.length
                    ? Math.round((done / dailyPlan.length) * 100)
                    : 0
                }
                color={G.emerald}
              />
            </div>
          </div>
        </div>
      </GlassCard>

      {dailyPlan.length === 0 ? (
        <GlassCard style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📝</div>
          <div
            style={{ color: G.textSecondary, fontSize: 14, fontWeight: 700 }}
          >
            Your daily plan is empty
          </div>
          <div style={{ color: G.textMuted, fontSize: 12, marginTop: 4 }}>
            Add a task above to start planning your day.
          </div>
        </GlassCard>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {dailyPlan.map((task) => {
            const clr = CAT_COLOR[task.type] || G.violet;
            return (
              <GlassCard
                key={task.id}
                style={{
                  padding: "12px 16px",
                  borderLeft: `3px solid ${clr}`,
                  opacity: task.done ? 0.55 : 1,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  <CB
                    checked={task.done}
                    onChange={() => toggle(task.id)}
                    color={clr}
                  />
                  <span style={{ fontSize: 14, flexShrink: 0 }}>
                    {CAT_ICON[task.type] || "📚"}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        color: task.done ? G.textDim : G.textPrimary,
                        fontWeight: 600,
                        textDecoration: task.done ? "line-through" : "none",
                      }}
                    >
                      {task.text}
                    </div>
                    <Tag
                      text={
                        task.subject === "General / Other"
                          ? "General"
                          : task.subject
                      }
                      color={clr}
                    />
                  </div>
                  <button
                    onClick={() => del(task.id)}
                    style={{
                      background: "none",
                      border: "none",
                      color: G.textDim,
                      cursor: "pointer",
                      fontSize: 18,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── WEEKLY EXPORT ────────────────────────────────────────── */
const generateWeeklyReport = (
  subjects,
  tests,
  errors,
  pomodoroLog,
  streak,
  qnotes,
  examDateStr
) => {
  const oneWeekAgo = addDays(todayS(), -7);
  const topicsThisWeek = subjects.reduce(
    (a, s) =>
      a +
      s.topics.filter((t) => t.studiedDate && t.studiedDate >= oneWeekAgo)
        .length,
    0
  );
  const testsThisWeek = tests.filter((t) => t.date && t.date >= oneWeekAgo);
  const errorsThisWeek = errors.filter((e) => e.date && e.date >= oneWeekAgo);
  const pomosThisWeek = pomodoroLog.filter(
    (l) => l.date && l.date >= oneWeekAgo
  );
  const totalMins = pomosThisWeek.reduce((a, l) => a + l.minutes, 0);
  const newNotes = qnotes.filter((n) => n.date && n.date >= oneWeekAgo);
  const overall = overallPct(subjects);
  const testAvgW = testsThisWeek.length
    ? Math.round(
        testsThisWeek.reduce(
          (a, t) => a + Math.round((t.score / t.total) * 100),
          0
        ) / testsThisWeek.length
      )
    : null;
  return `📊 GATE ECE 2027 — Weekly Progress Report
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Week ending: ${new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📈 OVERALL PROGRESS: ${overall}%
🔥 Current Streak: ${streak} days (≥2hrs/day)

THIS WEEK:
• Topics studied: ${topicsThisWeek}
• Focus time: ${Math.floor(totalMins / 60)}h ${totalMins % 60}m (${
    pomosThisWeek.length
  } sessions)
• Mock tests taken: ${testsThisWeek.length}${
    testAvgW ? ` (avg score: ${testAvgW}%)` : ""
  }
• Errors logged: ${errorsThisWeek.length}
• Notes added: ${newNotes.length}

SUBJECT PROGRESS:
${subjects
  .map((s) => `• ${s.name}: ${subjPct(s)}% (${s.wt} weightage)`)
  .join("\n")}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Days to Exam: ${Math.max(
    0,
    Math.ceil((new Date(examDateStr) - new Date()) / 86400000)
  )}
Keep pushing! 🚀`;
};

/* ── STREAK SHARE CARD ───────────────────────────────────── */
const StreakShareCard = ({
  subjects,
  streak,
  readinessScore,
  pomodoroLog,
  examDateStr,
}) => {
  const canvasRef = useRef(null);
  const [generated, setGenerated] = useState(false);
  const generate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = 600;
    canvas.height = 340;
    ctx.fillStyle = "#050810";
    ctx.fillRect(0, 0, 600, 340);
    const grd = ctx.createLinearGradient(0, 0, 600, 340);
    grd.addColorStop(0, "rgba(60,30,140,0.3)");
    grd.addColorStop(1, "rgba(20,80,140,0.2)");
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, 600, 340);
    ctx.strokeStyle = "rgba(120,160,255,0.4)";
    ctx.lineWidth = 2;
    ctx.roundRect(4, 4, 592, 332, 16);
    ctx.stroke();
    const topGrd = ctx.createLinearGradient(0, 0, 600, 0);
    topGrd.addColorStop(0, "transparent");
    topGrd.addColorStop(0.5, "#b39dff");
    topGrd.addColorStop(1, "transparent");
    ctx.strokeStyle = topGrd;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(4, 6);
    ctx.lineTo(596, 6);
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 28px system-ui";
    ctx.fillText("GATE ECE 2027", 30, 55);
    ctx.fillStyle = "#9ab4d8";
    ctx.font = "14px system-ui";
    ctx.fillText("Preparation Progress", 30, 78);
    ctx.font = "42px serif";
    ctx.fillText("🔥", 480, 75);
    ctx.fillStyle = "#ffe666";
    ctx.font = "bold 32px monospace";
    ctx.fillText(`${streak}`, 528, 68);
    ctx.fillStyle = "#9ab4d8";
    ctx.font = "11px system-ui";
    ctx.fillText("DAY STREAK", 518, 82);
    const overall = overallPct(subjects);
    ctx.fillStyle = "#9ab4d8";
    ctx.font = "12px system-ui";
    ctx.fillText("OVERALL PROGRESS", 30, 115);
    ctx.fillStyle = "rgba(8,14,32,0.8)";
    ctx.roundRect(30, 125, 360, 12, 6);
    ctx.fill();
    const pColor = pctColor(overall);
    ctx.fillStyle = pColor;
    ctx.roundRect(30, 125, 360 * (overall / 100), 12, 6);
    ctx.fill();
    ctx.fillStyle = pColor;
    ctx.font = "bold 22px system-ui";
    ctx.fillText(`${overall}%`, 405, 138);
    ctx.fillStyle = "#9ab4d8";
    ctx.font = "12px system-ui";
    ctx.fillText("READINESS SCORE", 30, 170);
    ctx.fillStyle = "#b39dff";
    ctx.font = "bold 34px system-ui";
    ctx.fillText(`${readinessScore}%`, 30, 205);
    const totalMins = pomodoroLog.reduce((a, l) => a + l.minutes, 0);
    ctx.fillStyle = "#9ab4d8";
    ctx.font = "11px system-ui";
    ctx.fillText("STUDY TIME (TOTAL)", 310, 170);
    ctx.fillStyle = "#5ecfff";
    ctx.font = "bold 30px system-ui";
    ctx.fillText(`${Math.floor(totalMins / 60)}h ${totalMins % 60}m`, 310, 202);
    const cols = 3,
      bw = 80,
      bh = 28,
      startX = 30,
      startY = 228,
      gapX = 90,
      gapY = 36;
    subjects.slice(0, 9).forEach((s, i) => {
      const cx = startX + (i % cols) * gapX * 2,
        cy = startY + Math.floor(i / cols) * gapY;
      const p = subjPct(s);
      ctx.fillStyle = "rgba(8,14,32,0.6)";
      ctx.roundRect(cx, cy, bw, bh, 4);
      ctx.fill();
      ctx.fillStyle = s.clr;
      ctx.roundRect(cx, cy, bw * (p / 100), bh, 4);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 10px system-ui";
      ctx.fillText(`${s.name.split(" ").slice(-1)[0]} ${p}%`, cx + 4, cy + 18);
    });
    ctx.fillStyle = "#6080a8";
    ctx.font = "11px system-ui";
    ctx.fillText(
      `Generated ${new Date().toLocaleDateString(
        "en-IN"
      )} · Target Exam — ${Math.max(
        0,
        Math.ceil((new Date(examDateStr) - new Date()) / 86400000)
      )} days left`,
      30,
      325
    );
    setGenerated(true);
  };
  const download = () => {
    const link = document.createElement("a");
    link.download = `gate-streak-${todayS()}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  };
  return (
    <div style={{ marginTop: 14 }}>
      <GlassCard accent={G.amber} style={{ padding: "14px 18px" }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: G.amber,
            marginBottom: 10,
          }}
        >
          📤 Study Buddy Streak Card
        </div>
        <div
          style={{ fontSize: 11.5, color: G.textSecondary, marginBottom: 12 }}
        >
          Share your progress with your GATE study community
        </div>
        <canvas
          ref={canvasRef}
          style={{
            display: generated ? "block" : "none",
            width: "100%",
            borderRadius: 12,
            border: `1px solid ${G.glassBorder}`,
            marginBottom: 10,
          }}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={generate} style={{ ...BS(G.amber), flex: 1 }}>
            {generated ? "🔄 Regenerate" : "🎨 Generate Card"}
          </button>
          {generated && (
            <button onClick={download} style={{ ...BS(G.emerald), flex: 1 }}>
              ⬇ Download PNG
            </button>
          )}
        </div>
      </GlassCard>
    </div>
  );
};

/* ── NEGATIVE MARKING TRACKER ────────────────────────────── */
const NegativeMarkingModal = ({ test, onSave, onClose }) => {
  const [correct, setCorrect] = useState(test.correct || "");
  const [wrong, setWrong] = useState(test.wrong || "");
  const [skipped, setSkipped] = useState(test.skipped || "");
  const c = parseInt(correct) || 0,
    w = parseInt(wrong) || 0,
    s = parseInt(skipped) || 0;
  const netScore = c - w / 3;
  const attempted = c + w;
  const accuracy = attempted > 0 ? Math.round((c / attempted) * 100) : 0;
  return (
    <Modal title="Negative Marking Analysis" onClose={onClose} accent={G.rose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 11,
          }}
        >
          {[
            { l: "Correct ✓", val: correct, set: setCorrect, c: G.emerald },
            { l: "Wrong ✗", val: wrong, set: setWrong, c: G.rose },
            { l: "Skipped —", val: skipped, set: setSkipped, c: G.textMuted },
          ].map((f) => (
            <div key={f.l}>
              <div
                style={{
                  fontSize: 11.5,
                  color: f.c,
                  marginBottom: 4,
                  fontWeight: 700,
                }}
              >
                {f.l}
              </div>
              <input
                type="number"
                min="0"
                value={f.val}
                onChange={(e) => f.set(e.target.value)}
                style={IS}
              />
            </div>
          ))}
        </div>
        <GlassCard accent={G.violet} style={{ padding: "12px 16px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2,1fr)",
              gap: 9,
            }}
          >
            {[
              {
                l: "Net GATE Score",
                v: netScore.toFixed(2),
                c: pctColor((netScore / 65) * 100),
              },
              {
                l: "Attempt Accuracy",
                v: `${accuracy}%`,
                c:
                  accuracy >= 70
                    ? G.emerald
                    : accuracy >= 50
                    ? G.amber
                    : G.rose,
              },
              { l: "Attempted", v: attempted, c: G.cyan },
              { l: "Penalty (marks)", v: (w / 3).toFixed(2), c: G.rose },
            ].map((s) => (
              <div key={s.l}>
                <div
                  style={{
                    fontSize: 10.5,
                    color: G.textMuted,
                    fontWeight: 700,
                    textTransform: "uppercase",
                  }}
                >
                  {s.l}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 900,
                    color: s.c,
                    textShadow: `0 0 14px ${s.c}55`,
                  }}
                >
                  {s.v}
                </div>
              </div>
            ))}
          </div>
          {accuracy < 65 && (
            <div
              style={{
                marginTop: 10,
                fontSize: 11.5,
                color: G.rose,
                background: `${G.rose}18`,
                borderRadius: 8,
                padding: "7px 11px",
                border: `1px solid ${G.rose}33`,
              }}
            >
              ⚠ Accuracy below 65% — you may be losing more marks than you gain
              by guessing. Consider skipping uncertain questions.
            </div>
          )}
        </GlassCard>
        <button
          onClick={() => onSave(c, w, s)}
          style={{ ...BS(G.emerald, true), padding: "9px", width: "100%" }}
        >
          Save Analysis
        </button>
      </div>
    </Modal>
  );
};

/* ── ERROR HEATMAP ──────────────────────────────────────── */
const ErrorHeatmap = ({ errors, onCellClick }) => {
  const counts = ALL_SUBJS.slice(0, 9).map((s) =>
    ERROR_TYPES.map(
      (et) => errors.filter((e) => e.subject === s && e.errorType === et).length
    )
  );
  const maxC = Math.max(1, ...counts.flat());
  if (errors.length === 0)
    return (
      <div style={{ fontSize: 12.5, color: G.textDim, padding: "16px 0" }}>
        Log errors to see the heatmap.
      </div>
    );
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{ borderCollapse: "separate", borderSpacing: 3, fontSize: 11.5 }}
      >
        <thead>
          <tr>
            <th
              style={{
                padding: "4px 8px",
                color: G.textSecondary,
                textAlign: "left",
                fontSize: 10.5,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              Subject
            </th>
            {ERROR_TYPES.map((et) => (
              <th
                key={et}
                style={{
                  padding: "4px 7px",
                  color: G.textSecondary,
                  fontSize: 10,
                  fontWeight: 700,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {et}
              </th>
            ))}
            <th
              style={{
                padding: "4px 7px",
                color: G.textSecondary,
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {ALL_SUBJS.slice(0, 9).map((s, si) => {
            const row = counts[si],
              rowTotal = row.reduce((a, b) => a + b, 0);
            return (
              <tr key={s}>
                <td
                  style={{
                    padding: "4px 8px",
                    color: G.textPrimary,
                    fontSize: 11.5,
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                  }}
                >
                  {SUBJ_SHORT[si]}
                </td>
                {row.map((c, ei) => {
                  const intensity = c > 0 ? 0.2 + (c / maxC) * 0.8 : 0,
                    isHot = c >= 3;
                  return (
                    <td
                      key={ei}
                      onClick={() =>
                        c > 0 &&
                        onCellClick &&
                        onCellClick(s, ERROR_TYPES[ei], c)
                      }
                      style={{
                        padding: "7px 8px",
                        textAlign: "center",
                        background:
                          c > 0
                            ? `rgba(255,85,119,${intensity})`
                            : "rgba(12,20,44,0.7)",
                        borderRadius: 7,
                        minWidth: 58,
                        cursor: c > 0 ? "pointer" : "default",
                        transition: "all 0.2s",
                        border: isHot
                          ? `1px solid rgba(255,85,119,0.5)`
                          : "1px solid transparent",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: c > 0 ? 700 : 400,
                          color:
                            c > 0
                              ? intensity > 0.6
                                ? "#fff"
                                : "#ff5577"
                              : G.textDim,
                        }}
                      >
                        {c > 0 ? c : "·"}
                      </span>
                    </td>
                  );
                })}
                <td
                  style={{
                    padding: "7px 8px",
                    textAlign: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color:
                      rowTotal > 0
                        ? pctColor(100 - (rowTotal / maxC) * 100)
                        : G.textDim,
                  }}
                >
                  {rowTotal || "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ── ACHIEVEMENT BADGE ──────────────────────────────────── */
const AchievementBadge = ({ achievement, earned }) => (
  <div
    style={{
      padding: "12px 14px",
      background: earned ? `${G.violet}22` : "rgba(12,20,44,0.6)",
      border: `1px solid ${earned ? G.violet + "55" : G.glassBorder}`,
      borderRadius: 14,
      opacity: earned ? 1 : 0.5,
      backdropFilter: "blur(10px)",
      transition: "all 0.3s",
    }}
  >
    <div
      style={{
        fontSize: 22,
        marginBottom: 5,
        filter: earned ? "drop-shadow(0 0 10px #b39dff88)" : "grayscale(1)",
      }}
    >
      {achievement.icon}
    </div>
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: earned ? G.textPrimary : G.textMuted,
      }}
    >
      {achievement.title}
    </div>
    <div
      style={{
        fontSize: 10.5,
        color: G.textMuted,
        marginTop: 2,
        lineHeight: 1.4,
      }}
    >
      {achievement.desc}
    </div>
    {earned && (
      <div
        style={{ fontSize: 10, color: G.violet, marginTop: 5, fontWeight: 700 }}
      >
        ✓ Earned
      </div>
    )}
  </div>
);

/* ── FLASHCARD SYSTEM (SM-2 spaced repetition) ──────────────
   Replaces the old fixed +1/+7 day buttons with a lightweight
   SM-2 style scheduler: "Again" resets the interval, "Good"
   grows it using an ease factor (1,6,then ×ease), "Easy" grows
   it faster and nudges the ease factor up. A card counts as
   "Known" once its interval reaches 7+ days.                  */
const FlashcardsPage = ({ flashcards, setFlashcards }) => {
  const [flipped, setFlipped] = useState({});
  const [filterSubj, setFilterSubj] = useState("All");
  const [filterMode, setFilterMode] = useState("all"); // all | due | learning
  const [showAdd, setShowAdd] = useState(false);
  const [viewImage, setViewImage] = useState(null);
  const [nf, setNf] = useState({
    subject: "Engineering Mathematics",
    front: "",
    back: "",
    backImg: "",
  });

  const filtered = flashcards.filter(
    (f) =>
      (filterSubj === "All" || f.subject === filterSubj) &&
      (filterMode === "all" ||
        (filterMode === "due" && (!f.due || f.due <= todayS())) ||
        (filterMode === "learning" && !f.known))
  );

  const reviewCard = (id, grade) => {
    vibrate(grade === "again" ? 15 : 40);
    setFlashcards((fc) =>
      fc.map((f) => {
        if (f.id !== id) return f;
        let ease = f.ease ?? 2.5,
          interval = f.interval ?? 1,
          reps = f.reps ?? 0;
        if (grade === "again") {
          reps = 0;
          interval = 1;
          ease = Math.max(1.3, ease - 0.2);
        } else if (grade === "good") {
          reps += 1;
          interval =
            reps === 1 ? 1 : reps === 2 ? 6 : Math.round(interval * ease);
        } else {
          reps += 1;
          interval = reps === 1 ? 4 : Math.round(interval * ease * 1.3);
          ease = Math.min(3, ease + 0.15);
        }
        interval = Math.max(1, interval);
        return {
          ...f,
          ease,
          interval,
          reps,
          known: interval >= 7,
          due: addDays(todayS(), interval),
        };
      })
    );
  };

  const addCard = () => {
    if (!nf.front.trim() || (!nf.back.trim() && !nf.backImg)) return;
    setFlashcards((fc) => [
      ...fc,
      {
        id: uid(),
        ...nf,
        known: false,
        due: todayS(),
        ease: 2.5,
        interval: 1,
        reps: 0,
      },
    ]);
    setNf({
      subject: "Engineering Mathematics",
      front: "",
      back: "",
      backImg: "",
    });
    setShowAdd(false);
  };
  const delCard = (id) => setFlashcards((fc) => fc.filter((f) => f.id !== id));

  const knownCount = flashcards.filter((f) => f.known).length;
  const dueCount = flashcards.filter((f) => !f.due || f.due <= todayS()).length;

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: G.textPrimary,
              margin: 0,
              letterSpacing: "-0.025em",
            }}
          >
            Formula Flash Cards
          </h1>
          <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
            SM-2 spaced repetition · Images & text on back · Tap to flip
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} style={BS(G.amber)}>
          + New Card
        </button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 10,
          marginBottom: 18,
        }}
      >
        {[
          { l: "Total Cards", v: flashcards.length, c: G.violet },
          { l: "Known (7d+)", v: knownCount, c: G.emerald },
          { l: "Due Today", v: dueCount, c: G.pink },
          {
            l: "Still Learning",
            v: flashcards.length - knownCount,
            c: G.orange,
          },
        ].map((s) => (
          <GlassCard key={s.l} accent={s.c} style={{ padding: "13px 16px" }}>
            <div
              style={{
                fontSize: 10.5,
                color: G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {s.l}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 900,
                color: s.c,
                margin: "5px 0",
                textShadow: `0 0 18px ${s.c}55`,
              }}
            >
              {s.v}
            </div>
          </GlassCard>
        ))}
      </div>
      <div
        style={{ display: "flex", gap: 7, marginBottom: 16, flexWrap: "wrap" }}
      >
        <select
          value={filterSubj}
          onChange={(e) => setFilterSubj(e.target.value)}
          style={{ ...IS, width: 200 }}
        >
          <option value="All">All Subjects</option>
          {ALL_SUBJS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {["all", "due", "learning"].map((m) => (
          <button
            key={m}
            onClick={() => setFilterMode(m)}
            style={{ ...BS(G.violet, filterMode === m), padding: "7px 16px" }}
          >
            {m === "all"
              ? "All Cards"
              : m === "due"
              ? "Due Today"
              : "Still Learning"}
          </button>
        ))}
        <span
          style={{
            fontSize: 12,
            color: G.textMuted,
            display: "flex",
            alignItems: "center",
          }}
        >
          {filtered.length} cards
        </span>
      </div>

      {filtered.length === 0 ? (
        <GlassCard style={{ padding: 52, textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🃏</div>
          <div style={{ color: G.textMuted, fontSize: 14 }}>
            No cards match. Add some or change filter!
          </div>
        </GlassCard>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))",
            gap: 12,
          }}
        >
          {filtered.map((f) => {
            const isFlipped = !!flipped[f.id];
            const subjectColor =
              INIT_SUBJECTS.find((s) => s.name === f.subject)?.clr || G.violet;
            const interval = f.interval ?? 1;
            return (
              <div key={f.id} style={{ perspective: 1000, height: 240 }}>
                <div
                  onClick={() =>
                    setFlipped((fv) => ({ ...fv, [f.id]: !fv[f.id] }))
                  }
                  style={{
                    position: "relative",
                    transformStyle: "preserve-3d",
                    transition: "transform 0.5s",
                    transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                    height: "100%",
                    cursor: "pointer",
                  }}
                >
                  {/* Front */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                      background: G.glass,
                      border: `1px solid ${
                        f.known ? G.emerald + "55" : subjectColor + "33"
                      }`,
                      borderRadius: 16,
                      padding: 18,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{ display: "flex", gap: 5, flexWrap: "wrap" }}
                      >
                        <Tag
                          text={f.subject.split(" ").slice(-1)[0]}
                          color={subjectColor}
                        />
                        <Tag
                          text={`📅 ${interval}d`}
                          color={interval >= 7 ? G.emerald : G.textMuted}
                        />
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          delCard(f.id);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: G.textMuted,
                          cursor: "pointer",
                          fontSize: 16,
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div
                      style={{
                        fontSize: 14.5,
                        fontWeight: 700,
                        color: G.textPrimary,
                        textAlign: "center",
                        lineHeight: 1.5,
                        padding: "12px 0",
                      }}
                    >
                      {f.front}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: G.textDim,
                        textAlign: "center",
                      }}
                    >
                      Tap to reveal →
                    </div>
                  </div>
                  {/* Back */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                      transform: "rotateY(180deg)",
                      background: "rgba(22,32,62,0.97)",
                      border: `1px solid ${subjectColor}55`,
                      borderRadius: 16,
                      padding: 18,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: subjectColor,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Answer
                    </div>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "8px 0",
                        overflowY: "auto",
                      }}
                    >
                      {f.backImg && (
                        <img
                          src={f.backImg}
                          alt="Flashcard visual"
                          title="Click to enlarge"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewImage(f.backImg);
                          }}
                          style={{
                            maxWidth: "100%",
                            maxHeight: 120,
                            objectFit: "contain",
                            marginBottom: 6,
                            borderRadius: 6,
                            cursor: "zoom-in",
                          }}
                        />
                      )}
                      {f.back && (
                        <div
                          style={{
                            fontSize: 13.5,
                            color: G.textPrimary,
                            textAlign: "center",
                            fontFamily: "monospace",
                            lineHeight: 1.6,
                          }}
                        >
                          {f.back}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          reviewCard(f.id, "again");
                        }}
                        style={{ ...BS(G.rose), flex: 1, fontSize: 10.5 }}
                      >
                        Again
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          reviewCard(f.id, "good");
                        }}
                        style={{ ...BS(G.cyan), flex: 1, fontSize: 10.5 }}
                      >
                        Good
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          reviewCard(f.id, "easy");
                        }}
                        style={{
                          ...BS(G.emerald, true),
                          flex: 1,
                          fontSize: 10.5,
                        }}
                      >
                        Easy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <Modal
          title="Add Flash Card"
          onClose={() => setShowAdd(false)}
          accent={G.amber}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
            <select
              value={nf.subject}
              onChange={(e) =>
                setNf((n) => ({ ...n, subject: e.target.value }))
              }
              style={IS}
            >
              {ALL_SUBJS.slice(0, -1).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <div>
              <div
                style={{ fontSize: 11.5, color: G.textMuted, marginBottom: 4 }}
              >
                Front (concept / symbol)
              </div>
              <input
                value={nf.front}
                onChange={(e) =>
                  setNf((n) => ({ ...n, front: e.target.value }))
                }
                placeholder="e.g. Fourier Transform definition"
                style={IS}
                autoFocus
              />
            </div>
            <div>
              <div
                style={{ fontSize: 11.5, color: G.textMuted, marginBottom: 4 }}
              >
                Back Image (Optional)
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "rgba(8,14,32,0.6)",
                  padding: 8,
                  borderRadius: 10,
                  border: `1px solid ${G.glassBorder}`,
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      const dataUrl = await compressImageForStorage(
                        file,
                        800,
                        0.7
                      );
                      setNf((n) => ({ ...n, backImg: dataUrl }));
                    } catch (err) {
                      setNf((n) => ({ ...n, backImg: "" }));
                    }
                  }}
                  style={{ fontSize: 11, color: G.textMuted, width: "100%" }}
                />
                {nf.backImg && (
                  <img
                    src={nf.backImg}
                    alt="Preview"
                    style={{
                      height: 36,
                      borderRadius: 4,
                      objectFit: "contain",
                    }}
                  />
                )}
              </div>
            </div>
            <div>
              <div
                style={{ fontSize: 11.5, color: G.textMuted, marginBottom: 4 }}
              >
                Back Text (formula / answer)
              </div>
              <textarea
                value={nf.back}
                onChange={(e) => setNf((n) => ({ ...n, back: e.target.value }))}
                placeholder="e.g. X(f) = ∫ x(t)e^(−j2πft) dt"
                rows={3}
                style={{ ...IS, resize: "none", fontFamily: "monospace" }}
              />
            </div>
            <button
              onClick={addCard}
              style={{ ...BS(G.amber), padding: "9px 16px", width: "100%" }}
            >
              Add Card
            </button>
          </div>
        </Modal>
      )}

      {viewImage && (
        <Modal
          title="🔍 Image Preview"
          onClose={() => setViewImage(null)}
          accent={G.amber}
          wide
        >
          <img
            src={viewImage}
            alt="Enlarged flashcard visual"
            style={{
              display: "block",
              width: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: 10,
            }}
          />
        </Modal>
      )}
    </div>
  );
};

/* ── SIDEBAR ─────────────────────────────────────────────── */
/* ── SIDEBAR ─────────────────────────────────────────────── */
const NAV = [
  { id: "dashboard", icon: "◈", label: "Dashboard" },
  { id: "subjects", icon: "⊟", label: "Subjects" },
  { id: "revqueue", icon: "🔁", label: "Revision Queue" },
  { id: "planner", icon: "◫", label: "Planner" },
  { id: "dailyplan", icon: "📋", label: "Daily Plan" },
  { id: "flashcards", icon: "🃏", label: "Flash Cards" },
  { id: "pyqmatrix", icon: "📊", label: "PYQ Matrix" },
  { id: "tests", icon: "◎", label: "Tests" },
  { id: "errors", icon: "🐛", label: "Error Log" },
  { id: "qnotes", icon: "⚡", label: "Quick Notes" },
  { id: "routine", icon: "◷", label: "Routine" },
  { id: "insights", icon: "📈", label: "Insights" },
  { id: "achievements", icon: "🏆", label: "Achievements" },
  { id: "predictor", icon: "🎓", label: "College Predictor" },
  { id: "topicmap", icon: "🕸", label: "Topic Map" },
];
/* ── COLLEGE / PSU PREDICTOR PAGE ──────────────────────── */
const CollegePredictorPage = ({ readinessScore, tests }) => {
  const testAvg = tests.length
    ? Math.round(
        tests.reduce((a, t) => a + Math.round((t.score / t.total) * 100), 0) /
          tests.length
      )
    : 0;
  const predicted = predictGATEScore(readinessScore, testAvg, []);
  const [inputScore, setInputScore] = useState(String(predicted.score));
  const [filterType, setFilterType] = useState("All");

  const score = Math.max(0, Math.min(100, parseFloat(inputScore) || 0));
  const qualifying = COLLEGE_DATA.filter(
    (c) =>
      score >= c.minScore && (filterType === "All" || c.type === filterType)
  ).sort((a, b) => b.minScore - a.minScore);
  const nearMiss = COLLEGE_DATA.filter(
    (c) =>
      score < c.minScore &&
      score >= c.minScore - 8 &&
      (filterType === "All" || c.type === filterType)
  ).sort((a, b) => a.minScore - b.minScore);

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: G.textPrimary,
            margin: 0,
          }}
        >
          🎓 College & PSU Predictor
        </h1>
        <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
          Based on historical GATE ECE cutoffs · General category · Approximate
        </p>
      </div>

      <GlassCard accent={G.violet} style={{ padding: 20, marginBottom: 18 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              GATE Score (out of 100)
            </div>
            <input
              type="number"
              min="0"
              max="100"
              value={inputScore}
              onChange={(e) => setInputScore(e.target.value)}
              style={{
                ...IS,
                width: 110,
                fontSize: 26,
                fontWeight: 900,
                textAlign: "center",
                color: G.violet,
                padding: "8px",
              }}
            />
            <div style={{ fontSize: 10, color: G.textDim, marginTop: 4 }}>
              Auto-filled from your readiness score
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              {[
                {
                  l: "Predicted Score",
                  v: `${predicted.score}/100`,
                  c: G.violet,
                },
                {
                  l: "Score Range",
                  v: `${predicted.low}–${predicted.high}`,
                  c: G.cyan,
                },
                { l: "Est. Rank", v: predicted.rank, c: G.amber },
              ].map((s) => (
                <div
                  key={s.l}
                  style={{
                    padding: "8px 14px",
                    background: "rgba(8,14,32,0.75)",
                    borderRadius: 10,
                    border: `1px solid ${G.glassBorder}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 9.5,
                      color: G.textMuted,
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {s.l}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: s.c }}>
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["All", "IIT", "NIT", "IIIT", "PSU"].map((t) => (
                <button
                  key={t}
                  onClick={() => setFilterType(t)}
                  style={{
                    ...BS(TYPE_COLORS[t] || G.violet, filterType === t),
                    padding: "5px 14px",
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      </GlassCard>

      {qualifying.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: G.emerald,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 10,
            }}
          >
            ✅ Likely to Qualify — {qualifying.length} options at score {score}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))",
              gap: 9,
            }}
          >
            {qualifying.map((c, i) => (
              <GlassCard
                key={i}
                accent={TYPE_COLORS[c.type]}
                style={{ padding: "13px 16px" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 800,
                        color: G.textPrimary,
                      }}
                    >
                      {c.name}
                    </div>
                    <div
                      style={{ fontSize: 11, color: G.textMuted, marginTop: 2 }}
                    >
                      {c.dept}
                    </div>
                  </div>
                  <Tag text={c.type} color={TYPE_COLORS[c.type]} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { l: "Min Score", v: c.minScore, c: TYPE_COLORS[c.type] },
                    {
                      l: "Your Edge",
                      v: `+${(score - c.minScore).toFixed(0)}`,
                      c: G.emerald,
                    },
                    ...(c.typicalRank
                      ? [{ l: "~Rank", v: `~${c.typicalRank}`, c: G.cyan }]
                      : []),
                  ].map((s) => (
                    <div
                      key={s.l}
                      style={{
                        flex: 1,
                        padding: "6px 8px",
                        background: "rgba(8,14,32,0.7)",
                        borderRadius: 9,
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: G.textMuted,
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {s.l}
                      </div>
                      <div
                        style={{ fontSize: 16, fontWeight: 900, color: s.c }}
                      >
                        {s.v}
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}

      {nearMiss.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: G.orange,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 10,
            }}
          >
            🎯 Near Miss — Improve {(nearMiss[0]?.minScore - score).toFixed(0)}{" "}
            more points to unlock top option
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))",
              gap: 9,
            }}
          >
            {nearMiss.map((c, i) => (
              <GlassCard
                key={i}
                style={{ padding: "13px 16px", opacity: 0.72 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: G.textSecondary,
                      }}
                    >
                      {c.name}
                    </div>
                    <div style={{ fontSize: 11, color: G.textMuted }}>
                      {c.dept}
                    </div>
                  </div>
                  <Tag text={c.type} color={TYPE_COLORS[c.type]} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { l: "Min Score", v: c.minScore, c: G.orange },
                    {
                      l: "Need",
                      v: `+${(c.minScore - score).toFixed(0)}`,
                      c: G.rose,
                    },
                  ].map((s) => (
                    <div
                      key={s.l}
                      style={{
                        flex: 1,
                        padding: "6px 8px",
                        background: "rgba(8,14,32,0.7)",
                        borderRadius: 9,
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: G.textMuted,
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {s.l}
                      </div>
                      <div
                        style={{ fontSize: 16, fontWeight: 900, color: s.c }}
                      >
                        {s.v}
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}

      {qualifying.length === 0 && nearMiss.length === 0 && (
        <GlassCard style={{ padding: 44, textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🎓</div>
          <div style={{ color: G.textSecondary, fontSize: 14 }}>
            Enter your expected score above to see results.
          </div>
          <div style={{ color: G.textMuted, fontSize: 12, marginTop: 5 }}>
            Cutoffs are based on 2022–2024 GATE ECE trends (approximate).
          </div>
        </GlassCard>
      )}
    </div>
  );
};
/* ── CROSS-REFERENCE MAP PAGE ────────────────────────────── */
const CrossReferencePage = ({ subjects }) => {
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);

  const getTopicInfo = (name) => {
    for (const s of subjects) {
      const t = s.topics.find((t) => t.name === name);
      if (t) {
        const pct = Math.round(
          (TRACK_FIELDS.filter((f) => t[f]).length / TRACK_FIELDS.length) * 100
        );
        return { pct, confidence: t.confidence || 0, subjectColor: s.clr };
      }
    }
    return { pct: 0, confidence: 0, subjectColor: G.textDim };
  };

  const clusterPct = (cl) => {
    const total = cl.topics.reduce((a, tn) => a + getTopicInfo(tn).pct, 0);
    return Math.round(total / cl.topics.length);
  };

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: G.textPrimary,
            margin: 0,
          }}
        >
          🕸 Topic Cross-Reference Map
        </h1>
        <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
          Topics that co-appear in GATE ECE papers — master a cluster, score in
          bunches
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 10,
          marginBottom: 20,
        }}
      >
        {[
          { l: "Total Clusters", v: TOPIC_CLUSTERS.length, c: G.violet },
          {
            l: "Clusters ≥50%",
            v: TOPIC_CLUSTERS.filter((cl) => clusterPct(cl) >= 50).length,
            c: G.emerald,
          },
          {
            l: "Fully Complete",
            v: TOPIC_CLUSTERS.filter((cl) => clusterPct(cl) === 100).length,
            c: G.amber,
          },
        ].map((s) => (
          <GlassCard key={s.l} accent={s.c} style={{ padding: "13px 16px" }}>
            <div
              style={{
                fontSize: 10,
                color: G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {s.l}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 900,
                color: s.c,
                margin: "5px 0",
                textShadow: `0 0 18px ${s.c}55`,
              }}
            >
              {s.v}
            </div>
          </GlassCard>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(330px,1fr))",
          gap: 12,
        }}
      >
        {TOPIC_CLUSTERS.map((cl) => {
          const cp = clusterPct(cl);
          const isSelected = selected === cl.id;
          const topicsData = cl.topics.map((tn) => ({
            name: tn,
            ...getTopicInfo(tn),
          }));
          const weakest = [...topicsData]
            .filter((t) => t.pct < 100)
            .sort((a, b) => a.pct - b.pct)
            .slice(0, 2);

          return (
            <GlassCard
              key={cl.id}
              accent={cl.color}
              glow={isSelected}
              onClick={() => setSelected(isSelected ? null : cl.id)}
              style={{ padding: 16, cursor: "pointer" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 800,
                      color: G.textPrimary,
                    }}
                  >
                    {cl.name}
                  </div>
                  <div
                    style={{
                      fontSize: 10.5,
                      color: G.textMuted,
                      marginTop: 3,
                      lineHeight: 1.4,
                    }}
                  >
                    {cl.desc}
                  </div>
                </div>
                <div
                  style={{ textAlign: "right", marginLeft: 12, flexShrink: 0 }}
                >
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 900,
                      color: pctColor(cp),
                      textShadow: `0 0 14px ${pctColor(cp)}55`,
                    }}
                  >
                    {cp}%
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      marginTop: 3,
                    }}
                  >
                    {Array(cl.strength)
                      .fill(0)
                      .map((_, i) => (
                        <div
                          key={i}
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: cl.color,
                            boxShadow: `0 0 5px ${cl.color}`,
                          }}
                        />
                      ))}
                  </div>
                  <div style={{ fontSize: 9, color: G.textDim, marginTop: 1 }}>
                    strength
                  </div>
                </div>
              </div>

              <Bar pct={cp} color={cl.color} h={5} />

              <div
                style={{
                  marginTop: 11,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {topicsData.map((t) => (
                  <div
                    key={t.name}
                    onMouseEnter={() => setHovered(t.name)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "5px 8px",
                      borderRadius: 8,
                      background:
                        hovered === t.name
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(8,14,32,0.5)",
                      border: `1px solid ${
                        hovered === t.name ? cl.color + "44" : "transparent"
                      }`,
                      transition: "all 0.14s",
                    }}
                  >
                    <div
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: pctColor(t.pct),
                        flexShrink: 0,
                        boxShadow: `0 0 5px ${pctColor(t.pct)}88`,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11.5,
                        color: G.textSecondary,
                        flex: 1,
                      }}
                    >
                      {t.name}
                    </span>
                    <Stars value={t.confidence} size={10} />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: pctColor(t.pct),
                        minWidth: 32,
                        textAlign: "right",
                      }}
                    >
                      {t.pct}%
                    </span>
                  </div>
                ))}
              </div>

              {isSelected && (
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 13px",
                    background: `${cl.color}18`,
                    borderRadius: 10,
                    border: `1px solid ${cl.color}33`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 800,
                      color: cl.color,
                      marginBottom: 6,
                    }}
                  >
                    📌 Study Strategy
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: G.textSecondary,
                      lineHeight: 1.5,
                    }}
                  >
                    {cp < 30 &&
                      "Start from the first topic in the chain — each one builds on the previous. Don't skip ahead."}
                    {cp >= 30 &&
                      cp < 70 &&
                      "Good start! Topics below 50% are your bottleneck — a partial cluster gives partial marks."}
                    {cp >= 70 &&
                      "🔥 Strong cluster! Revise all topics together in one session to cement cross-topic connections."}
                  </div>
                  {weakest.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div
                        style={{
                          fontSize: 10,
                          color: G.textDim,
                          marginBottom: 4,
                        }}
                      >
                        Focus next on:
                      </div>
                      {weakest.map((t) => (
                        <div
                          key={t.name}
                          style={{
                            fontSize: 11.5,
                            color: G.rose,
                            fontWeight: 700,
                          }}
                        >
                          → {t.name} ({t.pct}% done)
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </GlassCard>
          );
        })}
      </div>
    </div>
  );
};
const Sidebar = ({
  page,
  setPage,
  subjects,
  errors,
  onBackup,
  onRestoreClick,
  readinessScore,
  streak,
  isOpen,
  setIsOpen,
  profile,
  setShowSettings,
}) => {
  const pct = overallPct(subjects),
    clr = pctColor(pct);
  const dueCount = subjects.reduce(
    (a, s) => a + s.topics.filter(isDueToday).length,
    0
  );
  const pendingErr = errors.filter((e) => !e.resolved).length;

  return (
    <>
      {/* Mobile Overlay Background */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            zIndex: 998,
          }}
        />
      )}
      <div
        className={`sidebar-container ${isOpen ? "open" : ""}`}
        style={{
          width: 232,
          background: "rgba(5,8,20,0.94)",
          borderRight: `1px solid ${G.glassBorder}`,
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          overflowY: "auto",
          backdropFilter: "blur(24px) saturate(200%)",
        }}
      >
        <div
          style={{
            padding: "20px 16px 16px",
            borderBottom: `1px solid ${G.glassBorder}`,
          }}
        >
          <div
            onClick={() => setShowSettings(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 16,
              cursor: "pointer",
              padding: "6px",
              borderRadius: 12,
              transition: "background 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.background = "rgba(179,157,255,0.08)")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            {profile.photo ? (
              <img
                src={profile.photo}
                alt="Profile"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  objectFit: "cover",
                  boxShadow: "0 4px 16px rgba(179,157,255,0.4)",
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "linear-gradient(135deg,#b39dff,#5ecfff)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  fontWeight: 900,
                  boxShadow: "0 4px 16px rgba(179,157,255,0.4)",
                  flexShrink: 0,
                }}
              >
                {profile.name ? profile.name.charAt(0).toUpperCase() : "G"}
              </div>
            )}
            <div style={{ flex: 1, overflow: "hidden" }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: G.textPrimary,
                  letterSpacing: "-0.01em",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {profile.name || "GATE Aspirant"}
              </div>
              <div style={{ fontSize: 10, color: G.textMuted }}>
                Target:{" "}
                {new Date(profile.examDate + "T12:00:00").toLocaleDateString(
                  "en-IN",
                  {
                    month: "short",
                    year: "numeric",
                  }
                )}{" "}
                ⚙️
              </div>
            </div>
          </div>
          <GlassCard style={{ padding: "11px 14px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 10.5,
                marginBottom: 7,
              }}
            >
              <span style={{ color: G.textMuted, fontWeight: 600 }}>
                OVERALL PROGRESS
              </span>
              <span
                style={{
                  color: clr,
                  fontWeight: 900,
                  textShadow: `0 0 12px ${clr}66`,
                }}
              >
                {pct}%
              </span>
            </div>
            <Bar pct={pct} color={clr} h={5} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 8,
              }}
            >
              <span style={{ fontSize: 10.5, color: G.textMuted }}>
                Readiness
              </span>
              <span
                style={{ fontSize: 10.5, color: G.violet, fontWeight: 700 }}
              >
                {readinessScore}%
              </span>
            </div>
            <Bar pct={readinessScore} color={G.violet} h={3} />
            <div
              style={{
                fontSize: 10.5,
                color: G.amber,
                marginTop: 6,
                fontWeight: 700,
              }}
            >
              🔥 {streak}-day streak
            </div>
          </GlassCard>
        </div>
        <nav style={{ flex: 1, padding: "6px 0" }}>
          {NAV.map((n) => {
            const active = page === n.id;
            const badge =
              n.id === "revqueue" && dueCount > 0
                ? dueCount
                : n.id === "errors" && pendingErr > 0
                ? pendingErr
                : 0;
            return (
              <div
                key={n.id}
                onClick={() => {
                  setPage(n.id);
                  setIsOpen(false);
                }}
                className="nav-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "9px 8px 9px 16px",
                  margin: "1px 8px",
                  cursor: "pointer",
                  borderRadius: 10,
                  borderLeft: "none",
                  position: "relative",
                  background: active ? "rgba(179,157,255,0.1)" : "transparent",
                  color: active ? G.textPrimary : G.textSecondary,
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 500,
                }}
              >
                {active && (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: "18%",
                      bottom: "18%",
                      width: 3,
                      borderRadius: 99,
                      background: G.violet,
                      boxShadow: `0 0 8px ${G.violet}99`,
                    }}
                  />
                )}

                <span
                  style={{
                    fontSize: 13,
                    color: active ? G.violet : G.textDim,
                    width: 18,
                    textAlign: "center",
                  }}
                >
                  {n.icon}
                </span>
                <span>{n.label}</span>
                {badge > 0 && (
                  <span
                    style={{
                      marginLeft: "auto",
                      background:
                        n.id === "revqueue" ? `${G.pink}33` : `${G.rose}33`,
                      color: n.id === "revqueue" ? G.pink : G.rose,
                      fontSize: 10,
                      fontWeight: 800,
                      borderRadius: 99,
                      padding: "2px 7px",
                    }}
                  >
                    {badge}
                  </span>
                )}
                {active && !badge && (
                  <div
                    style={{
                      marginLeft: "auto",
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: G.violet,
                      boxShadow: `0 0 8px ${G.violet}`,
                    }}
                  />
                )}
              </div>
            );
          })}
        </nav>
        <div
          style={{
            padding: "12px 16px",
            borderTop: `1px solid ${G.glassBorder}`,
          }}
        >
          <div style={{ display: "flex", gap: 7 }}>
            <button
              onClick={onBackup}
              style={{
                flex: 1,
                padding: "7px 4px",
                background: `${G.emerald}22`,
                border: `1px solid ${G.emerald}44`,
                borderRadius: 9,
                color: G.emerald,
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "inherit",
              }}
            >
              ⬇ Backup
            </button>
            <button
              onClick={onRestoreClick}
              style={{
                flex: 1,
                padding: "7px 4px",
                background: `${G.cyan}22`,
                border: `1px solid ${G.cyan}44`,
                borderRadius: 9,
                color: G.cyan,
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "inherit",
              }}
            >
              ⬆ Restore
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
/* ── SETTINGS MODAL ────────────────────────────────────── */
const SettingsModal = ({ profile, setProfile, onClose }) => {
  const [temp, setTemp] = useState(profile);
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await compressImageForStorage(file, 400, 0.8);
      setTemp((t) => ({ ...t, photo: dataUrl }));
    } catch (err) {}
  };
  return (
    <Modal title="⚙️ Profile Settings" onClose={onClose} accent={G.cyan}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {temp.photo ? (
            <img
              src={temp.photo}
              alt="Preview"
              style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                objectFit: "cover",
                border: `2px solid ${G.cyan}`,
              }}
            />
          ) : (
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                background: `${G.cyan}22`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
                border: `2px dashed ${G.cyan}66`,
              }}
            >
              👤
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div
              style={{ fontSize: 11.5, color: G.textMuted, marginBottom: 6 }}
            >
              Profile Picture
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ ...IS, padding: "6px" }}
            />
          </div>
          {temp.photo && (
            <button
              onClick={() => setTemp({ ...temp, photo: "" })}
              style={{ ...BS(G.rose), padding: "8px 12px", marginTop: 20 }}
            >
              Remove
            </button>
          )}
        </div>
        <div>
          <div style={{ fontSize: 11.5, color: G.textMuted, marginBottom: 4 }}>
            Display Name
          </div>
          <input
            value={temp.name}
            onChange={(e) => setTemp({ ...temp, name: e.target.value })}
            style={IS}
            placeholder="Your Name"
          />
        </div>
        <div>
          <div style={{ fontSize: 11.5, color: G.textMuted, marginBottom: 4 }}>
            Target GATE / Exam Date
          </div>
          <input
            type="date"
            value={temp?.examDate || ""}
            onChange={(e) => setTemp({ ...temp, examDate: e.target.value })}
            style={IS}
          />
        </div>

        <button
          onClick={() => {
            setProfile(temp);
            onClose();
          }}
          style={{
            ...BS(G.cyan, true),
            padding: "12px",
            fontSize: 14,
            marginTop: 8,
          }}
        >
          Save Changes
        </button>
      </div>
    </Modal>
  );
};
/* ══════════════════════════════════════
   DASHBOARD
══════════════════════════════════════ */
const Dashboard = ({
  subjects,
  tests,
  goals,
  routine,
  errors,
  readinessScore,
  streak,
  pomodoroLog,
  flashcards,
  setGoals,
  addToast,
  setPage,
  profile,
}) => {
  const overall = overallPct(subjects);
  const total = subjects.reduce((a, s) => a + s.topics.length, 0);
  const lecDone = subjects.reduce(
    (a, s) => a + s.topics.filter((t) => t.lec).length,
    0
  );
  const pyqDone = subjects.reduce(
    (a, s) => a + s.topics.filter((t) => t.pyq).length,
    0
  );
  const rDone = routine.filter((r) => r.done).length;
  const dueToday = subjects.reduce(
    (a, s) => a + s.topics.filter(isDueToday).length,
    0
  );
  const recs = getRecommendations(subjects);
  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const testAvg = tests.length
    ? Math.round(
        tests.reduce((a, t) => a + Math.round((t.score / t.total) * 100), 0) /
          tests.length
      )
    : null;
  const dt = calcDailyTarget(subjects, profile.examDate);
  const [showStudyOrder, setShowStudyOrder] = useState(false);

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      {showStudyOrder && (
        <StudyOrderModal
          subjects={subjects}
          onClose={() => setShowStudyOrder(false)}
        />
      )}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: 11,
            color: G.textSecondary,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            marginBottom: 3,
          }}
        >
          {today}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h1
            style={{
              fontSize: 26,
              fontWeight: 900,
              color: G.textPrimary,
              margin: 0,
              letterSpacing: "-0.02em",
              background: `linear-gradient(135deg,${G.violet},${G.cyan})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Preparation Dashboard
          </h1>
          <button
            onClick={() => setShowStudyOrder(true)}
            style={{ ...BS(G.cyan), fontSize: 11 }}
          >
            📐 Study Order
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <GATECountdown examDateStr={profile.examDate} />
      </div>

      <GlassCard
        accent={dt.onTrack ? G.emerald : G.rose}
        style={{ padding: "14px 20px", marginBottom: 14 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <div
              style={{
                fontSize: 10.5,
                color: G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 4,
              }}
            >
              📊 Daily Target Engine
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: G.textSecondary,
                lineHeight: 1.7,
              }}
            >
              You have{" "}
              <strong style={{ color: G.amber }}>{dt.daysLeft} days</strong> to
              GATE. Completed{" "}
              <strong style={{ color: pctColor(dt.pct) }}>
                {dt.completedTopics}/{dt.totalTopics} topics
              </strong>{" "}
              ({dt.pct}%). At current pace of{" "}
              <strong style={{ color: G.cyan }}>
                {dt.pacePerDay} topics/day
              </strong>
              , you'd finish in{" "}
              <strong style={{ color: dt.onTrack ? G.emerald : G.rose }}>
                {dt.daysAtPace < 9999 ? `${dt.daysAtPace} days` : "unknown"}
              </strong>
              . You need{" "}
              <strong style={{ color: G.amber }}>
                {dt.neededPerDay} topics/day
              </strong>{" "}
              to finish before GATE.
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 9.5,
                color: G.textMuted,
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              Status
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 900,
                color: dt.onTrack ? G.emerald : G.rose,
                textShadow: `0 0 14px ${dt.onTrack ? G.emerald : G.rose}55`,
              }}
            >
              {dt.onTrack ? "✅ On Track" : "⚠ Behind Pace"}
            </div>
          </div>
        </div>
      </GlassCard>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 295px",
          gap: 14,
          marginBottom: 14,
        }}
      >
        <GlassCard accent={G.violet} style={{ padding: 18 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            🎯 Exam Readiness Score
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 18,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                position: "relative",
                width: 88,
                height: 88,
                flexShrink: 0,
              }}
            >
              <svg width={88} height={88} viewBox="0 0 88 88">
                <circle
                  cx={44}
                  cy={44}
                  r={36}
                  fill="none"
                  stroke={`${G.violet}1a`}
                  strokeWidth={8}
                />
                <circle
                  cx={44}
                  cy={44}
                  r={36}
                  fill="none"
                  stroke={G.violet}
                  strokeWidth={8}
                  strokeDasharray={2 * Math.PI * 36}
                  strokeDashoffset={
                    2 * Math.PI * 36 * (1 - readinessScore / 100)
                  }
                  strokeLinecap="round"
                  transform="rotate(-90 44 44)"
                  style={{
                    filter: `drop-shadow(0 0 10px ${G.violet}88)`,
                    transition: "stroke-dashoffset 0.8s ease",
                  }}
                />
                <text
                  x={44}
                  y={49}
                  textAnchor="middle"
                  fontSize={16}
                  fontWeight="900"
                  fill={G.violet}
                >
                  {readinessScore}%
                </text>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              {[
                { l: "Study Progress", v: `${overall}%`, c: G.emerald },
                {
                  l: "Test Performance",
                  v: testAvg !== null ? `${testAvg}%` : "—",
                  c: G.amber,
                },
                {
                  l: "Error Resolution",
                  v: errors.length
                    ? `${Math.round(
                        (errors.filter((e) => e.resolved).length /
                          errors.length) *
                          100
                      )}%`
                    : "—",
                  c: G.cyan,
                },
                { l: "Deep Study Streak", v: `${streak} days`, c: G.pink },
              ].map((s) => (
                <div
                  key={s.l}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 7,
                  }}
                >
                  <span style={{ fontSize: 11.5, color: G.textSecondary }}>
                    {s.l}
                  </span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: s.c }}>
                    {s.v}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 8,
            }}
          >
            {[
              {
                l: "Overall",
                v: `${overall}%`,
                c: pctColor(overall),
                spark: subjects.map((s) => subjPct(s)),
              },
              {
                l: "Lectures",
                v: `${lecDone}/${total}`,
                c: G.emerald,
                spark: subjects.map(
                  (s) => s.topics.filter((t) => t.lec).length
                ),
              },
              {
                l: "PYQs",
                v: `${pyqDone}/${total}`,
                c: G.amber,
                spark: subjects.map(
                  (s) => s.topics.filter((t) => t.pyq).length
                ),
              },
              {
                l: "Due Today",
                v: dueToday,
                c: dueToday > 0 ? G.pink : G.emerald,
                spark: null,
              },
            ].map((s) => (
              <div
                key={s.l}
                style={{
                  background: "rgba(8,14,32,0.7)",
                  borderRadius: 10,
                  padding: "9px 11px",
                  border: `1px solid ${G.glassBorder}`,
                }}
              >
                <div
                  style={{
                    fontSize: 9.5,
                    color: G.textMuted,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    marginBottom: 3,
                  }}
                >
                  {s.l}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 900,
                      color: s.c,
                      textShadow: `0 0 14px ${s.c}44`,
                    }}
                  >
                    {s.v}
                  </div>
                  {s.spark && <Spark data={s.spark} color={s.c} />}
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
        <GlassCard accent={G.cyan} style={{ padding: 16 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 6,
            }}
          >
            📡 Subject Radar
          </div>
          <RadarChart subjects={subjects} />
        </GlassCard>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 350px",
          gap: 14,
          marginBottom: 14,
        }}
      >
        <GlassCard style={{ padding: 18 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            Subject Progress
          </div>
          {subjects.map((s) => {
            const p = subjPct(s),
              fd = s.topics.filter((t) =>
                TRACK_FIELDS.every((f) => t[f])
              ).length;
            return (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    background: s.clr,
                    flexShrink: 0,
                    boxShadow: `0 0 8px ${s.clr}88`,
                  }}
                />
                <div
                  style={{
                    width: 195,
                    fontSize: 12,
                    color: G.textSecondary,
                    flexShrink: 0,
                    fontWeight: 500,
                  }}
                >
                  {s.name}
                </div>
                <div style={{ flex: 1 }}>
                  <Bar pct={p} color={s.clr} h={5} />
                </div>
                <div
                  style={{
                    width: 36,
                    fontSize: 12,
                    fontWeight: 800,
                    color: s.clr,
                    textAlign: "right",
                    flexShrink: 0,
                    textShadow: `0 0 10px ${s.clr}55`,
                  }}
                >
                  {p}%
                </div>
                <Tag text={s.wt} color={s.clr} />
                <div
                  style={{
                    width: 42,
                    fontSize: 10.5,
                    color: G.textDim,
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  {fd}/{s.topics.length}
                </div>
              </div>
            );
          })}
        </GlassCard>
        <GlassCard accent={G.violet} style={{ padding: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 14,
            }}
          >
            <span style={{ fontSize: 17 }}>🧠</span>
            <div>
              <div
                style={{ fontSize: 12, fontWeight: 700, color: G.textPrimary }}
              >
                Smart Recommendations
              </div>
              <div style={{ fontSize: 10, color: G.textMuted }}>
                ROI × Difficulty × PYQ frequency
              </div>
            </div>
          </div>
          {recs.length === 0 ? (
            <div
              style={{
                fontSize: 13,
                color: G.emerald,
                textAlign: "center",
                padding: "16px 0",
              }}
            >
              🎉 All topics complete!
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {recs.map((r, i) => (
                <div
                  key={`${r.subject}${r.topic}`}
                  style={{
                    padding: "9px 11px",
                    background: "rgba(8,14,32,0.7)",
                    borderRadius: 10,
                    border: `1px solid ${r.color}2a`,
                    borderLeft: `3px solid ${r.color}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        color: r.color,
                        background: `${r.color}22`,
                        borderRadius: 99,
                        padding: "1px 7px",
                        flexShrink: 0,
                      }}
                    >
                      #{i + 1}
                    </span>
                    <span
                      style={{
                        fontSize: 12.5,
                        color: G.textPrimary,
                        fontWeight: 600,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.topic}
                    </span>
                    <DiffStars value={r.difficulty} />
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                  >
                    <Tag
                      text={
                        r.subject.length > 14
                          ? r.subject.slice(0, 14) + "…"
                          : r.subject
                      }
                      color={r.color}
                    />
                    <span style={{ fontSize: 9, color: G.textDim }}>
                      PYQ:{r.pyqFreq}
                    </span>
                    <span
                      style={{
                        fontSize: 10.5,
                        color: G.cyan,
                        fontWeight: 700,
                        marginLeft: "auto",
                      }}
                    >
                      → {r.next}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}
      >
        <GlassCard style={{ padding: 16 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 10,
            }}
          >
            📋 Today's Routine
          </div>
          <Bar
            pct={
              routine.length ? Math.round((rDone / routine.length) * 100) : 0
            }
            color={G.emerald}
          />
          <div style={{ fontSize: 11, color: G.textDim, margin: "5px 0 10px" }}>
            {rDone}/{routine.length} completed
          </div>
          {routine.slice(0, 6).map((r) => (
            <div
              key={r.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontSize: 12,
                marginBottom: 5,
              }}
            >
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: r.done ? G.emerald : G.textDim,
                  flexShrink: 0,
                  boxShadow: r.done ? `0 0 8px ${G.emerald}88` : "none",
                }}
              />
              <span
                style={{
                  color: r.done ? G.textDim : G.textSecondary,
                  textDecoration: r.done ? "line-through" : "none",
                }}
              >
                {r.time} — {r.task}
              </span>
            </div>
          ))}
        </GlassCard>
        <GlassCard style={{ padding: 16 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 10,
            }}
          >
            🎯 Active Goals
          </div>
          {goals.filter((g) => !g.done).length === 0 ? (
            <div style={{ fontSize: 12.5, color: G.textDim }}>
              No goals yet — go to Planner!
            </div>
          ) : (
            goals
              .filter((g) => !g.done)
              .slice(0, 5)
              .map((g) => (
                <div
                  key={g.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "6px 9px",
                    background: "rgba(8,14,32,0.7)",
                    borderRadius: 9,
                    border: `1px solid ${G.glassBorder}`,
                    marginBottom: 6,
                  }}
                >
                  <Tag
                    text={g.type === "long" ? "LT" : "ST"}
                    color={g.type === "long" ? G.purple : G.violet}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      color: G.textSecondary,
                      flex: 1,
                      fontWeight: 500,
                    }}
                  >
                    {g.text}
                  </span>
                  {g.date && (
                    <span style={{ fontSize: 10, color: G.textDim }}>
                      {g.date}
                    </span>
                  )}
                </div>
              ))
          )}
        </GlassCard>
        <GlassCard accent={G.amber} style={{ padding: 16 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 10,
            }}
          >
            🃏 Flash Cards
          </div>
          <div
            style={{
              fontSize: 26,
              fontWeight: 900,
              color: G.amber,
              textShadow: `0 0 18px ${G.amber}55`,
            }}
          >
            {flashcards.filter((f) => f.known).length}/{flashcards.length}
          </div>
          <div style={{ fontSize: 11, color: G.textMuted, marginBottom: 8 }}>
            known
          </div>
          <Bar
            pct={
              flashcards.length
                ? Math.round(
                    (flashcards.filter((f) => f.known).length /
                      flashcards.length) *
                      100
                  )
                : 0
            }
            color={G.amber}
            h={4}
          />
          <div
            style={{
              fontSize: 11,
              color: G.pink,
              marginTop: 6,
              fontWeight: 700,
            }}
          >
            {flashcards.filter((f) => !f.due || f.due <= todayS()).length} due
            today
          </div>
          <button
            onClick={() => setPage("flashcards")}
            style={{
              ...BS(G.amber),
              fontSize: 11,
              marginTop: 8,
              width: "100%",
              padding: "6px",
            }}
          >
            Review Cards →
          </button>
        </GlassCard>
      </div>
    </div>
  );
};

/* ── TOPIC TABLE — defined at module level so SubjectsPage re-renders
   don't recreate this function, which would cause React to unmount and
   remount the DOM (caret-jump bug on every keystroke).               */
const TopicTable = ({
  subj,
  errors,
  editNote,
  noteVal,
  setNoteVal,
  setEditNote,
  saveNote,
  editUrl,
  urlVal,
  setUrlVal,
  setEditUrl,
  saveLectureUrl,
  toggle,
  setConf,
  delTopic,
}) => (
  <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
    <div style={{ minWidth: 680 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 80px 60px repeat(7,44px) 28px",
          padding: "8px 18px",
          borderBottom: `1px solid ${G.glassBorder}`,
          gap: 3,
          alignItems: "center",
        }}
      >
        {["TOPIC", "STATUS", "CONF", ...COLS.map((c) => c.label), ""].map(
          (h, i) => (
            <div
              key={i}
              style={{
                fontSize: 9.5,
                color:
                  i < 3 ? G.textSecondary : COLS[i - 3]?.color || G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
                textAlign: i > 2 ? "center" : "left",
              }}
            >
              {h}
            </div>
          )
        )}
      </div>
      {subj.topics.map((t, ti) => {
        const st = topicStatus(t),
          isEN = editNote?.sid === subj.id && editNote?.tid === t.id;
        const rev = getRevStatus(t);
        const tErr = errors.filter(
          (e) => e.subject === subj.name && e.topic === t.name && !e.resolved
        ).length;
        const freq = PYQ_FREQ[t.name]
          ? PYQ_FREQ[t.name].reduce((a, b) => a + b, 0)
          : 0;
        return (
          <div key={t.id}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 80px 60px repeat(7,44px) 28px",
                padding: "12px 18px",
                borderBottom: `1px solid ${G.glassBorder}44`,
                gap: 3,
                alignItems: "center",
                background: ti % 2 === 0 ? "transparent" : "rgba(8,14,32,0.5)",
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span
                    style={{
                      fontSize: 12.5,
                      color: G.textSecondary,
                      fontWeight: 500,
                    }}
                  >
                    {t.name}
                  </span>
                  <DiffStars value={TOPIC_DIFFICULTY[t.name] || 3} />
                  {tErr > 0 && (
                    <span
                      style={{
                        fontSize: 9.5,
                        background: `${G.rose}22`,
                        color: G.rose,
                        borderRadius: 99,
                        padding: "1px 5px",
                        fontWeight: 700,
                      }}
                    >
                      🐛{tErr}
                    </span>
                  )}
                  {rev && rev.diff <= 0 && (
                    <span
                      style={{
                        fontSize: 9.5,
                        background: `${G.pink}22`,
                        color: G.pink,
                        borderRadius: 99,
                        padding: "1px 5px",
                        fontWeight: 700,
                      }}
                    >
                      🔁 due
                    </span>
                  )}
                  {rev && rev.diff > 0 && rev.diff <= 3 && (
                    <span
                      style={{
                        fontSize: 9.5,
                        color: G.orange,
                        fontWeight: 600,
                      }}
                    >
                      +{rev.diff}d
                    </span>
                  )}
                </div>
                {freq > 0 && (
                  <span
                    style={{
                      fontSize: 9,
                      color: G.textDim,
                      display: "block",
                      marginTop: 2,
                    }}
                  >
                    PYQ freq: {freq}
                  </span>
                )}
                {t.notes && !isEN && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#9ab4d8",
                      marginTop: 6,
                      background: "rgba(255,255,255,0.05)",
                      padding: "6px 8px",
                      borderRadius: 6,
                      fontStyle: "italic",
                      borderLeft: `2px solid ${subj.clr}`,
                    }}
                  >
                    {t.notes}
                  </div>
                )}
                {!isEN && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      onClick={() => {
                        setEditNote({ sid: subj.id, tid: t.id });
                        setNoteVal(t.notes);
                      }}
                      style={{
                        background: t.notes ? "none" : "rgba(179,157,255,0.1)",
                        border: t.notes ? "none" : `1px solid ${G.violet}55`,
                        color: G.violet,
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 10,
                        padding: t.notes ? "0" : "3px 8px",
                        fontFamily: "inherit",
                        fontWeight: 700,
                      }}
                    >
                      {t.notes ? "✏ Edit Note" : "📝 Write Note"}
                    </button>
                    {t.lectureUrl ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <a
                          href={t.lectureUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: 10.5,
                            color: G.cyan,
                            textDecoration: "none",
                            fontWeight: 700,
                          }}
                        >
                          🎬 Watch ↗
                        </a>
                        <button
                          onClick={() => {
                            setEditUrl({ sid: subj.id, tid: t.id });
                            setUrlVal(t.lectureUrl);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            color: G.textDim,
                            cursor: "pointer",
                            fontSize: 10,
                            padding: 0,
                            fontFamily: "inherit",
                          }}
                        >
                          ✏
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditUrl({ sid: subj.id, tid: t.id });
                          setUrlVal("");
                        }}
                        style={{
                          background: `${G.cyan}18`,
                          border: `1px solid ${G.cyan}44`,
                          color: G.cyan,
                          borderRadius: 6,
                          cursor: "pointer",
                          fontSize: 10,
                          padding: "3px 8px",
                          fontFamily: "inherit",
                          fontWeight: 700,
                        }}
                      >
                        🎬 Add Lecture
                      </button>
                    )}
                  </div>
                )}
                {editUrl?.sid === subj.id && editUrl?.tid === t.id && (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      marginTop: 7,
                      alignItems: "center",
                    }}
                  >
                    <input
                      value={urlVal}
                      onChange={(e) => setUrlVal(e.target.value)}
                      placeholder="YouTube / NPTEL / Unacademy URL…"
                      style={{ ...IS, flex: 1, fontSize: 11 }}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveLectureUrl();
                        if (e.key === "Escape") setEditUrl(null);
                      }}
                    />
                    <button
                      onClick={saveLectureUrl}
                      style={{
                        ...BS(G.cyan, true),
                        fontSize: 11,
                        padding: "5px 12px",
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditUrl(null)}
                      style={{
                        ...BS(G.textMuted),
                        fontSize: 11,
                        padding: "5px 9px",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
              <Tag text={st.label} color={st.color} squared />
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Stars
                  value={t.confidence}
                  onChange={(v) => setConf(subj.id, t.id, v)}
                />
              </div>
              {COLS.map((c) => (
                <div
                  key={c.key}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <CB
                    checked={t[c.key]}
                    onChange={() => toggle(subj.id, t.id, c.key)}
                    color={c.color}
                  />
                </div>
              ))}
              <button
                onClick={() => delTopic(subj.id, t.id)}
                style={{
                  background: "none",
                  border: "none",
                  color: G.textDim,
                  cursor: "pointer",
                  fontSize: 16,
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>
            {isEN && (
              <div
                style={{
                  padding: "12px 18px",
                  borderBottom: `1px solid ${G.glassBorder}`,
                  background: "rgba(8,14,32,0.85)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <textarea
                  value={noteVal}
                  onChange={(e) => setNoteVal(e.target.value)}
                  placeholder="Write key takeaways or notes about this topic..."
                  style={{ ...IS, flex: 1, minHeight: 60, resize: "vertical" }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      saveNote();
                    }
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => setEditNote(null)}
                    style={{ ...BS(G.textMuted), fontSize: 11 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveNote}
                    style={{ ...BS(subj.clr, true), fontSize: 11 }}
                  >
                    Save Note
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

/* ── SUBJECTS PAGE (with split view & explicit notes) ───── */
const SubjectsPage = ({
  subjects,
  setSubjects,
  errors,
  setPage,
  addToast,
  goals,
  setGoals,
  profile,
}) => {
  const [expanded, setExpanded] = useState({});
  const [splitSubj, setSplitSubj] = useState(null);
  const [adding, setAdding] = useState(null);
  const [newTopic, setNewTopic] = useState("");
  const [editNote, setEditNote] = useState(null);
  const [noteVal, setNoteVal] = useState("");
  const [editUrl, setEditUrl] = useState(null);
  const [urlVal, setUrlVal] = useState("");
  const [splitView, setSplitView] = useState(false);

  /* toggle() — checking a box stamps today's date for that stage
     (as before). Unchecking now CASCADE-CLEARS this stage's date
     AND every later stage + its date. This fixes two bugs:
       1) Unchecking "Lec" left `studiedDate` set, so the Revision
          Forecast kept showing Rev1/2/3 entries for a topic that
          is no longer marked as lectured.
       2) Unchecking an earlier revision stage left later stages'
          dates dangling, which could produce inconsistent/duplicate
          forecast entries.                                        */
  const toggle = (sid, tid, field) =>
    setSubjects((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              topics: s.topics.map((t) => {
                if (t.id !== tid) return t;
                const v = !t[field];
                const u = { [field]: v };
                if (v) {
                  if (field === "lec" && !t.studiedDate)
                    u.studiedDate = todayS();
                  if (field === "r1" && !t.r1Date) u.r1Date = todayS();
                  if (field === "r2" && !t.r2Date) u.r2Date = todayS();
                  if (field === "r3" && !t.r3Date) u.r3Date = todayS();
                } else {
                  if (field === "lec") {
                    u.studiedDate = null;
                    u.r1 = false;
                    u.r1Date = null;
                    u.r2 = false;
                    u.r2Date = null;
                    u.r3 = false;
                    u.r3Date = null;
                  } else if (field === "r1") {
                    u.r1Date = null;
                    u.r2 = false;
                    u.r2Date = null;
                    u.r3 = false;
                    u.r3Date = null;
                  } else if (field === "r2") {
                    u.r2Date = null;
                    u.r3 = false;
                    u.r3Date = null;
                  } else if (field === "r3") {
                    u.r3Date = null;
                  }
                }
                return { ...t, ...u };
              }),
            }
      )
    );

  const setConf = (sid, tid, v) =>
    setSubjects((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              topics: s.topics.map((t) =>
                t.id !== tid ? t : { ...t, confidence: v }
              ),
            }
      )
    );

  const addTopic = (sid) => {
    if (!newTopic.trim()) return;
    setSubjects((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              topics: [
                ...s.topics,
                {
                  id: uid(),
                  name: newTopic.trim(),
                  lec: false,
                  asgn: false,
                  pyq: false,
                  r1: false,
                  r2: false,
                  r3: false,
                  test: false,
                  notes: "",
                  confidence: 0,
                  studiedDate: null,
                  r1Date: null,
                  r2Date: null,
                  r3Date: null,
                  difficulty: 0,
                  timeSpent: 0,
                },
              ],
            }
      )
    );
    setNewTopic("");
    setAdding(null);
  };
  const delTopic = (sid, tid) =>
    setSubjects((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : { ...s, topics: s.topics.filter((t) => t.id !== tid) }
      )
    );

  const saveNote = () => {
    if (!editNote) return;
    setSubjects((prev) =>
      prev.map((s) =>
        s.id !== editNote.sid
          ? s
          : {
              ...s,
              topics: s.topics.map((t) =>
                t.id !== editNote.tid ? t : { ...t, notes: noteVal }
              ),
            }
      )
    );
    setEditNote(null);
    setNoteVal("");
  };

  const saveLectureUrl = () => {
    if (!editUrl) return;
    const raw = urlVal.trim();
    const url = raw && !raw.startsWith("http") ? "https://" + raw : raw;
    setSubjects((prev) =>
      prev.map((s) =>
        s.id !== editUrl.sid
          ? s
          : {
              ...s,
              topics: s.topics.map((t) =>
                t.id !== editUrl.tid ? t : { ...t, lectureUrl: url }
              ),
            }
      )
    );
    setEditUrl(null);
    setUrlVal("");
  };

  const activeSplit = splitView && splitSubj;
  const splitSubjData = subjects.find((s) => s.id === splitSubj);

  const tableProps = {
    errors,
    editNote,
    noteVal,
    setNoteVal,
    setEditNote,
    saveNote,
    editUrl,
    urlVal,
    setUrlVal,
    setEditUrl,
    saveLectureUrl,
    toggle,
    setConf,
    delTopic,
  };

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div
        style={{
          marginBottom: 22,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: G.textPrimary,
              margin: 0,
              letterSpacing: "-0.025em",
            }}
          >
            Subject Tracker
          </h1>
          <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
            Lecture · Assignment · PYQ · Rev 1 (7d) → Rev 2 (14d) → Rev 3 (30d)
            · Test
          </p>
        </div>
        <button
          onClick={() => setSplitView((v) => !v)}
          style={{
            ...BS(splitView ? G.violet : G.textMuted, splitView),
            padding: "7px 14px",
            fontSize: 11.5,
          }}
        >
          ⊞ Split View
        </button>
      </div>

      <RescueAlerts
        subjects={subjects}
        goals={goals}
        setGoals={setGoals}
        addToast={addToast}
        examDateStr={profile.examDate}
      />

      {activeSplit && splitSubjData ? (
        <div
          style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 14 }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {subjects.map((subj) => {
              const pct = subjPct(subj),
                isActive = splitSubj === subj.id;
              return (
                <div
                  key={subj.id}
                  onClick={() => setSplitSubj(subj.id)}
                  style={{
                    padding: "11px 14px",
                    background: isActive ? `${subj.clr}18` : G.glass,
                    border: `1px solid ${
                      isActive ? subj.clr + "66" : G.glassBorder
                    }`,
                    borderRadius: 12,
                    cursor: "pointer",
                    borderLeft: `3px solid ${subj.clr}`,
                    transition: "all 0.18s",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: isActive ? 800 : 500,
                      color: G.textPrimary,
                    }}
                  >
                    {subj.name}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      marginTop: 5,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Bar pct={pct} color={subj.clr} h={4} />
                    </div>
                    <span
                      style={{ fontSize: 11, fontWeight: 700, color: subj.clr }}
                    >
                      {pct}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <GlassCard accent={splitSubjData.clr} style={{ overflow: "hidden" }}>
            <div
              style={{
                padding: "13px 18px",
                borderBottom: `1px solid ${G.glassBorder}`,
                borderLeft: `3px solid ${splitSubjData.clr}`,
              }}
            >
              <span
                style={{ fontWeight: 800, fontSize: 14, color: G.textPrimary }}
              >
                {splitSubjData.name}
              </span>
              <Tag
                text={splitSubjData.wt}
                color={splitSubjData.clr}
                style={{ marginLeft: 8 }}
              />
            </div>
            <TopicTable
              subj={splitSubjData}
              errors={errors}
              editNote={editNote}
              noteVal={noteVal}
              setNoteVal={setNoteVal}
              setEditNote={setEditNote}
              saveNote={saveNote}
              editUrl={editUrl}
              urlVal={urlVal}
              setUrlVal={setUrlVal}
              setEditUrl={setEditUrl}
              saveLectureUrl={saveLectureUrl}
              toggle={toggle}
              setConf={setConf}
              delTopic={delTopic}
            />
            {adding === splitSubjData.id ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "10px 18px",
                  borderTop: `1px solid ${G.glassBorder}44`,
                  background: "rgba(8,14,32,0.7)",
                }}
              >
                <input
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  placeholder="New topic name…"
                  style={{ ...IS, flex: 1, fontSize: 12 }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addTopic(splitSubjData.id);
                    if (e.key === "Escape") {
                      setAdding(null);
                      setNewTopic("");
                    }
                  }}
                />
                <button
                  onClick={() => addTopic(splitSubjData.id)}
                  style={{ ...BS(splitSubjData.clr), fontSize: 11 }}
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setAdding(null);
                    setNewTopic("");
                  }}
                  style={{ ...BS(G.textMuted), fontSize: 11 }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div style={{ padding: "8px 18px" }}>
                <button
                  onClick={() => setAdding(splitSubjData.id)}
                  style={{
                    ...BS(splitSubjData.clr),
                    fontSize: 11,
                    padding: "5px 14px",
                  }}
                >
                  + Add Topic
                </button>
              </div>
            )}
          </GlassCard>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {subjects.map((subj) => {
            const pct = subjPct(subj),
              isExp = !!expanded[subj.id];
            const fd = subj.topics.filter((t) =>
              TRACK_FIELDS.every((f) => t[f])
            ).length;
            const sErr = errors.filter(
              (e) => e.subject === subj.name && !e.resolved
            ).length;
            const sDue = subj.topics.filter(isDueToday).length;
            return (
              <GlassCard
                key={subj.id}
                accent={subj.clr}
                style={{ overflow: "hidden" }}
              >
                <div
                  onClick={() => {
                    if (splitView) {
                      setSplitSubj(subj.id);
                    } else {
                      setExpanded((e) => ({ ...e, [subj.id]: !e[subj.id] }));
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "13px 18px",
                    cursor: "pointer",
                    borderLeft: `3px solid ${subj.clr}`,
                  }}
                >
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 10 10"
                    style={{
                      flexShrink: 0,
                      transform: isExp ? "rotate(90deg)" : "none",
                      transition: "transform 0.2s",
                    }}
                  >
                    <path
                      d="M3 1.5L7 5L3 8.5"
                      stroke={subj.clr}
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span
                    style={{
                      flex: 1,
                      fontWeight: 800,
                      fontSize: 13.5,
                      color: G.textPrimary,
                    }}
                  >
                    {subj.name}
                  </span>
                  <Tag text={subj.wt} color={subj.clr} />
                  {sDue > 0 && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setPage("revqueue");
                      }}
                      style={{
                        background: `${G.pink}22`,
                        color: G.pink,
                        fontSize: 10,
                        padding: "2px 8px",
                        borderRadius: 99,
                        fontWeight: 700,
                        cursor: "pointer",
                        border: `1px solid ${G.pink}55`,
                      }}
                    >
                      🔁 {sDue}
                    </span>
                  )}
                  {sErr > 0 && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setPage("errors");
                      }}
                      style={{
                        background: `${G.rose}22`,
                        color: G.rose,
                        fontSize: 10,
                        padding: "2px 8px",
                        borderRadius: 99,
                        fontWeight: 700,
                        cursor: "pointer",
                        border: `1px solid ${G.rose}55`,
                      }}
                    >
                      🐛 {sErr}
                    </span>
                  )}
                  <span style={{ fontSize: 11.5, color: G.textDim }}>
                    {fd}/{subj.topics.length}
                  </span>
                  <div style={{ width: 80 }}>
                    <Bar pct={pct} color={subj.clr} h={5} />
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 900,
                      color: subj.clr,
                      minWidth: 36,
                      textAlign: "right",
                      textShadow: `0 0 10px ${subj.clr}55`,
                    }}
                  >
                    {pct}%
                  </span>
                </div>
                {isExp && (
                  <div style={{ borderTop: `1px solid ${G.glassBorder}` }}>
                    <TopicTable
                      subj={subj}
                      errors={errors}
                      editNote={editNote}
                      noteVal={noteVal}
                      setNoteVal={setNoteVal}
                      setEditNote={setEditNote}
                      saveNote={saveNote}
                      editUrl={editUrl}
                      urlVal={urlVal}
                      setUrlVal={setUrlVal}
                      setEditUrl={setEditUrl}
                      saveLectureUrl={saveLectureUrl}
                      toggle={toggle}
                      setConf={setConf}
                      delTopic={delTopic}
                    />
                    {adding === subj.id ? (
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          padding: "10px 18px",
                          borderTop: `1px solid ${G.glassBorder}44`,
                          background: "rgba(8,14,32,0.7)",
                        }}
                      >
                        <input
                          value={newTopic}
                          onChange={(e) => setNewTopic(e.target.value)}
                          placeholder="New topic name…"
                          style={{ ...IS, flex: 1, fontSize: 12 }}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") addTopic(subj.id);
                            if (e.key === "Escape") {
                              setAdding(null);
                              setNewTopic("");
                            }
                          }}
                        />
                        <button
                          onClick={() => addTopic(subj.id)}
                          style={{ ...BS(subj.clr), fontSize: 11 }}
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            setAdding(null);
                            setNewTopic("");
                          }}
                          style={{ ...BS(G.textMuted), fontSize: 11 }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{ padding: "8px 18px" }}>
                        <button
                          onClick={() => setAdding(subj.id)}
                          style={{
                            ...BS(subj.clr),
                            fontSize: 11,
                            padding: "5px 14px",
                          }}
                        >
                          + Add Topic
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── REVISION QUEUE ────────────────────────────────────── */
const RevisionQueuePage = ({
  subjects,
  setSubjects,
  calEvents,
  setCalEvents,
  addToast,
}) => {
  const allDue = [],
    allUpcoming = [];
  subjects.forEach((s) =>
    s.topics.forEach((t) => {
      const r = getRevStatus(t);
      if (!r) return;
      const item = { ...t, subject: s.name, color: s.clr, sid: s.id, rev: r };
      if (r.diff <= 0) allDue.push(item);
      else if (r.diff <= 7) allUpcoming.push(item);
    })
  );
  allDue.sort((a, b) => a.rev.diff - b.rev.diff);
  allUpcoming.sort((a, b) => a.rev.diff - b.rev.diff);

  /* forecastedEvents — now guarded by `t.lec`. Combined with the
     cascade-clear in SubjectsPage.toggle(), unchecking "Lec" wipes
     studiedDate (so this naturally drops out), and this explicit
     `!t.lec` early-return is a defensive second layer so a topic
     that isn't marked as lectured can never show a revision
     forecast entry, even from stale/imported data.               */
  const forecastedEvents = subjects
    .flatMap((s) =>
      s.topics.flatMap((t) => {
        if (!t.lec) return [];
        const events = [];
        if (t.studiedDate && !t.r1)
          events.push({
            date: addDays(t.studiedDate, 7),
            label: `Rev 1: ${t.name}`,
            color: s.clr,
          });
        if (t.r1Date && !t.r2)
          events.push({
            date: addDays(t.r1Date, 14),
            label: `Rev 2: ${t.name}`,
            color: s.clr,
          });
        if (t.r2Date && !t.r3)
          events.push({
            date: addDays(t.r2Date, 30),
            label: `Rev 3: ${t.name}`,
            color: s.clr,
          });
        return events.filter(
          (e) => daysDiff(e.date) > 0 && daysDiff(e.date) <= 60
        );
      })
    )
    .sort((a, b) => a.date.localeCompare(b.date));

  const markRevDone = (sid, tid, stage) => {
    setSubjects((prev) =>
      prev.map((s) =>
        s.id !== sid
          ? s
          : {
              ...s,
              topics: s.topics.map((t) => {
                if (t.id !== tid) return t;
                const u = {};
                if (stage === 1) {
                  u.r1 = true;
                  u.r1Date = todayS();
                } else if (stage === 2) {
                  u.r2 = true;
                  u.r2Date = todayS();
                } else if (stage === 3) {
                  u.r3 = true;
                  u.r3Date = todayS();
                }
                return { ...t, ...u };
              }),
            }
      )
    );
    const nextInfo =
      stage === 1
        ? "Next: +14 days"
        : stage === 2
        ? "Next: +30 days"
        : "All revisions done ✓";
    addToast &&
      addToast({
        title: "Revision Complete!",
        message: nextInfo,
        icon: "✅",
        color: G.emerald,
      });
  };

  const RevCard = ({ item }) => (
    <GlassCard
      accent={item.rev.diff <= 0 ? "#ff5577" : item.color}
      style={{ padding: "12px 16px", borderLeft: `3px solid ${item.color}` }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              marginBottom: 5,
            }}
          >
            <span
              style={{ fontSize: 13, color: G.textPrimary, fontWeight: 700 }}
            >
              {item.name}
            </span>
            <Stars value={item.confidence} size={11} />
            <DiffStars value={TOPIC_DIFFICULTY[item.name] || 3} />
          </div>
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Tag
              text={
                item.subject.length > 18
                  ? item.subject.slice(0, 18) + "…"
                  : item.subject
              }
              color={item.color}
            />
            <Tag
              text={item.rev.label}
              color={item.rev.diff < 0 ? "#ff5577" : G.pink}
              squared
            />
            {item.rev.diff < 0 && (
              <span
                style={{ fontSize: 10.5, color: "#ff5577", fontWeight: 700 }}
              >
                {Math.abs(item.rev.diff)}d overdue
              </span>
            )}
            {item.rev.diff === 0 && (
              <span style={{ fontSize: 10.5, color: G.pink, fontWeight: 700 }}>
                Due today
              </span>
            )}
            {item.rev.diff > 0 && (
              <span style={{ fontSize: 10.5, color: G.textMuted }}>
                in {item.rev.diff}d · {item.rev.due}
              </span>
            )}
          </div>
        </div>
        {item.rev.diff <= 0 && (
          <button
            onClick={() => markRevDone(item.sid, item.id, item.rev.stage)}
            style={{
              ...BS(G.emerald),
              padding: "7px 16px",
              fontSize: 11.5,
              flexShrink: 0,
            }}
          >
            Mark Done ✓
          </button>
        )}
      </div>
    </GlassCard>
  );

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div style={{ marginBottom: 22 }}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: G.textPrimary,
            margin: 0,
            letterSpacing: "-0.025em",
          }}
        >
          Revision Queue
        </h1>
        <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
          Spaced Repetition: Lec → Rev 1 (+7d) → Rev 2 (+14d) → Rev 3 (+30d)
        </p>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 10,
        }}
      >
        <button
          onClick={() => {
            if (!setCalEvents) return;
            const revEvents = forecastedEvents.map((e) => ({
              id: uid(),
              title: e.label,
              date: e.date,
              color: e.color,
              _autoRevision: true,
            }));
            setCalEvents((prev) => [
              ...prev.filter((e) => !e._autoRevision),
              ...revEvents,
            ]);
            addToast &&
              addToast({
                title: "Calendar Synced!",
                message: `${revEvents.length} revision events added to Planner`,
                icon: "📅",
                color: G.cyan,
              });
          }}
          style={{ ...BS(G.cyan), padding: "8px 18px" }}
        >
          📅 Sync Revisions to Calendar
        </button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 10,
          marginBottom: 22,
        }}
      >
        {[
          { l: "Due / Overdue", v: allDue.length, c: G.pink },
          { l: "Upcoming (7d)", v: allUpcoming.length, c: G.amber },
          {
            l: "Revisions Started",
            v: subjects.reduce(
              (a, s) => a + s.topics.filter((t) => t.r1).length,
              0
            ),
            c: G.violet,
          },
        ].map((s) => (
          <GlassCard key={s.l} accent={s.c} style={{ padding: "13px 16px" }}>
            <div
              style={{
                fontSize: 10.5,
                color: G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {s.l}
            </div>
            <div
              style={{
                fontSize: 30,
                fontWeight: 900,
                color: s.c,
                margin: "5px 0",
                textShadow: `0 0 20px ${s.c}55`,
              }}
            >
              {s.v}
            </div>
          </GlassCard>
        ))}
      </div>

      {forecastedEvents.length > 0 && (
        <GlassCard
          accent={G.cyan}
          style={{ padding: "14px 18px", marginBottom: 18 }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 12,
            }}
          >
            📅 Revision Forecast — Next 60 Days
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 5,
              maxHeight: 180,
              overflowY: "auto",
            }}
          >
            {forecastedEvents.map((e, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "5px 8px",
                  background: "rgba(8,14,32,0.6)",
                  borderRadius: 8,
                  borderLeft: `3px solid ${e.color}`,
                }}
              >
                <span
                  style={{
                    fontSize: 10.5,
                    color: G.textMuted,
                    minWidth: 85,
                    fontWeight: 600,
                  }}
                >
                  {e.date}
                </span>
                <span style={{ fontSize: 11.5, color: G.textSecondary }}>
                  {e.label}
                </span>
                <span
                  style={{ fontSize: 10, color: G.textDim, marginLeft: "auto" }}
                >
                  in {daysDiff(e.date)}d
                </span>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {allDue.length === 0 && allUpcoming.length === 0 ? (
        <GlassCard style={{ padding: 52, textAlign: "center" }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>🎉</div>
          <div style={{ color: G.emerald, fontSize: 15, fontWeight: 700 }}>
            All caught up!
          </div>
        </GlassCard>
      ) : (
        <>
          {allDue.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: "#ff5577",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 10,
                }}
              >
                🔥 Due / Overdue — {allDue.length} topic
                {allDue.length > 1 ? "s" : ""}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {allDue.map((item) => (
                  <RevCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          )}
          {allUpcoming.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: G.amber,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 10,
                }}
              >
                📅 Coming Up (Next 7 Days)
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {allUpcoming.map((item) => (
                  <RevCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ── PLANNER PAGE ─────────────────────────────────────────── */
const PlannerPage = ({ goals, setGoals, calEvents, setCalEvents }) => {
  const now = new Date();
  const [vd, setVd] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [sel, setSel] = useState(null);
  const [ag, setAg] = useState(false);
  const [ae, setAe] = useState(false);
  const [ng, setNg] = useState({ text: "", type: "short", date: "" });
  const [ne, setNe] = useState({ title: "", date: "", color: G.violet });

  const yr = vd.getFullYear(),
    mo = vd.getMonth();
  const fd = new Date(yr, mo, 1).getDay(),
    dm = new Date(yr, mo + 1, 0).getDate();
  const fmt = (d) =>
    `${yr}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const tf = todayS();

  const doG = () => {
    if (!ng.text.trim()) return;
    setGoals((g) => [...g, { id: uid(), ...ng, done: false }]);
    setNg({ text: "", type: "short", date: "" });
    setAg(false);
  };
  const doE = () => {
    if (!ne.title.trim() || !ne.date) return;
    setCalEvents((e) => [...e, { id: uid(), ...ne }]);
    setNe({ title: "", date: "", color: G.violet });
    setAe(false);
  };
  const CLRS = [
    G.violet,
    G.emerald,
    G.amber,
    G.pink,
    G.rose,
    G.cyan,
    G.purple,
    G.orange,
  ];

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 22,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: G.textPrimary,
              margin: 0,
              letterSpacing: "-0.025em",
            }}
          >
            Planner & Goals
          </h1>
          <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
            Short & long-term goals · Calendar events
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setAe(true)} style={BS(G.emerald)}>
            + Event
          </button>
          <button onClick={() => setAg(true)} style={BS(G.violet)}>
            + Goal
          </button>
        </div>
      </div>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 14 }}
      >
        <GlassCard style={{ padding: 18 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <button
              onClick={() => setVd(new Date(yr, mo - 1, 1))}
              style={{
                background: "rgba(8,14,32,0.85)",
                border: `1px solid ${G.glassBorder}`,
                borderRadius: 9,
                color: G.textMuted,
                cursor: "pointer",
                padding: "5px 13px",
                fontSize: 16,
              }}
            >
              ‹
            </button>
            <span
              style={{ fontWeight: 800, fontSize: 14.5, color: G.textPrimary }}
            >
              {MONTH_NAMES[mo]} {yr}
            </span>
            <button
              onClick={() => setVd(new Date(yr, mo + 1, 1))}
              style={{
                background: "rgba(8,14,32,0.85)",
                border: `1px solid ${G.glassBorder}`,
                borderRadius: 9,
                color: G.textMuted,
                cursor: "pointer",
                padding: "5px 13px",
                fontSize: 16,
              }}
            >
              ›
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7,1fr)",
              gap: 2,
              marginBottom: 6,
            }}
          >
            {DAY_NAMES.map((d) => (
              <div
                key={d}
                style={{
                  textAlign: "center",
                  fontSize: 10,
                  color: G.textDim,
                  fontWeight: 700,
                  padding: "2px 0",
                }}
              >
                {d}
              </div>
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7,1fr)",
              gap: 2,
            }}
          >
            {Array(fd)
              .fill(null)
              .map((_, i) => (
                <div key={`p${i}`} />
              ))}
            {Array(dm)
              .fill(null)
              .map((_, i) => {
                const d = i + 1,
                  ds = fmt(d),
                  iT = ds === tf,
                  iS = ds === sel;
                const evts = calEvents.filter((e) => e.date === ds);
                return (
                  <div
                    key={d}
                    onClick={() => {
                      setSel(ds);
                      setNe((n) => ({ ...n, date: ds }));
                    }}
                    style={{
                      minHeight: 50,
                      padding: 3,
                      borderRadius: 9,
                      cursor: "pointer",
                      background: iS
                        ? `${G.violet}22`
                        : iT
                        ? "rgba(12,22,50,0.9)"
                        : "transparent",
                      border: iT
                        ? `1px solid ${G.violet}55`
                        : iS
                        ? `1px solid ${G.violet}99`
                        : "1px solid transparent",
                      transition: "all 0.15s",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: iT ? G.violet : G.textMuted,
                        fontWeight: iT ? 900 : 400,
                        textAlign: "right",
                        marginBottom: 2,
                      }}
                    >
                      {d}
                    </div>
                    {evts.slice(0, 2).map((e) => (
                      <div
                        key={e.id}
                        style={{
                          fontSize: 9,
                          background: `${e.color}33`,
                          color: e.color,
                          borderRadius: 3,
                          padding: "1px 3px",
                          marginBottom: 1,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {e.title}
                      </div>
                    ))}
                    {evts.length > 2 && (
                      <div style={{ fontSize: 8.5, color: G.textDim }}>
                        +{evts.length - 2}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </GlassCard>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            { type: "long", label: "🎯 Long-term", color: G.purple },
            { type: "short", label: "⚡ Short-term", color: G.violet },
          ].map(({ type, label, color }) => (
            <GlassCard key={type} accent={color} style={{ padding: 14 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {label}
                </span>
                <span style={{ fontSize: 10.5, color: G.textDim }}>
                  {goals.filter((g) => g.type === type && !g.done).length}{" "}
                  active
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                  maxHeight: 140,
                  overflowY: "auto",
                }}
              >
                {goals.filter((g) => g.type === type).length === 0 && (
                  <div style={{ fontSize: 12, color: G.textDim }}>
                    No {type}-term goals yet
                  </div>
                )}
                {goals
                  .filter((g) => g.type === type)
                  .map((g) => (
                    <div
                      key={g.id}
                      style={{
                        display: "flex",
                        gap: 7,
                        padding: "7px 9px",
                        background: "rgba(8,14,32,0.85)",
                        borderRadius: 9,
                        alignItems: "flex-start",
                        border: `1px solid ${G.glassBorder}`,
                      }}
                    >
                      <CB
                        checked={g.done}
                        onChange={() =>
                          setGoals((gs) =>
                            gs.map((x) =>
                              x.id === g.id ? { ...x, done: !x.done } : x
                            )
                          )
                        }
                        color={color}
                        size={14}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 12,
                            color: g.done ? G.textDim : G.textSecondary,
                            textDecoration: g.done ? "line-through" : "none",
                          }}
                        >
                          {g.text}
                        </div>
                        {g.date && (
                          <div
                            style={{
                              fontSize: 10,
                              color: G.textDim,
                              marginTop: 1,
                            }}
                          >
                            {g.date}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() =>
                          setGoals((gs) => gs.filter((x) => x.id !== g.id))
                        }
                        style={{
                          background: "none",
                          border: "none",
                          color: G.textDim,
                          cursor: "pointer",
                          fontSize: 14,
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            </GlassCard>
          ))}
          {sel && (
            <GlassCard accent={G.cyan} style={{ padding: 14 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: G.textPrimary,
                  marginBottom: 7,
                }}
              >
                📅 {sel}
              </div>
              {calEvents.filter((e) => e.date === sel).length === 0 ? (
                <div style={{ fontSize: 12, color: G.textDim }}>
                  No events — click "+ Event"
                </div>
              ) : (
                calEvents
                  .filter((e) => e.date === sel)
                  .map((e) => (
                    <div
                      key={e.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "4px 0",
                        borderBottom: `1px solid ${G.glassBorder}`,
                      }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: e.color,
                          flexShrink: 0,
                          boxShadow: `0 0 8px ${e.color}88`,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12.5,
                          color: G.textSecondary,
                          flex: 1,
                        }}
                      >
                        {e.title}
                      </span>
                      <button
                        onClick={() =>
                          setCalEvents((ev) => ev.filter((x) => x.id !== e.id))
                        }
                        style={{
                          background: "none",
                          border: "none",
                          color: G.textDim,
                          cursor: "pointer",
                          fontSize: 14,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))
              )}
            </GlassCard>
          )}
        </div>
      </div>
      {ag && (
        <Modal title="Add Goal" onClose={() => setAg(false)} accent={G.violet}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input
              value={ng.text}
              onChange={(e) => setNg((g) => ({ ...g, text: e.target.value }))}
              placeholder="Goal description…"
              style={IS}
              autoFocus
            />
            <select
              value={ng.type}
              onChange={(e) => setNg((g) => ({ ...g, type: e.target.value }))}
              style={IS}
            >
              <option value="short">⚡ Short-term</option>
              <option value="long">🎯 Long-term</option>
            </select>
            <input
              type="date"
              value={ng.date}
              onChange={(e) => setNg((g) => ({ ...g, date: e.target.value }))}
              style={IS}
            />
            <button
              onClick={doG}
              style={{ ...BS(G.violet), padding: "9px 16px", width: "100%" }}
            >
              Add Goal
            </button>
          </div>
        </Modal>
      )}
      {ae && (
        <Modal
          title="Add Event"
          onClose={() => setAe(false)}
          accent={G.emerald}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input
              value={ne.title}
              onChange={(e) => setNe((v) => ({ ...v, title: e.target.value }))}
              placeholder="Event title…"
              style={IS}
              autoFocus
            />
            <input
              type="date"
              value={ne.date}
              onChange={(e) => setNe((v) => ({ ...v, date: e.target.value }))}
              style={IS}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span
                style={{ fontSize: 11.5, color: G.textMuted, fontWeight: 600 }}
              >
                Color:
              </span>
              {CLRS.map((c) => (
                <div
                  key={c}
                  onClick={() => setNe((v) => ({ ...v, color: c }))}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: c,
                    cursor: "pointer",
                    border:
                      ne.color === c
                        ? "3px solid #fff"
                        : "3px solid transparent",
                    boxShadow: ne.color === c ? `0 0 10px ${c}88` : "none",
                    transition: "all 0.15s",
                  }}
                />
              ))}
            </div>
            <button
              onClick={doE}
              style={{ ...BS(ne.color), padding: "9px 16px", width: "100%" }}
            >
              Add Event
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ── TESTS PAGE (with negative marking) ────────────────── */
const TestsPage = ({ tests, setTests, addToast }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [negModal, setNegModal] = useState(null);
  const [nt, setNt] = useState({
    name: "",
    date: "",
    score: "",
    total: "100",
    type: "mock",
    subject: "All",
    remarks: "",
  });

  const doAdd = () => {
    if (!nt.name.trim() || nt.score === "") return;
    setTests((t) => [
      ...t,
      {
        id: uid(),
        ...nt,
        score: Number(nt.score),
        total: Number(nt.total),
        correct: null,
        wrong: null,
        skipped: null,
      },
    ]);
    setNt({
      name: "",
      date: "",
      score: "",
      total: "100",
      type: "mock",
      subject: "All",
      remarks: "",
    });
    setShowAdd(false);
  };

  const saveNegAnalysis = (testId, correct, wrong, skipped) => {
    setTests((ts) =>
      ts.map((t) => (t.id !== testId ? t : { ...t, correct, wrong, skipped }))
    );
    setNegModal(null);
    addToast({
      title: "Analysis Saved",
      message: `Net score: ${(correct - wrong / 3).toFixed(2)}`,
      icon: "📊",
      color: G.violet,
    });
  };

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 22,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: G.textPrimary,
              margin: 0,
              letterSpacing: "-0.025em",
            }}
          >
            Test Tracker
          </h1>
          <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
            Mock tests · PYQs · Negative marking analysis
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} style={BS(G.emerald)}>
          + Add Test
        </button>
      </div>
      {tests.length > 0 && (
        <GlassCard style={{ padding: 20, marginBottom: 16 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            📈 Performance Analytics
          </div>
          <SVGTestChart tests={tests} />
        </GlassCard>
      )}
      {tests.length === 0 ? (
        <GlassCard style={{ padding: 52, textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📊</div>
          <div style={{ color: G.textMuted, fontSize: 14 }}>
            No tests recorded yet. Add your first result!
          </div>
        </GlassCard>
      ) : (
        <GlassCard style={{ overflow: "hidden" }}>
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <div style={{ minWidth: 680 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 90px 55px 55px 65px 90px 90px 24px",
                  padding: "9px 18px",
                  borderBottom: `1px solid ${G.glassBorder}`,
                  gap: 7,
                }}
              >
                {[
                  "Test Name",
                  "Date",
                  "Score",
                  "Total",
                  "%",
                  "Neg.Mark",
                  "Remarks",
                  "",
                ].map((h, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 10,
                      color: G.textDim,
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {h}
                  </div>
                ))}
              </div>
              {[...tests].reverse().map((t, i) => {
                const pct = Math.round((t.score / t.total) * 100),
                  clr = pctColor(pct);
                const netScore =
                  t.correct !== null ? t.correct - t.wrong / 3 : null;
                return (
                  <div
                    key={t.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "1fr 90px 55px 55px 65px 90px 90px 24px",
                      padding: "10px 18px",
                      borderBottom: `1px solid ${G.glassBorder}44`,
                      gap: 7,
                      alignItems: "center",
                      background:
                        i % 2 === 0 ? "transparent" : "rgba(8,14,32,0.5)",
                    }}
                  >
                    {/* ... (Keep the exact same test rendering rows here) ... */}
                    <div>
                      <div
                        style={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: G.textSecondary,
                        }}
                      >
                        {t.name}
                      </div>
                      <Tag text={t.type} color={G.cyan} />
                    </div>
                    <div style={{ fontSize: 11.5, color: G.textMuted }}>
                      {t.date}
                    </div>
                    <div style={{ fontSize: 12.5, color: G.textMuted }}>
                      {t.score}
                    </div>
                    <div style={{ fontSize: 12.5, color: G.textMuted }}>
                      {t.total}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 900,
                        color: clr,
                        textShadow: `0 0 12px ${clr}55`,
                      }}
                    >
                      {pct}%
                    </div>
                    <div>
                      {netScore !== null ? (
                        <div
                          style={{
                            fontSize: 11.5,
                            color: pctColor((netScore / t.total) * 100),
                          }}
                        >
                          Net: {netScore.toFixed(1)}
                        </div>
                      ) : (
                        <button
                          onClick={() => setNegModal(t)}
                          style={{
                            ...BS(G.rose),
                            padding: "3px 8px",
                            fontSize: 10.5,
                          }}
                        >
                          Analyze
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: G.textMuted }}>
                      {t.remarks}
                    </div>
                    <button
                      onClick={() =>
                        setTests((ts) => ts.filter((x) => x.id !== t.id))
                      }
                      style={{
                        background: "none",
                        border: "none",
                        color: G.textDim,
                        cursor: "pointer",
                        fontSize: 16,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </GlassCard>
      )}
      {negModal && (
        <NegativeMarkingModal
          test={negModal}
          onSave={(c, w, s) => saveNegAnalysis(negModal.id, c, w, s)}
          onClose={() => setNegModal(null)}
        />
      )}
      {showAdd && (
        <Modal
          title="Add Test Result"
          onClose={() => setShowAdd(false)}
          accent={G.emerald}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input
              value={nt.name}
              onChange={(e) => setNt((t) => ({ ...t, name: e.target.value }))}
              placeholder="Test name (e.g. GATE Mock #1)…"
              style={IS}
              autoFocus
            />
            <input
              type="date"
              value={nt.date}
              onChange={(e) => setNt((t) => ({ ...t, date: e.target.value }))}
              style={IS}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: G.textMuted,
                    marginBottom: 4,
                  }}
                >
                  Marks Obtained
                </div>
                <input
                  type="number"
                  value={nt.score}
                  onChange={(e) =>
                    setNt((t) => ({ ...t, score: e.target.value }))
                  }
                  style={IS}
                />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: G.textMuted,
                    marginBottom: 4,
                  }}
                >
                  Total Marks
                </div>
                <input
                  type="number"
                  value={nt.total}
                  onChange={(e) =>
                    setNt((t) => ({ ...t, total: e.target.value }))
                  }
                  style={IS}
                />
              </div>
            </div>
            <select
              value={nt.type}
              onChange={(e) => setNt((t) => ({ ...t, type: e.target.value }))}
              style={IS}
            >
              <option value="mock">Full Mock Test</option>
              <option value="pyq">Previous Year Paper</option>
              <option value="sectional">Sectional Test</option>
              <option value="topicwise">Topic-wise Test</option>
            </select>
            <select
              value={nt.subject}
              onChange={(e) =>
                setNt((t) => ({ ...t, subject: e.target.value }))
              }
              style={IS}
            >
              <option value="All">All Subjects</option>
              {ALL_SUBJS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <textarea
              value={nt.remarks}
              onChange={(e) =>
                setNt((t) => ({ ...t, remarks: e.target.value }))
              }
              placeholder="Weak areas / topics to improve…"
              rows={2}
              style={{ ...IS, resize: "none" }}
            />
            <button
              onClick={doAdd}
              style={{ ...BS(G.emerald), padding: "9px 16px", width: "100%" }}
            >
              Save Test Result
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ── ERROR LOG PAGE ─────────────────────────────────────── */
const ErrorLogPage = ({ errors, setErrors }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [fSubj, setFSubj] = useState("All");
  const [fType, setFType] = useState("All");
  const [fStat, setFStat] = useState("All");
  const [viewErr, setViewErr] = useState(null);
  const [showHM, setShowHM] = useState(false);
  const [cellModal, setCellModal] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrStatus, setOcrStatus] = useState("");
  const [ocrPreview, setOcrPreview] = useState(null);
  const imgRef = useRef(null);
  const [ne, setNe] = useState({
    subject: "",
    topic: "",
    errorType: "Conceptual",
    description: "",
    source: "",
    solution: "",
    date: todayS(),
  });

  /* Rebuilt OCR — loads Tesseract.js from jsDelivr (its core/worker/
     language data all default to jsDelivr too, so nothing is blocked
     by the artifact's CDN allowlist), downsizes + contrast-boosts the
     photo first for speed & accuracy, and reports live progress so it
     never looks "stuck". */
  const runOCR = async (file) => {
    setOcrLoading(true);
    setOcrProgress(0);
    setOcrStatus("Starting…");
    setOcrPreview(null);
    try {
      const { text, preview } = await runImageOCR(file, (pct, status) => {
        setOcrProgress(pct);
        setOcrStatus(status);
      });
      setOcrPreview(preview);
      if (text) {
        const detectedSubj = autoDetectSubject(text);
        setNe((n) => ({
          ...n,
          description: text.slice(0, 600),
          subject: detectedSubj,
        }));
        setOcrStatus(`Done — extracted ${text.length} characters`);
      } else {
        setOcrStatus(
          "No text detected — try a clearer/closer photo, or type manually."
        );
      }
    } catch (e) {
      setOcrStatus(
        "OCR failed (" +
          (e?.message || "unknown error") +
          ") — please type manually."
      );
    }
    setOcrLoading(false);
  };

  const doAdd = () => {
    if (!ne.description.trim()) return;
    setErrors((e) => [{ id: uid(), ...ne, resolved: false }, ...e]);
    setNe({
      subject: "",
      topic: "",
      errorType: "Conceptual",
      description: "",
      source: "",
      solution: "",
      date: todayS(),
    });
    setOcrPreview(null);
    setOcrStatus("");
    setShowAdd(false);
  };

  const flip = (id) =>
    setErrors((e) =>
      e.map((x) => (x.id !== id ? x : { ...x, resolved: !x.resolved }))
    );
  const del = (id) => {
    setErrors((e) => e.filter((x) => x.id !== id));
    if (viewErr?.id === id) setViewErr(null);
  };

  const filtered = errors.filter(
    (e) =>
      (fSubj === "All" || e.subject === fSubj) &&
      (fType === "All" || e.errorType === fType) &&
      (fStat === "All" ||
        (fStat === "Pending" && !e.resolved) ||
        (fStat === "Resolved" && e.resolved))
  );
  const tot = errors.length,
    res = errors.filter((e) => e.resolved).length;

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: G.textPrimary,
              margin: 0,
              letterSpacing: "-0.025em",
            }}
          >
            Error Log
          </h1>
          <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
            Track every mistake · OCR upload · Never repeat errors
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowHM(!showHM)} style={BS(G.purple)}>
            {showHM ? "Hide" : "Show"} Heatmap
          </button>
          <button onClick={() => setShowAdd(true)} style={BS(G.rose)}>
            + Log Error
          </button>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {[
          { l: "Total Logged", v: tot, c: G.cyan },
          { l: "Pending", v: tot - res, c: G.rose },
          { l: "Resolved", v: res, c: G.emerald },
          {
            l: "Resolution Rate",
            v: tot > 0 ? `${Math.round((res / tot) * 100)}%` : "—",
            c: G.violet,
          },
        ].map((s) => (
          <GlassCard key={s.l} accent={s.c} style={{ padding: "13px 16px" }}>
            <div
              style={{
                fontSize: 10.5,
                color: G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {s.l}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 900,
                color: s.c,
                margin: "5px 0",
                textShadow: `0 0 18px ${s.c}55`,
              }}
            >
              {s.v}
            </div>
          </GlassCard>
        ))}
      </div>
      {showHM && (
        <GlassCard accent={G.rose} style={{ padding: 18, marginBottom: 16 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 12,
            }}
          >
            🔥 Error Heatmap
          </div>
          <ErrorHeatmap
            errors={errors}
            onCellClick={(s, et) =>
              setCellModal({
                subject: s,
                errorType: et,
                errors: errors.filter(
                  (e) => e.subject === s && e.errorType === et
                ),
              })
            }
          />
        </GlassCard>
      )}
      <div
        style={{ display: "flex", gap: 7, marginBottom: 13, flexWrap: "wrap" }}
      >
        <select
          value={fSubj}
          onChange={(e) => setFSubj(e.target.value)}
          style={{ ...IS, width: 185 }}
        >
          <option value="All">All Subjects</option>
          {ALL_SUBJS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={fType}
          onChange={(e) => setFType(e.target.value)}
          style={{ ...IS, width: 155 }}
        >
          <option value="All">All Types</option>
          {ERROR_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={fStat}
          onChange={(e) => setFStat(e.target.value)}
          style={{ ...IS, width: 135 }}
        >
          <option value="All">All</option>
          <option value="Pending">Pending</option>
          <option value="Resolved">Resolved</option>
        </select>
        <span
          style={{
            fontSize: 12.5,
            color: G.textMuted,
            display: "flex",
            alignItems: "center",
          }}
        >
          {filtered.length} result{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>
      {filtered.length === 0 ? (
        <GlassCard style={{ padding: 44, textAlign: "center" }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>🐛</div>
          <div style={{ color: G.textMuted, fontSize: 13.5 }}>
            {errors.length === 0
              ? "No errors logged yet — great start!"
              : "No errors match your filters."}
          </div>
        </GlassCard>
      ) : (
        <GlassCard style={{ overflow: "hidden" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "28px 120px 106px 106px 1fr 84px 84px",
              padding: "9px 18px",
              borderBottom: `1px solid ${G.glassBorder}`,
              gap: 7,
            }}
          >
            {[
              "",
              "Subject",
              "Topic",
              "Type",
              "Description",
              "Source",
              "Status",
            ].map((h, i) => (
              <div
                key={i}
                style={{
                  fontSize: 10,
                  color: G.textDim,
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                {h}
              </div>
            ))}
          </div>
          {filtered.map((err, i) => (
            <div
              key={err.id}
              style={{
                display: "grid",
                gridTemplateColumns: "28px 120px 106px 106px 1fr 84px 84px",
                padding: "10px 18px",
                borderBottom: `1px solid ${G.glassBorder}44`,
                gap: 7,
                alignItems: "center",
                background: i % 2 === 0 ? "transparent" : "rgba(8,14,32,0.5)",
                cursor: "pointer",
              }}
              onClick={() => setViewErr(err)}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  del(err.id);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: G.textDim,
                  cursor: "pointer",
                  fontSize: 14,
                  padding: 0,
                }}
              >
                ×
              </button>
              <div
                style={{
                  fontSize: 11.5,
                  color: G.textSecondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {err.subject || "—"}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: G.textMuted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {err.topic || "—"}
              </div>
              <Tag text={err.errorType} color={G.rose} />
              <div
                style={{
                  fontSize: 12,
                  color: G.textSecondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {err.description}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: G.textMuted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {err.source || "—"}
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  flip(err.id);
                }}
                style={{ cursor: "pointer" }}
              >
                <Tag
                  text={err.resolved ? "✓ Done" : "Pending"}
                  color={err.resolved ? G.emerald : G.rose}
                  squared
                />
              </div>
            </div>
          ))}
        </GlassCard>
      )}
      {cellModal && (
        <Modal
          title={`${cellModal.errorType} — ${cellModal.subject}`}
          onClose={() => setCellModal(null)}
          accent={G.rose}
          wide
        >
          <div style={{ marginBottom: 10, fontSize: 12.5, color: G.textMuted }}>
            {cellModal.errors.length} error
            {cellModal.errors.length !== 1 ? "s" : ""} here
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {cellModal.errors.map((err) => (
              <GlassCard
                key={err.id}
                style={{ padding: "12px 15px" }}
                accent={G.rose}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 7,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: G.textPrimary,
                    }}
                  >
                    {err.topic || "(no topic)"}
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Tag
                      text={err.resolved ? "✓ Done" : "Pending"}
                      color={err.resolved ? G.emerald : G.rose}
                    />
                    <button
                      onClick={() => flip(err.id)}
                      style={{
                        ...BS(err.resolved ? G.rose : G.emerald),
                        padding: "3px 10px",
                        fontSize: 10.5,
                      }}
                    >
                      {err.resolved ? "Reopen" : "Resolve"}
                    </button>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: G.textSecondary,
                    lineHeight: 1.5,
                  }}
                >
                  {err.description}
                </div>
                {err.solution && (
                  <div
                    style={{
                      fontSize: 11.5,
                      color: G.emerald,
                      marginTop: 7,
                      background: `${G.emerald}18`,
                      borderRadius: 8,
                      padding: "7px 11px",
                      border: `1px solid ${G.emerald}33`,
                    }}
                  >
                    {err.solution}
                  </div>
                )}
              </GlassCard>
            ))}
          </div>
        </Modal>
      )}
      {viewErr && (
        <Modal
          title="Error Details"
          onClose={() => setViewErr(null)}
          wide
          accent={G.rose}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 13,
              }}
            >
              {[
                { l: "Subject", v: viewErr.subject || "—" },
                { l: "Topic", v: viewErr.topic || "—" },
                { l: "Source", v: viewErr.source || "—" },
                { l: "Date", v: viewErr.date || "—" },
              ].map((s) => (
                <div key={s.l}>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: G.textMuted,
                      marginBottom: 4,
                    }}
                  >
                    {s.l}
                  </div>
                  <div
                    style={{
                      fontSize: 13.5,
                      color: G.textPrimary,
                      fontWeight: 700,
                    }}
                  >
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
            <Tag text={viewErr.errorType} color={G.rose} />
            <div>
              <div
                style={{ fontSize: 11.5, color: G.textMuted, marginBottom: 4 }}
              >
                Description
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: G.textSecondary,
                  background: "rgba(8,14,32,0.85)",
                  padding: "11px 14px",
                  borderRadius: 11,
                  lineHeight: 1.6,
                  border: `1px solid ${G.glassBorder}`,
                }}
              >
                {viewErr.description}
              </div>
            </div>
            {viewErr.solution && (
              <div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: G.textMuted,
                    marginBottom: 4,
                  }}
                >
                  How to Avoid
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: G.emerald,
                    background: `${G.emerald}18`,
                    padding: "11px 14px",
                    borderRadius: 11,
                    lineHeight: 1.6,
                    border: `1px solid ${G.emerald}33`,
                  }}
                >
                  {viewErr.solution}
                </div>
              </div>
            )}
            <div
              style={{ display: "flex", gap: 9, justifyContent: "flex-end" }}
            >
              <button
                onClick={() => {
                  flip(viewErr.id);
                  setViewErr((e) => ({ ...e, resolved: !e.resolved }));
                }}
                style={BS(viewErr.resolved ? G.rose : G.emerald)}
              >
                {viewErr.resolved ? "Mark Pending" : "Mark Resolved ✓"}
              </button>
              <button onClick={() => del(viewErr.id)} style={BS(G.textMuted)}>
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
      {showAdd && (
        <Modal
          title="Log New Error"
          onClose={() => setShowAdd(false)}
          wide
          accent={G.rose}
        >
          <input
            ref={imgRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => e.target.files[0] && runOCR(e.target.files[0])}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 9,
                padding: "10px 14px",
                background: `${G.violet}18`,
                borderRadius: 11,
                border: `1px solid ${G.violet}44`,
              }}
            >
              <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                <span style={{ fontSize: 14 }}>📷</span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontSize: 12.5, fontWeight: 700, color: G.violet }}
                  >
                    OCR Image Upload
                  </div>
                  <div style={{ fontSize: 11, color: G.textSecondary }}>
                    Upload a photo of a question — text auto-extracted
                  </div>
                </div>
                <button
                  onClick={() => imgRef.current?.click()}
                  disabled={ocrLoading}
                  style={{
                    ...BS(G.violet),
                    padding: "7px 14px",
                    opacity: ocrLoading ? 0.6 : 1,
                  }}
                >
                  {ocrLoading ? "Scanning…" : "📷 Upload"}
                </button>
              </div>
              {ocrLoading && (
                <div>
                  <Bar pct={ocrProgress} color={G.violet} h={5} />
                  <div
                    style={{ fontSize: 10.5, color: G.textMuted, marginTop: 4 }}
                  >
                    {ocrStatus} {ocrProgress}%
                  </div>
                </div>
              )}
              {!ocrLoading && ocrStatus && (
                <div
                  style={{
                    fontSize: 10.5,
                    color: ocrStatus.startsWith("Done") ? G.emerald : G.amber,
                  }}
                >
                  {ocrStatus}
                </div>
              )}
              {ocrPreview && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <img
                    src={ocrPreview}
                    alt="Scanned preview"
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: `1px solid ${G.glassBorder}`,
                    }}
                  />
                  <span style={{ fontSize: 10.5, color: G.textDim }}>
                    Processed image used for OCR (downscaled + contrast-boosted)
                  </span>
                </div>
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 11,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: G.textMuted,
                    marginBottom: 4,
                  }}
                >
                  Subject
                </div>
                <select
                  value={ne.subject}
                  onChange={(e) =>
                    setNe((x) => ({ ...x, subject: e.target.value }))
                  }
                  style={IS}
                >
                  <option value="">Select…</option>
                  {ALL_SUBJS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: G.textMuted,
                    marginBottom: 4,
                  }}
                >
                  Topic
                </div>
                <input
                  value={ne.topic}
                  onChange={(e) =>
                    setNe((x) => ({ ...x, topic: e.target.value }))
                  }
                  placeholder="e.g. BJT Amplifiers"
                  style={IS}
                />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: G.textMuted,
                    marginBottom: 4,
                  }}
                >
                  Error Type
                </div>
                <select
                  value={ne.errorType}
                  onChange={(e) =>
                    setNe((x) => ({ ...x, errorType: e.target.value }))
                  }
                  style={IS}
                >
                  {ERROR_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: G.textMuted,
                    marginBottom: 4,
                  }}
                >
                  Source
                </div>
                <input
                  value={ne.source}
                  onChange={(e) =>
                    setNe((x) => ({ ...x, source: e.target.value }))
                  }
                  placeholder="e.g. GATE 2021, Mock #3"
                  style={IS}
                />
              </div>
            </div>
            <div>
              <div
                style={{ fontSize: 11.5, color: G.textMuted, marginBottom: 4 }}
              >
                What went wrong? *
              </div>
              <textarea
                value={ne.description}
                onChange={(e) =>
                  setNe((x) => ({ ...x, description: e.target.value }))
                }
                placeholder="Describe the mistake clearly…"
                rows={3}
                style={{ ...IS, resize: "none" }}
                autoFocus
              />
            </div>
            <div>
              <div
                style={{ fontSize: 11.5, color: G.textMuted, marginBottom: 4 }}
              >
                How to avoid next time
              </div>
              <textarea
                value={ne.solution}
                onChange={(e) =>
                  setNe((x) => ({ ...x, solution: e.target.value }))
                }
                placeholder="Correction / formula / concept to remember…"
                rows={2}
                style={{ ...IS, resize: "none" }}
              />
            </div>
            <input
              type="date"
              value={ne.date}
              onChange={(e) => setNe((x) => ({ ...x, date: e.target.value }))}
              style={IS}
            />
            <button
              onClick={doAdd}
              style={{ ...BS(G.rose), padding: "9px 16px", width: "100%" }}
            >
              Log Error
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ── QUICK NOTES ─────────────────────────────────────────── */
const QN_COLORS = [
  "rgba(12,20,46,0.94)",
  "rgba(22,10,44,0.94)",
  "rgba(8,30,24,0.94)",
  "rgba(34,20,8,0.94)",
  "rgba(34,8,20,0.94)",
  "rgba(12,28,44,0.94)",
];
const TC = {
  note: G.violet,
  formula: G.amber,
  reminder: G.emerald,
  important: G.rose,
};
const QuickNotesPage = ({ qnotes, setQnotes }) => {
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [nq, setNq] = useState({
    content: "",
    subject: "General / Other",
    type: "note",
    color: QN_COLORS[0],
    autoTags: [],
  });
  const [filterSubj, setFilterSubj] = useState("All");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrStatus, setOcrStatus] = useState("");
  const [ocrPreview, setOcrPreview] = useState(null);
  const imgRef = useRef(null);

  const handleContentChange = (val) => {
    const detectedSubj = autoDetectSubject(val),
      tags = extractAutoTags(val);
    setNq((n) => ({
      ...n,
      content: val,
      subject: val.length > 10 ? detectedSubj : n.subject,
      autoTags: tags,
    }));
  };

  /* Rebuilt OCR — see ErrorLogPage for details on the jsDelivr/
     preprocessing/progress approach. */
  const runOCR = async (file) => {
    setOcrLoading(true);
    setOcrProgress(0);
    setOcrStatus("Starting…");
    setOcrPreview(null);
    try {
      const { text, preview } = await runImageOCR(file, (pct, status) => {
        setOcrProgress(pct);
        setOcrStatus(status);
      });
      setOcrPreview(preview);
      if (text) {
        handleContentChange(text.slice(0, 600));
        setOcrStatus(`Done — extracted ${text.length} characters`);
      } else {
        setOcrStatus(
          "No text detected — try a clearer/closer photo, or type manually."
        );
      }
    } catch (e) {
      setOcrStatus(
        "OCR failed (" +
          (e?.message || "unknown error") +
          ") — please type manually."
      );
    }
    setOcrLoading(false);
  };

  const doAdd = () => {
    if (!nq.content.trim()) return;
    setQnotes((n) => [
      { id: uid(), ...nq, pinned: false, date: todayS() },
      ...n,
    ]);
    setNq({
      content: "",
      subject: "General / Other",
      type: "note",
      color: QN_COLORS[0],
      autoTags: [],
    });
    setOcrPreview(null);
    setOcrStatus("");
    setShowAdd(false);
  };

  const filtered = qnotes.filter(
    (n) =>
      (filterSubj === "All" || n.subject === filterSubj) &&
      (!search ||
        n.content.toLowerCase().includes(search.toLowerCase()) ||
        n.subject.toLowerCase().includes(search.toLowerCase()))
  );
  const sorted = [
    ...filtered.filter((n) => n.pinned),
    ...filtered.filter((n) => !n.pinned),
  ];

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: G.textPrimary,
              margin: 0,
              letterSpacing: "-0.025em",
            }}
          >
            Quick Notes
          </h1>
          <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
            Formulas · Reminders · Key concepts · OCR from image
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} style={BS(G.amber)}>
          + Quick Note
        </button>
      </div>
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 17,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search notes…"
          style={{ ...IS, maxWidth: 240 }}
        />
        <select
          value={filterSubj}
          onChange={(e) => setFilterSubj(e.target.value)}
          style={{ ...IS, maxWidth: 205 }}
        >
          <option value="All">All Subjects</option>
          {ALL_SUBJS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 12.5, color: G.textMuted }}>
          {sorted.length} note{sorted.length !== 1 ? "s" : ""}
        </span>
      </div>
      {sorted.length === 0 ? (
        <GlassCard style={{ padding: 52, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>⚡</div>
          <div style={{ color: G.textMuted, fontSize: 14 }}>
            Add formulas, reminders, and key concepts here!
          </div>
        </GlassCard>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(236px,1fr))",
            gap: 11,
          }}
        >
          {sorted.map((n) => (
            <div
              key={n.id}
              style={{
                background: n.color || QN_COLORS[0],
                border: `1px solid ${
                  n.pinned ? `${G.amber}66` : G.glassBorder
                }`,
                borderRadius: 16,
                padding: 14,
                borderTop: n.pinned
                  ? `2px solid ${G.amber}`
                  : `1px solid ${G.glassBorder}`,
                backdropFilter: "blur(20px) saturate(180%)",
                boxShadow: n.pinned
                  ? `0 4px 24px ${G.amber}18`
                  : "0 4px 18px rgba(0,0,0,0.45)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <Tag text={n.type || "note"} color={TC[n.type || "note"]} />
                <div style={{ display: "flex", gap: 5 }}>
                  <span
                    onClick={() =>
                      setQnotes((qn) =>
                        qn.map((x) =>
                          x.id === n.id ? { ...x, pinned: !x.pinned } : x
                        )
                      )
                    }
                    style={{
                      fontSize: 14,
                      cursor: "pointer",
                      opacity: n.pinned ? 1 : 0.35,
                      filter: n.pinned
                        ? "drop-shadow(0 0 6px #ffe666)"
                        : "none",
                    }}
                  >
                    📌
                  </span>
                  <button
                    onClick={() =>
                      setQnotes((qn) => qn.filter((x) => x.id !== n.id))
                    }
                    style={{
                      background: "none",
                      border: "none",
                      color: G.textMuted,
                      cursor: "pointer",
                      fontSize: 14,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: G.textPrimary,
                  lineHeight: 1.65,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {n.content}
              </div>
              {n.autoTags && n.autoTags.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    marginTop: 8,
                  }}
                >
                  {n.autoTags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      style={{
                        fontSize: 9.5,
                        color: G.textMuted,
                        background: "rgba(255,255,255,0.08)",
                        borderRadius: 99,
                        padding: "1px 7px",
                        border: `1px solid ${G.glassBorder}`,
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 10,
                }}
              >
                <Tag
                  text={
                    n.subject.length > 14
                      ? n.subject.slice(0, 14) + "…"
                      : n.subject
                  }
                  color={G.textMuted}
                />
                <span style={{ fontSize: 10, color: G.textDim }}>{n.date}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {showAdd && (
        <Modal
          title="Add Quick Note"
          onClose={() => setShowAdd(false)}
          accent={G.amber}
        >
          <input
            ref={imgRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => e.target.files[0] && runOCR(e.target.files[0])}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 9,
                padding: "10px 14px",
                background: `${G.amber}18`,
                borderRadius: 11,
                border: `1px solid ${G.amber}44`,
              }}
            >
              <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                <span style={{ fontSize: 14 }}>📷</span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontSize: 12.5, fontWeight: 700, color: G.amber }}
                  >
                    OCR Scan
                  </div>
                  <div style={{ fontSize: 11, color: G.textSecondary }}>
                    Upload formula sheet or handwritten note
                  </div>
                </div>
                <button
                  onClick={() => imgRef.current?.click()}
                  disabled={ocrLoading}
                  style={{
                    ...BS(G.amber),
                    padding: "7px 14px",
                    opacity: ocrLoading ? 0.6 : 1,
                  }}
                >
                  {ocrLoading ? "Scanning…" : "📷 Scan"}
                </button>
              </div>
              {ocrLoading && (
                <div>
                  <Bar pct={ocrProgress} color={G.amber} h={5} />
                  <div
                    style={{ fontSize: 10.5, color: G.textMuted, marginTop: 4 }}
                  >
                    {ocrStatus} {ocrProgress}%
                  </div>
                </div>
              )}
              {!ocrLoading && ocrStatus && (
                <div
                  style={{
                    fontSize: 10.5,
                    color: ocrStatus.startsWith("Done") ? G.emerald : G.amber,
                  }}
                >
                  {ocrStatus}
                </div>
              )}
              {ocrPreview && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <img
                    src={ocrPreview}
                    alt="Scanned preview"
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: `1px solid ${G.glassBorder}`,
                    }}
                  />
                  <span style={{ fontSize: 10.5, color: G.textDim }}>
                    Processed image used for OCR (downscaled + contrast-boosted)
                  </span>
                </div>
              )}
            </div>
            <textarea
              value={nq.content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Formula, reminder, key concept…"
              rows={4}
              style={{ ...IS, resize: "none" }}
              autoFocus
            />
            {nq.autoTags.length > 0 && (
              <div
                style={{
                  padding: "7px 11px",
                  background: `${G.violet}18`,
                  borderRadius: 9,
                  border: `1px solid ${G.violet}33`,
                }}
              >
                <span
                  style={{ fontSize: 11, color: G.violet, fontWeight: 700 }}
                >
                  🏷 Auto:{" "}
                </span>
                <span style={{ fontSize: 11, color: G.textSecondary }}>
                  {nq.subject}
                </span>
              </div>
            )}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 11,
              }}
            >
              <select
                value={nq.type}
                onChange={(e) => setNq((n) => ({ ...n, type: e.target.value }))}
                style={IS}
              >
                <option value="note">📝 Note</option>
                <option value="formula">📐 Formula</option>
                <option value="reminder">🔔 Reminder</option>
                <option value="important">⚠ Important</option>
              </select>
              <select
                value={nq.subject}
                onChange={(e) =>
                  setNq((n) => ({ ...n, subject: e.target.value }))
                }
                style={IS}
              >
                {ALL_SUBJS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
              <span
                style={{ fontSize: 11.5, color: G.textMuted, fontWeight: 600 }}
              >
                Color:
              </span>
              {QN_COLORS.map((c) => (
                <div
                  key={c}
                  onClick={() => setNq((n) => ({ ...n, color: c }))}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: c,
                    cursor: "pointer",
                    border:
                      nq.color === c
                        ? `2px solid ${G.violet}66`
                        : `2px solid ${G.glassBorder}`,
                    boxShadow:
                      nq.color === c ? `0 0 10px ${G.violet}55` : "none",
                    transition: "all 0.15s",
                  }}
                />
              ))}
            </div>
            <button
              onClick={doAdd}
              style={{ ...BS(G.amber), padding: "9px 16px", width: "100%" }}
            >
              Add Note
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ── ROUTINE PAGE ─────────────────────────────────────────── */
const RoutinePage = ({ routine, setRoutine }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [ns, setNs] = useState({ time: "", task: "", cat: "study" });
  const [editSlot, setEditSlot] = useState(null);
  const [editVal, setEditVal] = useState({});
  const done = routine.filter((r) => r.done).length;
  const ss = routine.filter((r) => r.cat === "study"),
    sd = ss.filter((r) => r.done).length;

  const doAdd = () => {
    if (!ns.time.trim() || !ns.task.trim()) return;
    setRoutine((r) => [...r, { id: uid(), ...ns, done: false }]);
    setNs({ time: "", task: "", cat: "study" });
    setShowAdd(false);
  };
  const save = (id) => {
    setRoutine((r) => r.map((s) => (s.id !== id ? s : { ...s, ...editVal })));
    setEditSlot(null);
  };

  return (
    <div style={{ padding: 28, paddingBottom: 110 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: G.textPrimary,
              margin: 0,
              letterSpacing: "-0.025em",
            }}
          >
            Daily Routine
          </h1>
          <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
            Check off as you go · Reset each morning
          </p>
        </div>
        <div style={{ display: "flex", gap: 7 }}>
          <button
            onClick={() =>
              setRoutine((r) => r.map((s) => ({ ...s, done: false })))
            }
            style={BS(G.textMuted)}
          >
            ↺ Reset
          </button>
          <button onClick={() => setShowAdd(true)} style={BS(G.violet)}>
            + Slot
          </button>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 10,
          marginBottom: 18,
        }}
      >
        {[
          {
            l: "Day Progress",
            v: `${done}/${routine.length}`,
            pct: routine.length ? Math.round((done / routine.length) * 100) : 0,
            c: G.violet,
          },
          {
            l: "Study Sessions",
            v: `${sd}/${ss.length}`,
            pct: ss.length ? Math.round((sd / ss.length) * 100) : 0,
            c: G.cyan,
          },
          {
            l: "Completion",
            v: `${
              routine.length ? Math.round((done / routine.length) * 100) : 0
            }%`,
            pct: routine.length ? Math.round((done / routine.length) * 100) : 0,
            c: G.emerald,
          },
        ].map((s) => (
          <GlassCard key={s.l} accent={s.c} style={{ padding: "13px 16px" }}>
            <div
              style={{
                fontSize: 10.5,
                color: G.textMuted,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {s.l}
            </div>
            <div
              style={{
                fontSize: 27,
                fontWeight: 900,
                color: s.c,
                margin: "5px 0 8px",
                textShadow: `0 0 18px ${s.c}44`,
              }}
            >
              {s.v}
            </div>
            <Bar pct={s.pct} color={s.c} h={4} />
          </GlassCard>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {routine.map((slot) => {
          const isE = editSlot === slot.id;
          return (
            <GlassCard key={slot.id} style={{ overflow: "hidden" }}>
              {isE ? (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    padding: "10px 15px",
                    alignItems: "center",
                  }}
                >
                  <input
                    value={editVal.time}
                    onChange={(e) =>
                      setEditVal((v) => ({ ...v, time: e.target.value }))
                    }
                    style={{ ...IS, width: 90, flexShrink: 0 }}
                    placeholder="Time"
                  />
                  <input
                    value={editVal.task}
                    onChange={(e) =>
                      setEditVal((v) => ({ ...v, task: e.target.value }))
                    }
                    style={{ ...IS, flex: 1 }}
                    autoFocus
                  />
                  <select
                    value={editVal.cat}
                    onChange={(e) =>
                      setEditVal((v) => ({ ...v, cat: e.target.value }))
                    }
                    style={{ ...IS, width: 115, flexShrink: 0 }}
                  >
                    <option value="study">📚 Study</option>
                    <option value="health">🌿 Health</option>
                    <option value="personal">☕ Personal</option>
                  </select>
                  <button
                    onClick={() => save(slot.id)}
                    style={BS(CAT_COLOR[editVal.cat])}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditSlot(null)}
                    style={BS(G.textMuted)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    padding: "11px 15px",
                    opacity: slot.done ? 0.55 : 1,
                    transition: "opacity 0.2s",
                  }}
                >
                  <CB
                    checked={slot.done}
                    onChange={() =>
                      setRoutine((r) =>
                        r.map((s) =>
                          s.id === slot.id ? { ...s, done: !s.done } : s
                        )
                      )
                    }
                    color={CAT_COLOR[slot.cat]}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      color: CAT_COLOR[slot.cat],
                      fontWeight: 800,
                      minWidth: 60,
                    }}
                  >
                    {slot.time}
                  </span>
                  <span style={{ fontSize: 14 }}>{CAT_ICON[slot.cat]}</span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: slot.done ? G.textDim : G.textSecondary,
                      textDecoration: slot.done ? "line-through" : "none",
                    }}
                  >
                    {slot.task}
                  </span>
                  <Tag text={slot.cat} color={CAT_COLOR[slot.cat]} />
                  <button
                    onClick={() => {
                      setEditSlot(slot.id);
                      setEditVal({
                        time: slot.time,
                        task: slot.task,
                        cat: slot.cat,
                      });
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: G.textDim,
                      cursor: "pointer",
                      fontSize: 13,
                      padding: "0 5px",
                    }}
                  >
                    ✏
                  </button>
                  <button
                    onClick={() =>
                      setRoutine((r) => r.filter((s) => s.id !== slot.id))
                    }
                    style={{
                      background: "none",
                      border: "none",
                      color: G.textDim,
                      cursor: "pointer",
                      fontSize: 17,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </GlassCard>
          );
        })}
      </div>
      {showAdd && (
        <Modal
          title="Add Routine Slot"
          onClose={() => setShowAdd(false)}
          accent={G.violet}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
            <input
              value={ns.time}
              onChange={(e) => setNs((s) => ({ ...s, time: e.target.value }))}
              placeholder="Time (e.g. 7:00 AM)"
              style={IS}
              autoFocus
            />
            <input
              value={ns.task}
              onChange={(e) => setNs((s) => ({ ...s, task: e.target.value }))}
              placeholder="Task description…"
              style={IS}
            />
            <select
              value={ns.cat}
              onChange={(e) => setNs((s) => ({ ...s, cat: e.target.value }))}
              style={IS}
            >
              <option value="study">📚 Study</option>
              <option value="health">🌿 Health</option>
              <option value="personal">☕ Personal</option>
            </select>
            <button
              onClick={doAdd}
              style={{ ...BS(G.violet), padding: "9px 16px", width: "100%" }}
            >
              Add Slot
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ── INSIGHTS PAGE ─────────────────────────────────────── */
const InsightsPage = ({
  subjects,
  tests = [],
  errors = [],
  pomodoroLog = [],
  streak,
  qnotes = [],
  profile,
}) => {
  const [tab, setTab] = useState("effort");

  const burnoutRisk = () => {
    const recentTests = tests.slice(-5);
    if (recentTests.length < 3)
      return { level: "low", msg: "Not enough data yet", c: G.emerald };
    const scores = recentTests.map((t) =>
      Math.round((t.score / t.total) * 100)
    );
    const trend = scores[scores.length - 1] - scores[0];
    const errUnresolved = errors.filter((e) => !e.resolved).length;
    if (trend < -15 && errUnresolved > 10)
      return {
        level: "high",
        msg: "Score declining + high unresolved errors. Take a break and review fundamentals.",
        c: "#ff5577",
      };
    if (trend < -5 || errUnresolved > 5)
      return {
        level: "medium",
        msg: "Some warning signs. Consider adjusting your study approach.",
        c: "#ff9f6b",
      };
    return {
      level: "low",
      msg: "You're on track! Keep the consistency going.",
      c: "#4eeaaa",
    };
  };

  const burnout = burnoutRisk();
  const confVsActual = subjects.map((s) => {
    const tw = s.topics.filter((t) => t.confidence > 0);
    const avgConf = tw.length
      ? tw.reduce((a, t) => a + t.confidence, 0) / tw.length
      : 0;
    const subjTests = tests.filter((t) => t.subject === s.name);
    const avgScore = subjTests.length
      ? subjTests.reduce(
          (a, t) => a + Math.round((t.score / t.total) * 100),
          0
        ) / subjTests.length
      : subjPct(s) * 0.8;
    return {
      name: s.name.split(" ").slice(-1)[0],
      conf: avgConf,
      score: avgScore,
      gap: avgScore - (avgConf / 5) * 100,
      clr: s.clr,
    };
  });

  const totalPomMinutes = (pomodoroLog || []).reduce(
    (a, l) => a + l.minutes,
    0
  );
  const timePerSubj = {};
  (pomodoroLog || []).forEach((l) => {
    timePerSubj[l.subject] = (timePerSubj[l.subject] || 0) + l.minutes;
  });

  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div style={{ marginBottom: 22 }}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: G.textPrimary,
            margin: 0,
            letterSpacing: "-0.025em",
          }}
        >
          Insights & Analytics
        </h1>
        <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
          Effort vs Yield · Gantt timeline · Study calendar · Confidence · Time
          analysis · Weekly report
        </p>
      </div>
      <GlassCard
        accent={burnout.c}
        style={{ padding: "15px 20px", marginBottom: 18 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
          <div style={{ fontSize: 28 }}>
            {burnout.level === "high"
              ? "🚨"
              : burnout.level === "medium"
              ? "⚠️"
              : "✅"}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: burnout.c }}>
              Burnout Risk: {burnout.level.toUpperCase()}
            </div>
            <div style={{ fontSize: 12, color: G.textSecondary, marginTop: 2 }}>
              {burnout.msg}
            </div>
          </div>
          <div style={{ marginLeft: "auto", textAlign: "right" }}>
            <div style={{ fontSize: 10.5, color: G.textMuted }}>
              Study Streak (≥2hrs)
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 900,
                color: G.amber,
                textShadow: `0 0 18px ${G.amber}77`,
              }}
            >
              {streak}🔥
            </div>
          </div>
        </div>
      </GlassCard>
      <div
        style={{ display: "flex", gap: 7, marginBottom: 18, flexWrap: "wrap" }}
      >
        {[
          { id: "effort", l: "⚡ Effort vs Yield" },
          { id: "gantt", l: "📊 Completion Timeline" },
          { id: "calendar", l: "🗓 Study Calendar" },
          { id: "conf", l: "🎯 Confidence Check" },
          { id: "time", l: "⏱ Time Analysis" },
          { id: "weekly", l: "📋 Weekly Report" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{ ...BS(G.violet, tab === t.id), padding: "8px 16px" }}
          >
            {t.l}
          </button>
        ))}
      </div>
      {tab === "effort" && (
        <GlassCard accent={G.cyan} style={{ padding: 20 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            ⚡ Effort vs Yield
          </div>
          <EffortYieldChart
            subjects={subjects}
            tests={tests}
            pomodoroLog={pomodoroLog}
          />
          <div
            style={{
              marginTop: 13,
              fontSize: 11.5,
              color: G.textSecondary,
              padding: "9px 14px",
              background: `${G.rose}18`,
              borderRadius: 10,
              border: `1px solid ${G.rose}33`,
            }}
          >
            💡 A "Time Sink" takes &gt;1.5× its exam weightage in study time yet
            scores below 60%.
          </div>
        </GlassCard>
      )}
      {tab === "gantt" && (
        <GlassCard accent={G.violet} style={{ padding: 20 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            📊 Completion Timeline — Gantt View
          </div>
          <div
            style={{ fontSize: 11, color: G.textSecondary, marginBottom: 14 }}
          >
            Based on current pace and time allocation. Aim to complete each
            subject 60+ days before GATE.
          </div>
          <GanttTimeline
            subjects={subjects}
            examDateStr={profile.examDate}
            pomodoroLog={pomodoroLog}
          />
        </GlassCard>
      )}
      {tab === "calendar" && (
        <GlassCard accent={G.emerald} style={{ padding: 20 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            🗓 Study Calendar — Daily Focus Time
          </div>
          <StudyCalendarHeatmap pomodoroLog={pomodoroLog} streak={streak} />
        </GlassCard>
      )}
      {tab === "conf" && (
        <GlassCard accent={G.violet} style={{ padding: 20 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            🎯 Confidence vs Actual Performance
          </div>
          {confVsActual.map((s) => (
            <div key={s.name} style={{ marginBottom: 14 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 5,
                }}
              >
                <span
                  style={{
                    fontSize: 12.5,
                    color: G.textSecondary,
                    fontWeight: 600,
                  }}
                >
                  {s.name}
                </span>
                <div style={{ display: "flex", gap: 9 }}>
                  <span style={{ fontSize: 11, color: G.violet }}>
                    Conf: {((s.conf / 5) * 100).toFixed(0)}%
                  </span>
                  <span style={{ fontSize: 11, color: pctColor(s.score) }}>
                    Score: {s.score.toFixed(0)}%
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color:
                        s.gap > 15
                          ? "#ff5577"
                          : s.gap < -15
                          ? "#ffe666"
                          : G.emerald,
                      fontWeight: 700,
                    }}
                  >
                    {s.gap > 15
                      ? "overconfident"
                      : s.gap < -15
                      ? "underconfident"
                      : "calibrated"}
                  </span>
                </div>
              </div>
              <div
                style={{
                  position: "relative",
                  height: 9,
                  background: `${G.glassBorder}44`,
                  borderRadius: 99,
                  overflow: "visible",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: `${Math.min(100, (s.conf / 5) * 100)}%`,
                    background: `${G.violet}88`,
                    borderRadius: 99,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: `${Math.min(100, s.score)}%`,
                    background: pctColor(s.score),
                    borderRadius: 99,
                    opacity: 0.7,
                  }}
                />
              </div>
            </div>
          ))}
        </GlassCard>
      )}
      {tab === "time" && (
        <GlassCard accent={G.amber} style={{ padding: 20 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            ⏱ Pomodoro Time Distribution
          </div>
          {totalPomMinutes === 0 ? (
            <div
              style={{
                fontSize: 12.5,
                color: G.textDim,
                textAlign: "center",
                padding: "28px 0",
              }}
            >
              Start the Pomodoro timer to see time distribution here.
            </div>
          ) : (
            <>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  color: G.amber,
                  marginBottom: 14,
                }}
              >
                {Math.floor(totalPomMinutes / 60)}h {totalPomMinutes % 60}m
                total focused
              </div>
              {subjects.map((s) => {
                const mins = timePerSubj[s.name] || 0;
                const pct = Math.round((mins / totalPomMinutes) * 100);
                const wt = WEIGHTAGE[s.name] || 10;
                const isOk = Math.abs(pct - wt) <= wt * 0.5;
                return (
                  <div key={s.name} style={{ marginBottom: 11 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 4,
                      }}
                    >
                      <span style={{ fontSize: 12, color: G.textSecondary }}>
                        {s.name}
                      </span>
                      <div style={{ display: "flex", gap: 7 }}>
                        <span style={{ fontSize: 11, color: G.textMuted }}>
                          {Math.floor(mins / 60)}h {mins % 60}m
                        </span>
                        <Tag
                          text={`${pct}% time`}
                          color={isOk ? G.emerald : "#ff5577"}
                        />
                        <Tag text={`${wt}% wt`} color={s.clr} />
                      </div>
                    </div>
                    <Bar pct={pct} color={isOk ? s.clr : "#ff5577"} h={6} />
                  </div>
                );
              })}
            </>
          )}
        </GlassCard>
      )}
      {tab === "weekly" && (
        <GlassCard accent={G.cyan} style={{ padding: 20 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: G.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 14,
            }}
          >
            📋 Weekly Progress Report
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 12,
            }}
          >
            <button
              onClick={() => {
                const r = generateWeeklyReport(
                  subjects,
                  tests, // ← use actual tests prop
                  errors || [],

                  pomodoroLog || [],
                  streak,
                  qnotes || [],
                  profile.examDate
                );
                const blob = new Blob([r], { type: "text/plain" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `gate-weekly-${todayS()}.txt`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{ ...BS(G.cyan), padding: "7px 18px" }}
            >
              ⬇ Export Report
            </button>
          </div>
          <div
            style={{
              background: "rgba(8,14,32,0.85)",
              borderRadius: 12,
              padding: "14px 18px",
              border: `1px solid ${G.glassBorder}`,
              fontFamily: "monospace",
              fontSize: 12,
              color: G.textSecondary,
              lineHeight: 1.8,
              whiteSpace: "pre-wrap",
            }}
          >
            {generateWeeklyReport(
              subjects,
              tests, // ← use actual tests prop
              errors || [],
              pomodoroLog || [],
              streak,
              qnotes || [],
              profile.examDate
            )}
          </div>
          <StreakShareCard
            subjects={subjects}
            streak={streak}
            readinessScore={calcReadiness(subjects, tests, errors, streak)}
            pomodoroLog={pomodoroLog}
            examDateStr={profile.examDate}
          />
        </GlassCard>
      )}
    </div>
  );
};

/* ── ACHIEVEMENTS PAGE ──────────────────────────────────── */
const AchievementsPage = ({
  earnedIds,
  subjects,
  tests,
  errors,
  streak,
  qnotes,
  goals,
  flashcards,
  pomodoroLog = [],
  dailyPlan = [],
}) => {
  const dailyPlanCount = dailyPlan.length;
  const total = ACHIEVEMENTS.length,
    earned = earnedIds.length,
    pct = Math.round((earned / total) * 100);
  return (
    <div style={{ padding: 28, paddingBottom: 48 }}>
      <div style={{ marginBottom: 22 }}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: G.textPrimary,
            margin: 0,
            letterSpacing: "-0.025em",
          }}
        >
          Achievements
        </h1>
        <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
          Track milestones · Earn badges · Stay motivated
        </p>
      </div>
      <GlassCard accent={G.amber} style={{ padding: 17, marginBottom: 18 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 11,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: G.textPrimary }}>
            🏆 {earned} / {total} Earned
          </span>
          <span
            style={{
              fontSize: 22,
              fontWeight: 900,
              color: G.amber,
              textShadow: `0 0 18px ${G.amber}66`,
            }}
          >
            {pct}%
          </span>
        </div>
        <Bar pct={pct} color={G.amber} h={7} />
      </GlassCard>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(165px,1fr))",
          gap: 10,
        }}
      >
        {ACHIEVEMENTS.map((a) => (
          <AchievementBadge
            key={a.id}
            achievement={a}
            earned={earnedIds.includes(a.id)}
          />
        ))}
      </div>
    </div>
  );
};

/* ── GLOBAL CSS ─────────────────────────────────────────── */
const GlobalCSS = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,600;0,9..40,700;0,9..40,800;0,9..40,900&display=swap');
    *{box-sizing:border-box;}
    ::-webkit-scrollbar{width:5px;height:5px;}
    ::-webkit-scrollbar-track{background:transparent;}
    ::-webkit-scrollbar-thumb{background:rgba(120,160,255,0.3);border-radius:99px;}
    ::-webkit-scrollbar-thumb:hover{background:rgba(120,160,255,0.55);}
    @keyframes slideIn{from{opacity:0;transform:translateX(22px);}to{opacity:1;transform:translateX(0);}}
    @keyframes pulseGlow{0%,100%{box-shadow:0 0 14px rgba(179,157,255,0.5);}50%{box-shadow:0 0 32px rgba(179,157,255,0.85);}}
    @keyframes shimmer{0%{transform:translateX(-100%);}100%{transform:translateX(400%);}}
    @keyframes drawCheck{from{stroke-dashoffset:14;}to{stroke-dashoffset:0;}}
    @keyframes orb1{0%,100%{transform:translate(0,0);}50%{transform:translate(40px,-30px);}}
    @keyframes orb2{0%,100%{transform:translate(0,0);}50%{transform:translate(-30px,50px);}}
    @keyframes fadeUp{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
    input,select,textarea{transition:border-color 0.2s,box-shadow 0.2s;}
    input:focus,select:focus,textarea:focus{border-color:rgba(179,157,255,0.65)!important;box-shadow:0 0 0 3px rgba(179,157,255,0.18)!important;}
    option{background:#0e1832;color:#d4e4ff;}
    button:hover{filter:brightness(1.12);}
    button:active{transform:scale(0.97);}
    .mobile-header{display:none;}
    .nav-item{transition:background 0.15s ease,color 0.15s ease;}
    .nav-item:hover{background:rgba(179,157,255,0.07)!important;}
    .glass-card-clickable{transition:transform 0.18s ease,border-color 0.18s ease,box-shadow 0.18s ease;}
    .glass-card-clickable:hover{transform:translateY(-2px);}
    @media (max-width: 768px) {
      .mobile-header{
        display:flex;
        align-items:center;
        padding:14px 20px;
        background:rgba(5,8,20,0.97);
        backdrop-filter:blur(20px) saturate(200%);
        -webkit-backdrop-filter:blur(20px) saturate(200%);
        border-bottom:1px solid rgba(120,160,255,0.22);
        box-shadow:0 2px 24px rgba(0,0,0,0.65),0 1px 0 rgba(120,160,255,0.1);
        position:fixed;
        top:0;left:0;right:0;
        z-index:350;
        height:56px;
      }
      main.page-container{padding-top:72px!important;}
      div[style*="grid-template-columns: 1fr 295px"],
      div[style*="grid-template-columns: 1fr 300px"],
      div[style*="grid-template-columns: 1fr 350px"]{grid-template-columns:1fr!important;}
      div[style*="grid-template-columns: repeat(4,1fr)"]{grid-template-columns:repeat(2,1fr)!important;}
      div[style*="grid-template-columns: repeat(3,1fr)"]{grid-template-columns:repeat(2,1fr)!important;}
      div[style*="grid-template-columns: 1fr 1fr 1fr"]{grid-template-columns:repeat(2,1fr)!important;}
      .sidebar-container{
        position:fixed!important;
        left:-300px;top:0;height:100vh;
        z-index:999!important;
        transition:left 0.3s ease-in-out;
      }
      .sidebar-container.open{left:0;}
      .page-container{padding:16px!important;padding-bottom:80px!important;}
      .pomo-widget{right:16px!important;bottom:16px!important;}
      .pomo-widget:not(.pomo-widget-min){left:16px!important;width:auto!important;}
    }
  `}</style>
);
/* ── PYQ MATRIX WRAPPER PAGE ────────────────────────────── */
const PYQMatrixPage = ({ subjects, pyqMatrix, setPyqMatrix }) => (
  <div style={{ padding: 28, paddingBottom: 48 }}>
    <div style={{ marginBottom: 22 }}>
      <h1
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: G.textPrimary,
          margin: 0,
          letterSpacing: "-0.025em",
        }}
      >
        PYQ Year-wise Matrix
      </h1>
      <p style={{ fontSize: 12.5, color: G.textMuted, margin: "4px 0 0" }}>
        Track 2015–2024 Past Year Questions completion per topic
      </p>
    </div>
    <GlassCard style={{ padding: 20 }}>
      <PYQMatrix
        subjects={subjects}
        pyqMatrix={pyqMatrix}
        setPyqMatrix={setPyqMatrix}
      />
    </GlassCard>
  </div>
);

/* ══════════════════════════════════════
   MAIN APP
══════════════════════════════════════ */
/* ══════════════════════════════════════
   MAIN APP
══════════════════════════════════════ */
export default function GATETracker() {
  const [dailyPlan, setDailyPlan] = useState([]);
  // ADD THESE TWO LINES:
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [showSettings, setShowSettings] = useState(false);
  const [page, setPage] = useState("dashboard");
  const [subjects, setSubjects] = useState(INIT_SUBJECTS);
  const [tests, setTests] = useState([]);
  const [goals, setGoals] = useState([]);
  const [calEvents, setCalEvents] = useState([]);
  const [routine, setRoutine] = useState(INIT_ROUTINE);
  const [errors, setErrors] = useState([]);
  const [qnotes, setQnotes] = useState([]);
  const [pomodoroLog, setPomodoroLog] = useState([]);
  const [earnedAchievements, setEarnedAchievements] = useState([]);
  const [streak, setStreak] = useState(0);

  const [flashcards, setFlashcards] = useState(INIT_FLASHCARDS);
  const [pyqMatrix, setPyqMatrix] = useState({});

  const [ready, setReady] = useState(false);
  const [toasts, setToasts] = useState([]);
  const restoreRef = useRef(null);

  // NEW: State to control the mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFormulaKeyboard, setShowFormulaKeyboard] = useState(false);

  const addToast = useCallback((t) => {
    const id = uid();
    setToasts((ts) => [...ts, { ...t, id }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), 3800);
  }, []);

  /* Load from localStorage */
  useEffect(() => {
    const KEYS = [
      ["gate_subjects_v2", setSubjects, INIT_SUBJECTS],
      ["gate_tests", setTests, []],
      ["gate_goals", setGoals, []],
      ["gate_events", setCalEvents, []],
      ["gate_routine_v2", setRoutine, INIT_ROUTINE],
      ["gate_errors", setErrors, []],
      ["gate_qnotes", setQnotes, []],
      ["gate_pomo_log", setPomodoroLog, []],
      ["gate_achievements", setEarnedAchievements, []],
      ["gate_flashcards", setFlashcards, INIT_FLASHCARDS],
      ["gate_pyq_matrix", setPyqMatrix, {}],
      ["gate_daily_plan", setDailyPlan, []],
      ["gate_profile", setProfile, DEFAULT_PROFILE],
    ];
    for (const [k, setter, def] of KEYS) {
      try {
        const r = localStorage.getItem(k);
        if (r) setter(JSON.parse(r));
        else setter(def);
      } catch {
        setter(def);
      }
    }
    setReady(true);
  }, []);

  /* Auto-save profile to localStorage */
  useEffect(() => {
    if (ready && profile) {
      localStorage.setItem("gate_profile", JSON.stringify(profile));
    }
  }, [profile, ready]);

  /* Streak from Pomodoro */
  useEffect(() => {
    if (!ready) return;
    setStreak(calcStreak(pomodoroLog));
  }, [pomodoroLog, ready]); /* Achievement checking */
  /* Revision & Streak Notifications — fires once on load */
  useEffect(() => {
    if (!ready) return;
    if (!("Notification" in window)) return;
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    if (Notification.permission !== "granted") return;

    const due = [],
      overdue = [];
    subjects.forEach((s) =>
      s.topics.forEach((t) => {
        const r = getRevStatus(t);
        if (!r) return;
        if (r.diff === 0)
          due.push(`${t.name} (${s.name.split(" ").slice(-1)[0]})`);
        if (r.diff < 0)
          overdue.push(`${t.name} — ${Math.abs(r.diff)}d overdue`);
      })
    );

    if (due.length > 0) {
      sendNotification(
        `📚 ${due.length} Revision${due.length > 1 ? "s" : ""} Due Today`,
        due.slice(0, 3).join(", ") +
          (due.length > 3 ? ` +${due.length - 3} more` : ""),
        "gate-rev-due"
      );
    }
    if (overdue.length > 0) {
      sendNotification(
        `⚠️ ${overdue.length} Overdue Revision${overdue.length > 1 ? "s" : ""}`,
        overdue.slice(0, 3).join(", "),
        "gate-rev-overdue"
      );
    }

    // Streak warning: after 6 PM and under 2hrs today
    const hour = new Date().getHours();
    const todayMins = pomodoroLog
      .filter((l) => l.date === todayS())
      .reduce((a, l) => a + l.minutes, 0);
    if (streak > 0 && hour >= 18 && todayMins < 120) {
      sendNotification(
        `🔥 Streak at Risk — ${streak} days`,
        `Only ${todayMins} of 120 min studied today. ${
          120 - todayMins
        } more minutes to keep your streak!`,
        "gate-streak-risk"
      );
    }
  }, [ready]); // eslint-disable-line
  /* Persist Data */
  useEffect(() => {
    if (!ready) return;
    safeSetItem("gate_subjects_v2", JSON.stringify(subjects));
    safeSetItem("gate_tests", JSON.stringify(tests));
    safeSetItem("gate_goals", JSON.stringify(goals));
    safeSetItem("gate_events", JSON.stringify(calEvents));
    safeSetItem("gate_routine_v2", JSON.stringify(routine));
    safeSetItem("gate_errors", JSON.stringify(errors));
    safeSetItem("gate_qnotes", JSON.stringify(qnotes));
    safeSetItem("gate_pomo_log", JSON.stringify(pomodoroLog));
    safeSetItem("gate_achievements", JSON.stringify(earnedAchievements));
    const flashOk = safeSetItem("gate_flashcards", JSON.stringify(flashcards));
    if (!flashOk) {
      addToast({
        title: "Storage Full",
        message: "Couldn't save flashcards — try smaller/fewer images.",
        icon: "⚠️",
        color: G.rose,
      });
    }
    safeSetItem("gate_pyq_matrix", JSON.stringify(pyqMatrix));
    safeSetItem("gate_daily_plan", JSON.stringify(dailyPlan));
  }, [
    subjects,
    tests,
    goals,
    calEvents,
    routine,
    errors,
    qnotes,
    pomodoroLog,
    earnedAchievements,
    flashcards,
    pyqMatrix,
    dailyPlan,
    ready,
    addToast,
  ]);

  useEffect(() => {
    if (!document.getElementById("pwa-manifest")) {
      const manifest = {
        name: "GATE ECE 2027 Tracker v3",
        short_name: "GATE2027",
        display: "standalone",
        background_color: "#050810",
        theme_color: "#b39dff",
        icons: [
          {
            src: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23b39dff'/><text x='50' y='65' font-size='50' text-anchor='middle' fill='%23050810' font-family='sans-serif' font-weight='bold'>G</text></svg>",
            sizes: "192x192",
            type: "image/svg+xml",
          },
        ],
      };
      const blob = new Blob([JSON.stringify(manifest)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("link");
      link.id = "pwa-manifest";
      link.rel = "manifest";
      link.href = url;
      document.head.appendChild(link);
    }
  }, []);

  const onSessionComplete = useCallback(
    (subject, minutes) => {
      setPomodoroLog((l) => [
        ...l,
        { id: uid(), subject, minutes, date: todayS() },
      ]);
      addToast({
        title: "Focus Session Complete!",
        message: `${minutes}min on ${subject.split(" ").slice(-1)[0]}`,
        icon: "🍅",
        color: G.pink,
      });
    },
    [addToast]
  );
  useEffect(() => {
    if (!ready) return;
    const newIds = checkAchievements(
      {
        subjects,
        tests,
        errors,
        streak,
        qnotes,
        flashcards,
        dailyPlan,
        pomodoroLog,
      },
      earnedAchievements
    );
    if (newIds.length > 0) {
      setEarnedAchievements((prev) => [...prev, ...newIds]);
      newIds.forEach((id) => {
        const ach = ACHIEVEMENTS.find((a) => a.id === id);
        if (ach)
          addToast({
            title: `🏆 ${ach.title}`,
            message: ach.desc,
            icon: ach.icon,
            color: G.amber,
          });
      });
    }
  }, [
    subjects,
    tests,
    errors,
    streak,
    qnotes,
    flashcards,
    dailyPlan,
    pomodoroLog,
    earnedAchievements,
    ready,
    addToast,
  ]);

  const readinessScore = useMemo(
    () => calcReadiness(subjects, tests, errors, streak),
    [subjects, tests, errors, streak]
  );

  const backup = () => {
    const data = {
      v: "8.0",
      date: new Date().toISOString(),
      gate_profile: profile,
      gate_subjects_v2: subjects,
      gate_tests: tests,
      gate_goals: goals,
      gate_events: calEvents,
      gate_routine_v2: routine,
      gate_errors: errors,
      gate_qnotes: qnotes,
      gate_pomo_log: pomodoroLog,
      gate_achievements: earnedAchievements,
      gate_flashcards: flashcards,
      gate_pyq_matrix: pyqMatrix,
      gate_daily_plan: dailyPlan,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gate-tracker-v3-${todayS()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    addToast({
      title: "Backup Downloaded",
      message: "Your data is saved",
      icon: "💾",
      color: G.emerald,
    });
  };
  const onRestoreClick = () => restoreRef.current?.click();
  const restore = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const d = JSON.parse(ev.target.result);
        if (d.gate_subjects_v2) setSubjects(d.gate_subjects_v2);
        else if (d.gate_subjects) setSubjects(d.gate_subjects);
        if (d.gate_tests) setTests(d.gate_tests);
        if (d.gate_goals) setGoals(d.gate_goals);
        if (d.gate_events) setCalEvents(d.gate_events);
        if (d.gate_routine_v2) setRoutine(d.gate_routine_v2);
        else if (d.gate_routine) setRoutine(d.gate_routine);
        if (d.gate_errors) setErrors(d.gate_errors);
        if (d.gate_profile) setProfile(d.gate_profile);
        if (d.gate_qnotes) setQnotes(d.gate_qnotes);
        if (d.gate_pomo_log) setPomodoroLog(d.gate_pomo_log);
        if (d.gate_achievements) setEarnedAchievements(d.gate_achievements);
        if (d.gate_flashcards) setFlashcards(d.gate_flashcards);
        if (d.gate_pyq_matrix) setPyqMatrix(d.gate_pyq_matrix);
        if (d.gate_daily_plan) setDailyPlan(d.gate_daily_plan);
        addToast({
          title: "Backup Restored",
          message: "All data loaded successfully",
          icon: "✅",
          color: G.emerald,
        });
      } catch {
        addToast({
          title: "Restore Failed",
          message: "Invalid backup file",
          icon: "❌",
          color: G.rose,
        });
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  if (!ready)
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          background: G.bg,
          color: G.textSecondary,
          fontSize: 14,
          fontFamily: "'DM Sans',system-ui,sans-serif",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 11,
            background: "linear-gradient(135deg,#b39dff,#5ecfff)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            fontWeight: 900,
            boxShadow: "0 4px 24px rgba(179,157,255,0.55)",
            animation: "pulseGlow 2s infinite",
          }}
        >
          G
        </div>
        <div>Loading GATE Tracker v3…</div>
      </div>
    );

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        background: `radial-gradient(ellipse at 20% 0%,rgba(60,30,140,0.22) 0%,transparent 55%),radial-gradient(ellipse at 80% 100%,rgba(20,80,140,0.2) 0%,transparent 55%),${G.bg}`,
        color: G.textPrimary,
        fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
        overflow: "hidden",
      }}
    >
      <GlobalCSS />
      {showSettings && (
        <SettingsModal
          profile={profile}
          setProfile={setProfile}
          onClose={() => setShowSettings(false)}
        />
      )}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: 700,
            height: 700,
            borderRadius: "50%",
            top: "-20%",
            left: "-12%",
            background:
              "radial-gradient(circle,rgba(90,60,200,0.1),transparent 68%)",
            animation: "orb1 20s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 550,
            height: 550,
            borderRadius: "50%",
            bottom: "-12%",
            right: "-8%",
            background:
              "radial-gradient(circle,rgba(20,120,200,0.09),transparent 68%)",
            animation: "orb2 26s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 400,
            height: 400,
            borderRadius: "50%",
            top: "40%",
            right: "25%",
            background:
              "radial-gradient(circle,rgba(78,234,170,0.05),transparent 68%)",
            animation: "orb1 32s ease-in-out infinite reverse",
          }}
        />
      </div>
      <input
        ref={restoreRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={restore}
      />
      <Sidebar
        page={page}
        setPage={setPage}
        subjects={subjects}
        errors={errors}
        onBackup={backup}
        onRestoreClick={onRestoreClick}
        readinessScore={readinessScore}
        streak={streak}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        profile={profile}
        setShowSettings={setShowSettings}
      />
      <main
        className="page-container"
        style={{
          flex: 1,
          overflowY: "auto",
          background: "transparent",
          position: "relative",
        }}
      >
        {/* Mobile Header with Hamburger Menu */}
        <div className="mobile-header">
          <button
            onClick={() => setSidebarOpen(true)}
            style={{
              background: "none",
              border: "none",
              color: G.textPrimary,
              fontSize: 24,
              cursor: "pointer",
              padding: 0,
            }}
          >
            ☰
          </button>
          <span style={{ marginLeft: 16, fontSize: 16, fontWeight: 800 }}>
            GATE Tracker
          </span>
        </div>

        {page === "dashboard" && (
          <Dashboard
            subjects={subjects}
            tests={tests}
            goals={goals}
            routine={routine}
            errors={errors}
            readinessScore={readinessScore}
            streak={streak}
            pomodoroLog={pomodoroLog}
            flashcards={flashcards}
            setGoals={setGoals}
            addToast={addToast}
            setPage={setPage}
            profile={profile}
          />
        )}
        {page === "subjects" && (
          <SubjectsPage
            subjects={subjects}
            setSubjects={setSubjects}
            errors={errors}
            setPage={setPage}
            addToast={addToast}
            goals={goals}
            setGoals={setGoals}
            profile={profile}
          />
        )}
        {page === "revqueue" && (
          <RevisionQueuePage
            subjects={subjects}
            setSubjects={setSubjects}
            calEvents={calEvents}
            setCalEvents={setCalEvents}
            addToast={addToast}
          />
        )}
        {page === "planner" && (
          <PlannerPage
            goals={goals}
            setGoals={setGoals}
            calEvents={calEvents}
            setCalEvents={setCalEvents}
          />
        )}
        {page === "dailyplan" && (
          <DailyPlanPage dailyPlan={dailyPlan} setDailyPlan={setDailyPlan} />
        )}
        {page === "flashcards" && (
          <FlashcardsPage
            flashcards={flashcards}
            setFlashcards={setFlashcards}
          />
        )}
        {page === "pyqmatrix" && (
          <PYQMatrixPage
            subjects={subjects}
            pyqMatrix={pyqMatrix}
            setPyqMatrix={setPyqMatrix}
          />
        )}
        {page === "tests" && (
          <TestsPage tests={tests} setTests={setTests} addToast={addToast} />
        )}
        {page === "errors" && (
          <ErrorLogPage errors={errors} setErrors={setErrors} />
        )}
        {page === "qnotes" && (
          <QuickNotesPage qnotes={qnotes} setQnotes={setQnotes} />
        )}
        {page === "routine" && (
          <RoutinePage routine={routine} setRoutine={setRoutine} />
        )}
        {page === "insights" && (
          <InsightsPage
            subjects={subjects}
            tests={tests}
            errors={errors}
            pomodoroLog={pomodoroLog}
            streak={streak}
            qnotes={qnotes}
            profile={profile}
          />
        )}
        {page === "predictor" && (
          <CollegePredictorPage readinessScore={readinessScore} tests={tests} />
        )}
        {page === "topicmap" && <CrossReferencePage subjects={subjects} />}
        {page === "achievements" && (
          <AchievementsPage
            earnedIds={earnedAchievements}
            subjects={subjects}
            tests={tests}
            errors={errors}
            streak={streak}
            qnotes={qnotes}
            goals={goals}
            flashcards={flashcards}
            pomodoroLog={pomodoroLog}
            dailyPlan={dailyPlan}
          />
        )}
      </main>

      <PomodoroTimer
        subjects={subjects}
        pomodoroLog={pomodoroLog}
        onSessionComplete={onSessionComplete}
        addToast={addToast}
      />
      <FormulaKeyboard
        visible={showFormulaKeyboard}
        onToggle={() => setShowFormulaKeyboard((v) => !v)}
      />
      <Toast toasts={toasts} />
    </div>
  );
}
